/**
 * Moodboard freeform helpers (mirrors BACK layout logic for UI).
 * Coords: normalized 0–1, origin top-left.
 * Optional page.board { x, y, w, h } = artboard pose on the workspace (UI only).
 */

import {
  assignDefaultBoards,
  normalizePageBoard,
} from "./skillAgentMoodboardBoard";

export const SLOT_KEYS = [
  "hero",
  "midLeft",
  "midRight",
  "botTL",
  "botTR",
  "botBL",
  "botBR",
];

export const SLOTS_PER_PAGE = 7;
export const MIN_SLOT = 0.05;
export const LEFT_COL_RATIO = 0.26;

let slotIdSeq = 0;
export function newSlotId(prefix = "s") {
  slotIdSeq += 1;
  return `${prefix}_${Date.now().toString(36)}_${slotIdSeq}`;
}

let markIdSeq = 0;
export function newMarkId(prefix = "m") {
  markIdSeq += 1;
  return `${prefix}_${Date.now().toString(36)}_${markIdSeq}`;
}

let groupIdSeq = 0;
/** Id estable de grupo VC (Object → Group). */
export function newGroupId(prefix = "g") {
  groupIdSeq += 1;
  return `${prefix}_${Date.now().toString(36)}_${groupIdSeq}`;
}

let symbolIdSeq = 0;
/** Id de símbolo VC (Symbols panel / Sprayer). */
export function newSymbolId(prefix = "sym") {
  symbolIdSeq += 1;
  return `${prefix}_${Date.now().toString(36)}_${symbolIdSeq}`;
}

/**
 * Plantilla de marca sin id de instancia (para Symbols).
 */
export function markAsSymbolTemplate(mark) {
  if (!mark || typeof mark !== "object") return null;
  const {
    id: _id,
    groupId: _g,
    onPath: _op,
    objectsOnPath: _oop,
    puppet: _pup,
    symbolId: _sid,
    ...rest
  } = mark;
  try {
    const cloned = JSON.parse(JSON.stringify(rest));
    const normalized = clampMark(
      { ...cloned, id: "__sym_tpl__" },
      { allowOutside: true },
    );
    if (!normalized) return null;
    const { id: _drop, ...template } = normalized;
    return template;
  } catch {
    return null;
  }
}

export function normalizeSymbolEntry(raw) {
  if (!raw || typeof raw !== "object") return null;
  const mark = markAsSymbolTemplate(raw.mark || raw.template);
  if (!mark) return null;
  const id = String(raw.id || newSymbolId()).slice(0, 64);
  const name = String(raw.name || mark.type || "Symbol")
    .trim()
    .slice(0, 48);
  return { id, name: name || "Symbol", mark };
}

export function normalizeSymbols(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeSymbolEntry).filter(Boolean).slice(0, 48);
}

export const MARK_TYPES = [
  "path",
  "line",
  "arrow",
  "ellipse",
  "arc",
  "rect",
  "roundrect",
  "polygon",
  "star",
  "grid",
  "polarGrid",
  "flare",
  "dimension",
  "text",
  "textpath",
];

/** Mesa A4 vertical (mm) — coords de marca 0–1 sobre el artboard. */
export const ARTBOARD_MM = { w: 210, h: 297 };

export function clampSpiralTurns(raw, fallback = 3) {
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n)) return Math.max(1, Math.min(8, fallback));
  return Math.max(1, Math.min(8, n));
}

/**
 * Espiral Arquímedes o logarítmica hasta rOuter (coords 0–1).
 * kind: "archimedean" | "log"
 */
export function spiralPathPoints({
  cx = 0.5,
  cy = 0.5,
  rOuter = 0.2,
  turns = 3,
  steps = 96,
  clockwise = true,
  kind = "archimedean",
} = {}) {
  const r = Math.max(0.001, Number(rOuter) || 0.2);
  const nTurns = clampSpiralTurns(turns, 3);
  const n = Math.max(24, Math.min(240, Math.round(Number(steps) || 96)));
  const total = nTurns * Math.PI * 2;
  const dir = clockwise ? 1 : -1;
  const logKind = String(kind || "").toLowerCase() === "log";
  const pts = [];
  for (let i = 0; i <= n; i += 1) {
    const t = i / n;
    const ang = dir * t * total;
    // Log: crece más al final (estilo VC Flare/Spiral decay).
    const rr = logKind
      ? (r * (Math.exp(2.2 * t) - 1)) / (Math.exp(2.2) - 1)
      : r * t;
    pts.push({
      x: (Number(cx) || 0) + rr * Math.cos(ang),
      y: (Number(cy) || 0) + rr * Math.sin(ang),
    });
  }
  return pts;
}

/**
 * Geometría de cota VC: ticks perpendiculares, etiqueta offset, % y mm (A4).
 */
export function dimensionMetrics(mark, artboardMm = ARTBOARD_MM) {
  const x1 = Number(mark?.x1) || 0;
  const y1 = Number(mark?.y1) || 0;
  const x2 = Number(mark?.x2) || 0;
  const y2 = Number(mark?.y2) || 0;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy);
  const ux = len > 1e-8 ? dx / len : 1;
  const uy = len > 1e-8 ? dy / len : 0;
  const nx = -uy;
  const ny = ux;
  const tick = 0.014;
  const labelOff = 0.02;
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const aw = Number(artboardMm?.w) || ARTBOARD_MM.w;
  const ah = Number(artboardMm?.h) || ARTBOARD_MM.h;
  const distPct = len * 100;
  const distMm = Math.hypot(dx * aw, dy * ah);
  const angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
  const unit =
    String(mark?.unit || "pct").toLowerCase() === "mm" ? "mm" : "pct";
  const custom =
    mark?.label != null && String(mark.label).trim()
      ? String(mark.label).trim().slice(0, 48)
      : "";
  const autoLabel =
    unit === "mm" ? `${distMm.toFixed(1)} mm` : `${distPct.toFixed(1)}%`;
  return {
    x1,
    y1,
    x2,
    y2,
    dx,
    dy,
    len,
    ux,
    uy,
    nx,
    ny,
    tick,
    mx,
    my,
    distPct,
    distMm,
    angleDeg,
    unit,
    label: custom || autoLabel,
    t1a: { x: x1 - nx * tick, y: y1 - ny * tick },
    t1b: { x: x1 + nx * tick, y: y1 + ny * tick },
    t2a: { x: x2 - nx * tick, y: y2 - ny * tick },
    t2b: { x: x2 + nx * tick, y: y2 + ny * tick },
    labelX: mx + nx * labelOff,
    labelY: my + ny * labelOff,
  };
}

/**
 * Rayos + anillos de Flare (Illustrator-ish): tip + base ancha, long/short.
 * @returns {{ center, rings: Array<{rx,ry}>, rays: Array<{tip,b1,b2}> }}
 */
export function flareGeometry(mark) {
  const ringsN = Math.max(
    1,
    Math.min(12, Math.round(Number(mark?.rings) || 3)),
  );
  const raysN = Math.max(4, Math.min(32, Math.round(Number(mark?.rays) || 8)));
  const cx = (Number(mark?.x) || 0) + (Number(mark?.w) || 0) / 2;
  const cy = (Number(mark?.y) || 0) + (Number(mark?.h) || 0) / 2;
  const rx = Math.max((Number(mark?.w) || 0) / 2, 0.001);
  const ry = Math.max((Number(mark?.h) || 0) / 2, 0.001);
  const rings = [];
  for (let r = 1; r <= ringsN; r += 1) {
    const t = 0.22 + (0.78 * r) / ringsN;
    rings.push({ rx: rx * t, ry: ry * t });
  }
  const rays = [];
  for (let i = 0; i < raysN; i += 1) {
    const a = (i / raysN) * Math.PI * 2 - Math.PI / 2;
    const long = i % 2 === 0;
    const reach = long ? 1 : 0.62;
    const base = long ? 0.28 : 0.2;
    const spread = long ? 0.14 : 0.1;
    const tip = {
      x: cx + rx * reach * Math.cos(a),
      y: cy + ry * reach * Math.sin(a),
    };
    const b1 = {
      x: cx + rx * base * Math.cos(a - spread),
      y: cy + ry * base * Math.sin(a - spread),
    };
    const b2 = {
      x: cx + rx * base * Math.cos(a + spread),
      y: cy + ry * base * Math.sin(a + spread),
    };
    rays.push({ tip, b1, b2, long });
  }
  return {
    cx,
    cy,
    rx,
    ry,
    coreRx: rx * 0.1,
    coreRy: ry * 0.1,
    rings,
    rays,
  };
}

/** BBox aproximado de una marca (0–1). */
export function markBounds(mark) {
  if (!mark) return null;
  if (
    mark.type === "line" ||
    mark.type === "arrow" ||
    mark.type === "dimension"
  ) {
    const x1 = Number(mark.x1) || 0;
    const y1 = Number(mark.y1) || 0;
    const x2 = Number(mark.x2) || 0;
    const y2 = Number(mark.y2) || 0;
    const x = Math.min(x1, x2);
    const y = Math.min(y1, y2);
    return {
      x,
      y,
      w: Math.max(0.001, Math.abs(x2 - x1)),
      h: Math.max(0.001, Math.abs(y2 - y1)),
      cx: (x1 + x2) / 2,
      cy: (y1 + y2) / 2,
    };
  }
  if (
    (mark.type === "path" || mark.type === "textpath") &&
    Array.isArray(mark.points) &&
    mark.points.length
  ) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    mark.points.forEach((p) => {
      const px = Number(p.x) || 0;
      const py = Number(p.y) || 0;
      minX = Math.min(minX, px);
      minY = Math.min(minY, py);
      maxX = Math.max(maxX, px);
      maxY = Math.max(maxY, py);
    });
    if (!Number.isFinite(minX)) return null;
    return {
      x: minX,
      y: minY,
      w: Math.max(0.001, maxX - minX),
      h: Math.max(0.001, maxY - minY),
      cx: (minX + maxX) / 2,
      cy: (minY + maxY) / 2,
    };
  }
  if (mark.type === "text") {
    const x = Number(mark.x) || 0;
    const y = Number(mark.y) || 0;
    const w = Math.max(0.001, textMarkBoxWidth(mark));
    const h = Math.max(0.001, textMarkBoxHeight(mark));
    return { x, y, w, h, cx: x + w / 2, cy: y + h / 2 };
  }
  if (
    mark.type === "rect" ||
    mark.type === "roundrect" ||
    mark.type === "ellipse" ||
    mark.type === "arc" ||
    mark.type === "polygon" ||
    mark.type === "star" ||
    mark.type === "grid" ||
    mark.type === "polarGrid" ||
    mark.type === "flare"
  ) {
    const x = Number(mark.x) || 0;
    const y = Number(mark.y) || 0;
    const w = Math.max(0.001, Number(mark.w) || 0);
    const h = Math.max(0.001, Number(mark.h) || 0);
    return { x, y, w, h, cx: x + w / 2, cy: y + h / 2 };
  }
  return null;
}

/** Intersección de dos segmentos 2D, o null. `t` en a1→a2, `u` en b1→b2. */
function segIntersect(a1, a2, b1, b2) {
  const x1 = Number(a1.x) || 0;
  const y1 = Number(a1.y) || 0;
  const x2 = Number(a2.x) || 0;
  const y2 = Number(a2.y) || 0;
  const x3 = Number(b1.x) || 0;
  const y3 = Number(b1.y) || 0;
  const x4 = Number(b2.x) || 0;
  const y4 = Number(b2.y) || 0;
  const den = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(den) < 1e-12) return null;
  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / den;
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / den;
  if (t < 0.02 || t > 0.98 || u < 0 || u > 1) return null;
  return {
    x: x1 + t * (x2 - x1),
    y: y1 + t * (y2 - y1),
    t,
    u,
  };
}

/**
 * Parte marca con bbox (rect/ellipse/texto…) en 2 según trazo knife.
 * Usa markBounds (texto incluido). Devuelve { parts, splitVertical, t }.
 */
function knifeSplitBoxMark(mark, knife) {
  const b = markBounds(mark);
  if (!b || b.w < 0.04 || b.h < 0.03) return null;
  const { x, y, w, h } = b;
  const k0 = knife[0];
  const k1 = knife[knife.length - 1];
  const kdx = (Number(k1.x) || 0) - (Number(k0.x) || 0);
  const kdy = (Number(k1.y) || 0) - (Number(k0.y) || 0);
  const splitVertical = Math.abs(kdx) < Math.abs(kdy);
  const inside = knife.filter((p) => {
    const px = Number(p.x) || 0;
    const py = Number(p.y) || 0;
    return px >= x && px <= x + w && py >= y && py <= y + h;
  });
  let midX;
  let midY;
  if (inside.length) {
    midX = inside.reduce((a, p) => a + (Number(p.x) || 0), 0) / inside.length;
    midY = inside.reduce((a, p) => a + (Number(p.y) || 0), 0) / inside.length;
  } else {
    const crosses =
      Math.min(Number(k0.x) || 0, Number(k1.x) || 0) <= x + w &&
      Math.max(Number(k0.x) || 0, Number(k1.x) || 0) >= x &&
      Math.min(Number(k0.y) || 0, Number(k1.y) || 0) <= y + h &&
      Math.max(Number(k0.y) || 0, Number(k1.y) || 0) >= y;
    if (!crosses) return null;
    midX = Math.min(
      x + w,
      Math.max(x, ((Number(k0.x) || 0) + (Number(k1.x) || 0)) / 2),
    );
    midY = Math.min(
      y + h,
      Math.max(y, ((Number(k0.y) || 0) + (Number(k1.y) || 0)) / 2),
    );
  }
  if (splitVertical) {
    let t = (midX - x) / (w || 1);
    t = Math.min(0.85, Math.max(0.15, t));
    const w1 = w * t;
    const w2 = w - w1;
    if (w1 < 0.02 || w2 < 0.02) return null;
    return {
      splitVertical: true,
      t,
      parts: [
        clampMark({ ...mark, id: newMarkId(), x, y, w: w1, h }),
        clampMark({ ...mark, id: newMarkId(), x: x + w1, y, w: w2, h }),
      ],
    };
  }
  let t = (midY - y) / (h || 1);
  t = Math.min(0.85, Math.max(0.15, t));
  const h1 = h * t;
  const h2 = h - h1;
  if (h1 < 0.02 || h2 < 0.02) return null;
  return {
    splitVertical: false,
    t,
    parts: [
      clampMark({ ...mark, id: newMarkId(), x, y, w, h: h1 }),
      clampMark({ ...mark, id: newMarkId(), x, y: y + h1, w, h: h2 }),
    ],
  };
}

/** Parte el string de un texto en el corte (aprox. por caracteres / líneas). */
function splitTextContent(text, splitVertical, t) {
  const raw = String(text ?? "");
  if (!raw) return ["", ""];
  if (splitVertical) {
    const cut = Math.max(
      1,
      Math.min(raw.length - 1, Math.round(raw.length * t)),
    );
    return [raw.slice(0, cut), raw.slice(cut)];
  }
  const lines = raw.split(/\r?\n/);
  if (lines.length >= 2) {
    const cut = Math.max(
      1,
      Math.min(lines.length - 1, Math.round(lines.length * t)),
    );
    return [lines.slice(0, cut).join("\n"), lines.slice(cut).join("\n")];
  }
  const cut = Math.max(1, Math.min(raw.length - 1, Math.round(raw.length * t)));
  return [raw.slice(0, cut), raw.slice(cut)];
}

/**
 * Cortes del knife sobre un path, ordenados a lo largo del trazo (param = seg + t).
 */
export function collectKnifePathCuts(pathPoints, knifePoints, tol = 0.028) {
  const pts = (Array.isArray(pathPoints) ? pathPoints : []).map((p) => ({
    x: Number(p?.x) || 0,
    y: Number(p?.y) || 0,
  }));
  const knife = Array.isArray(knifePoints) ? knifePoints : [];
  if (pts.length < 2 || knife.length < 2) return [];
  const cuts = [];
  for (let j = 1; j < pts.length; j += 1) {
    for (let i = 1; i < knife.length; i += 1) {
      const hit = segIntersect(knife[i - 1], knife[i], pts[j - 1], pts[j]);
      if (!hit) continue;
      const u = Number(hit.u);
      if (!(u > 0.02 && u < 0.98)) continue;
      cuts.push({
        x: hit.x,
        y: hit.y,
        segIndex: j - 1,
        t: u,
        param: j - 1 + u,
      });
    }
  }
  for (let i = 0; i < knife.length; i += 1) {
    const kp = knife[i];
    const near = nearestOnPolyline(pts, Number(kp.x) || 0, Number(kp.y) || 0);
    if (!near || near.dist > tol) continue;
    if (near.t < 0.02 && near.segIndex === 0) continue;
    if (near.t > 0.98 && near.segIndex === pts.length - 2) continue;
    cuts.push({
      x: near.qx,
      y: near.qy,
      segIndex: near.segIndex,
      t: near.t,
      param: near.segIndex + near.t,
    });
  }
  cuts.sort((a, b) => a.param - b.param);
  const uniq = [];
  cuts.forEach((c) => {
    const prev = uniq[uniq.length - 1];
    if (prev && Math.abs(c.param - prev.param) < 0.045) return;
    if (c.param <= 0.04 || c.param >= pts.length - 1.04) return;
    uniq.push(c);
  });
  return uniq;
}

/**
 * Parte un path en N+1 trozos según hits ordenados (segIndex + t).
 */
export function splitPathAtOrderedHits(points, hits) {
  const pts = (Array.isArray(points) ? points : []).map((p) => ({
    x: Number(p?.x) || 0,
    y: Number(p?.y) || 0,
  }));
  const list = (Array.isArray(hits) ? hits : [])
    .map((h) => ({
      x: clamp01Local(h.x),
      y: clamp01Local(h.y),
      segIndex: Math.floor(Number(h.segIndex)),
      t: Number(h.t),
    }))
    .filter(
      (h) =>
        h.segIndex >= 0 && h.segIndex < pts.length - 1 && Number.isFinite(h.t),
    )
    .sort((a, b) => a.segIndex + a.t - (b.segIndex + b.t));
  if (pts.length < 2 || !list.length) return null;
  const pieces = [];
  let current = [pts[0]];
  let hi = 0;
  for (let j = 1; j < pts.length; j += 1) {
    while (hi < list.length && list[hi].segIndex === j - 1) {
      const cut = { x: list[hi].x, y: list[hi].y };
      current.push(cut);
      if (current.length >= 2) pieces.push(current);
      current = [cut];
      hi += 1;
    }
    current.push(pts[j]);
  }
  if (current.length >= 2) pieces.push(current);
  return pieces.length >= 2 ? pieces : null;
}

/**
 * Corta paths, formas y texto que cruza el knife.
 * Paths: todos los cruces → N+1 piezas. Texto: parte caja + contenido.
 */
export function knifeCutMarks(marks, knifePoints, tol = 0.028) {
  const knife = Array.isArray(knifePoints) ? knifePoints : [];
  const list = Array.isArray(marks) ? marks : [];
  if (knife.length < 2) return list;
  const out = [];
  list.forEach((m) => {
    if (m.type === "text") {
      const split = knifeSplitBoxMark(m, knife);
      if (!split?.parts?.length) {
        out.push(m);
        return;
      }
      const [t0, t1] = splitTextContent(m.text, split.splitVertical, split.t);
      out.push(
        clampMark({ ...split.parts[0], type: "text", text: t0 }),
        clampMark({ ...split.parts[1], type: "text", text: t1 }),
      );
      return;
    }
    if (
      m.type === "rect" ||
      m.type === "roundrect" ||
      m.type === "ellipse" ||
      m.type === "polygon" ||
      m.type === "star" ||
      m.type === "grid" ||
      m.type === "polarGrid" ||
      m.type === "flare"
    ) {
      const split = knifeSplitBoxMark(m, knife);
      if (split?.parts?.length) out.push(...split.parts);
      else out.push(m);
      return;
    }
    if (
      (m.type !== "path" && m.type !== "textpath") ||
      !Array.isArray(m.points) ||
      m.points.length < 2
    ) {
      out.push(m);
      return;
    }
    const hits = collectKnifePathCuts(m.points, knife, tol);
    if (!hits.length) {
      out.push(m);
      return;
    }
    const parts = splitPathAtOrderedHits(m.points, hits);
    if (!parts) {
      out.push(m);
      return;
    }
    parts.forEach((pts) => {
      out.push(
        clampMark({
          ...m,
          type: m.type === "textpath" ? "path" : m.type,
          id: newMarkId(),
          points: pts,
          closed: false,
          text: undefined,
        }),
      );
    });
  });
  return out;
}

const SCISSORS_GEOM_TYPES = new Set([
  "rect",
  "roundrect",
  "ellipse",
  "polygon",
  "star",
  "grid",
  "polarGrid",
  "flare",
  "arc",
]);

function polylineIsClosed(points, eps = 1e-4) {
  const pts = Array.isArray(points) ? points : [];
  if (pts.length < 3) return false;
  const a = pts[0];
  const b = pts[pts.length - 1];
  return (
    Math.hypot(
      (Number(a.x) || 0) - (Number(b.x) || 0),
      (Number(a.y) || 0) - (Number(b.y) || 0),
    ) <= eps
  );
}

/**
 * Abre un anillo cerrado en el hit: un path con hueco visible en el corte
 * (extremos = cut y el vértice previo del segmento).
 */
function openClosedRingAtHit(ring, hit) {
  const n = ring.length;
  if (n < 3 || !hit) return null;
  const i = hit.segIndex;
  if (i < 0 || i >= n) return null;
  const cut = {
    x: clamp01Local(hit.qx),
    y: clamp01Local(hit.qy),
  };
  const opened = [cut];
  for (let k = 1; k < n; k += 1) {
    opened.push({
      x: Number(ring[(i + k) % n].x) || 0,
      y: Number(ring[(i + k) % n].y) || 0,
    });
  }
  opened.push({
    x: Number(ring[i].x) || 0,
    y: Number(ring[i].y) || 0,
  });
  return opened.length >= 2 ? opened : null;
}

/**
 * Contorno (polyline) de una marca para Scissors.
 * Formas cerradas incluyen el cierre (último ≈ primero).
 */
export function markOutlinePoints(mark, steps = 48) {
  if (!mark) return null;
  const t = String(mark.type || "").toLowerCase();
  if (
    (t === "path" || t === "textpath") &&
    Array.isArray(mark.points) &&
    mark.points.length >= 2
  ) {
    return mark.points.map((p) => ({
      x: Number(p?.x) || 0,
      y: Number(p?.y) || 0,
    }));
  }
  if (t === "line" || t === "arrow" || t === "dimension") {
    return [
      { x: Number(mark.x1) || 0, y: Number(mark.y1) || 0 },
      { x: Number(mark.x2) || 0, y: Number(mark.y2) || 0 },
    ];
  }
  if (t === "arc") {
    return sampleEllipticalArcPoints(mark, steps);
  }
  if (
    t === "rect" ||
    t === "roundrect" ||
    t === "grid" ||
    t === "polargrid" ||
    t === "flare"
  ) {
    const x = Number(mark.x) || 0;
    const y = Number(mark.y) || 0;
    const w = Number(mark.w) || 0;
    const h = Number(mark.h) || 0;
    if (w < 0.001 || h < 0.001) return null;
    if (t === "roundrect") {
      const rx = Math.min(Number(mark.rx) || 0.04, Math.min(w, h) / 2);
      const n = Math.max(4, Math.min(12, Math.round(Number(steps) / 8) || 6));
      const pts = [];
      const corners = [
        { cx: x + w - rx, cy: y + rx, a0: -Math.PI / 2, a1: 0 },
        { cx: x + w - rx, cy: y + h - rx, a0: 0, a1: Math.PI / 2 },
        { cx: x + rx, cy: y + h - rx, a0: Math.PI / 2, a1: Math.PI },
        { cx: x + rx, cy: y + rx, a0: Math.PI, a1: (3 * Math.PI) / 2 },
      ];
      corners.forEach((c) => {
        for (let i = 0; i <= n; i += 1) {
          const a = c.a0 + ((c.a1 - c.a0) * i) / n;
          pts.push({ x: c.cx + rx * Math.cos(a), y: c.cy + rx * Math.sin(a) });
        }
      });
      if (pts.length) pts.push({ ...pts[0] });
      return pts;
    }
    // Densificar lados: el corte proyecta bien en cualquier punto del borde
    const edge = (a, b, segs = 8) => {
      const out = [];
      for (let i = 0; i < segs; i += 1) {
        const t0 = i / segs;
        out.push({
          x: a.x + (b.x - a.x) * t0,
          y: a.y + (b.y - a.y) * t0,
        });
      }
      return out;
    };
    const tl = { x, y };
    const tr = { x: x + w, y };
    const br = { x: x + w, y: y + h };
    const bl = { x, y: y + h };
    return [
      ...edge(tl, tr),
      ...edge(tr, br),
      ...edge(br, bl),
      ...edge(bl, tl),
      { ...tl },
    ];
  }
  if (t === "ellipse") {
    const x = Number(mark.x) || 0;
    const y = Number(mark.y) || 0;
    const w = Math.max(Number(mark.w) || 0, 0.001);
    const h = Math.max(Number(mark.h) || 0, 0.001);
    const cx = x + w / 2;
    const cy = y + h / 2;
    const rx = w / 2;
    const ry = h / 2;
    const n = Math.max(16, Math.min(64, Math.round(Number(steps) || 48)));
    const pts = [];
    for (let i = 0; i <= n; i += 1) {
      const a = (i / n) * Math.PI * 2;
      pts.push({ x: cx + rx * Math.cos(a), y: cy + ry * Math.sin(a) });
    }
    return pts;
  }
  if (t === "polygon") {
    const verts = regularPolygonPoints({
      x: mark.x,
      y: mark.y,
      w: mark.w,
      h: mark.h,
      sides: mark.sides,
    });
    if (verts.length < 3) return null;
    return [...verts, { ...verts[0] }];
  }
  if (t === "star") {
    const verts = starPolygonPoints({
      x: mark.x,
      y: mark.y,
      w: mark.w,
      h: mark.h,
      tips: mark.tips,
      innerRatio: mark.innerRatio,
    });
    if (verts.length < 3) return null;
    return [...verts, { ...verts[0] }];
  }
  // Texto vivo: caja tipográfica (como figura). Path tools → Create Outlines primero
  // o usarán este rectángulo como contorno.
  if (t === "text") {
    const b = markBounds(mark);
    if (!b) return null;
    const { x, y, w, h } = b;
    return [
      { x, y },
      { x: x + w, y },
      { x: x + w, y: y + h },
      { x, y: y + h },
      { x, y },
    ];
  }
  return null;
}

/**
 * Scissors: corta el trazo en (x,y).
 * - Forma cerrada → abre el contorno (1 path con hueco en el corte).
 * - Path/línea abiertos → 2 piezas.
 * En geométricas proyecta al borde más cercano (clic en relleno también vale).
 */
export function scissorsCutMark(marks, markId, x, y, tol = 0.05) {
  const list = Array.isArray(marks) ? marks : [];
  const src = list.find((m) => String(m.id) === String(markId));
  if (!src) return null;

  const strokeBase = {
    color: src.color,
    strokeColor: src.strokeColor || src.color,
    stroke: src.stroke,
    opacity: src.opacity,
    fill: "none",
    z: src.z,
  };

  if (src.type === "line" || src.type === "arrow") {
    const parts = splitPathAtPoint(
      [
        { x: Number(src.x1) || 0, y: Number(src.y1) || 0 },
        { x: Number(src.x2) || 0, y: Number(src.y2) || 0 },
      ],
      x,
      y,
      tol,
    );
    if (!parts) return null;
    return list
      .filter((m) => String(m.id) !== String(markId))
      .concat([
        clampMark({
          ...src,
          type: "line",
          id: newMarkId(),
          x1: parts[0][0].x,
          y1: parts[0][0].y,
          x2: parts[0][parts[0].length - 1].x,
          y2: parts[0][parts[0].length - 1].y,
        }),
        clampMark({
          ...src,
          type: "line",
          id: newMarkId(),
          x1: parts[1][0].x,
          y1: parts[1][0].y,
          x2: parts[1][parts[1].length - 1].x,
          y2: parts[1][parts[1].length - 1].y,
        }),
      ]);
  }

  const outline = markOutlinePoints(src);
  if (!outline || outline.length < 2) return null;

  const isGeom = SCISSORS_GEOM_TYPES.has(String(src.type || "").toLowerCase());
  const closed = polylineIsClosed(outline);
  const hit = nearestOnPolyline(outline, x, y);
  if (!hit) return null;

  // Geom: siempre proyecta al borde (el hit-test ya eligió la forma).
  // Path abierto: exige cercanía al trazo.
  if (!isGeom && hit.dist > tol) return null;

  if (closed) {
    const ring = outline.slice(0, -1);
    if (ring.length < 3) return null;
    // Re-hit sobre el anillo + segmento de cierre
    const loop = [...ring, { ...ring[0] }];
    const loopHit = nearestOnPolyline(loop, x, y) || hit;
    const opened = openClosedRingAtHit(ring, {
      ...loopHit,
      segIndex: Math.min(loopHit.segIndex, ring.length - 1),
    });
    if (!opened) return null;
    return list
      .filter((m) => String(m.id) !== String(markId))
      .concat([
        clampMark({
          ...strokeBase,
          type: "path",
          id: newMarkId(),
          points: opened,
        }),
      ]);
  }

  // Path / arco abierto → 2 piezas en el punto proyectado
  const parts = splitPathAtPoint(
    outline,
    hit.qx,
    hit.qy,
    Math.max(tol, hit.dist + 1e-6),
  );
  if (!parts) {
    // Fallback: partir por el hit aunque esté en extremo (paths cortos)
    const i = hit.segIndex;
    const cut = {
      x: clamp01Local(hit.qx),
      y: clamp01Local(hit.qy),
    };
    const left = [...outline.slice(0, i + 1), cut];
    const right = [cut, ...outline.slice(i + 1)];
    if (left.length < 2 || right.length < 2) return null;
    return list
      .filter((m) => String(m.id) !== String(markId))
      .concat([
        clampMark({
          ...strokeBase,
          type: "path",
          id: newMarkId(),
          points: left,
        }),
        clampMark({
          ...strokeBase,
          type: "path",
          id: newMarkId(),
          points: right,
        }),
      ]);
  }
  return list
    .filter((m) => String(m.id) !== String(markId))
    .concat([
      clampMark({
        ...strokeBase,
        type: "path",
        id: newMarkId(),
        points: parts[0],
      }),
      clampMark({
        ...strokeBase,
        type: "path",
        id: newMarkId(),
        points: parts[1],
      }),
    ]);
}

/** Path `d` de arco elíptico abierto (coords 0–1). Ángulos en grados, 0 = este, + = horario en y↓. */
export function ellipticalArcPathD(mark) {
  const x = Number(mark?.x) || 0;
  const y = Number(mark?.y) || 0;
  const w = Math.max(Number(mark?.w) || 0, 0.001);
  const h = Math.max(Number(mark?.h) || 0, 0.001);
  const cx = x + w / 2;
  const cy = y + h / 2;
  const rx = w / 2;
  const ry = h / 2;
  let startDeg = Number(mark?.startAngle);
  let sweepDeg = Number(mark?.sweep);
  if (!Number.isFinite(startDeg)) startDeg = -90;
  if (!Number.isFinite(sweepDeg) || Math.abs(sweepDeg) < 1) sweepDeg = 90;
  const a0 = (startDeg * Math.PI) / 180;
  const a1 = ((startDeg + sweepDeg) * Math.PI) / 180;
  const x0 = cx + rx * Math.cos(a0);
  const y0 = cy + ry * Math.sin(a0);
  const x1 = cx + rx * Math.cos(a1);
  const y1 = cy + ry * Math.sin(a1);
  const large = Math.abs(sweepDeg) > 180 ? 1 : 0;
  const sweepFlag = sweepDeg >= 0 ? 1 : 0;
  return `M ${x0} ${y0} A ${rx} ${ry} 0 ${large} ${sweepFlag} ${x1} ${y1}`;
}

/** Muestras a lo largo del arco (hit-test / PDF). */
export function sampleEllipticalArcPoints(mark, steps = 24) {
  const x = Number(mark?.x) || 0;
  const y = Number(mark?.y) || 0;
  const w = Math.max(Number(mark?.w) || 0, 0.001);
  const h = Math.max(Number(mark?.h) || 0, 0.001);
  const cx = x + w / 2;
  const cy = y + h / 2;
  const rx = w / 2;
  const ry = h / 2;
  let startDeg = Number(mark?.startAngle);
  let sweepDeg = Number(mark?.sweep);
  if (!Number.isFinite(startDeg)) startDeg = -90;
  if (!Number.isFinite(sweepDeg) || Math.abs(sweepDeg) < 1) sweepDeg = 90;
  const n = Math.max(2, Math.min(64, Math.round(Number(steps) || 24)));
  const out = [];
  for (let i = 0; i <= n; i += 1) {
    const t = i / n;
    const deg = startDeg + sweepDeg * t;
    const a = (deg * Math.PI) / 180;
    out.push({ x: cx + rx * Math.cos(a), y: cy + ry * Math.sin(a) });
  }
  return out;
}

/** Ray-casting point-in-polygon (coords normalizadas). */
export function pointInPolygon(x, y, polygon) {
  const pts = Array.isArray(polygon) ? polygon : [];
  if (pts.length < 3) return false;
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = Number(pts[i].x) || 0;
    const yi = Number(pts[i].y) || 0;
    const xj = Number(pts[j].x) || 0;
    const yj = Number(pts[j].y) || 0;
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Punto representativo de una marca (centro aprox.) para lasso / wand. */
export function markAnchorPoint(mark) {
  if (!mark) return null;
  if (
    (mark.type === "path" || mark.type === "textpath") &&
    Array.isArray(mark.points) &&
    mark.points.length
  ) {
    let sx = 0;
    let sy = 0;
    mark.points.forEach((p) => {
      sx += Number(p.x) || 0;
      sy += Number(p.y) || 0;
    });
    return {
      x: sx / mark.points.length,
      y: sy / mark.points.length,
    };
  }
  if (
    mark.type === "line" ||
    mark.type === "arrow" ||
    mark.type === "dimension"
  ) {
    return {
      x: ((Number(mark.x1) || 0) + (Number(mark.x2) || 0)) / 2,
      y: ((Number(mark.y1) || 0) + (Number(mark.y2) || 0)) / 2,
    };
  }
  if (
    mark.type === "rect" ||
    mark.type === "roundrect" ||
    mark.type === "ellipse" ||
    mark.type === "arc" ||
    mark.type === "polygon" ||
    mark.type === "star" ||
    mark.type === "grid" ||
    mark.type === "polarGrid" ||
    mark.type === "flare" ||
    mark.type === "text"
  ) {
    return {
      x: (Number(mark.x) || 0) + (Number(mark.w) || 0) / 2,
      y: (Number(mark.y) || 0) + (Number(mark.h) || 0.04) / 2,
    };
  }
  return null;
}

/** ¿Algún punto de muestra de la marca cae en el polígono (lasso)? */
function markSamplesForLasso(mark) {
  const samples = [];
  const anchor = markAnchorPoint(mark);
  if (anchor) samples.push(anchor);
  const b = markBounds(mark);
  if (b) {
    samples.push(
      { x: b.x, y: b.y },
      { x: b.x + b.w, y: b.y },
      { x: b.x, y: b.y + b.h },
      { x: b.x + b.w, y: b.y + b.h },
      { x: b.cx, y: b.cy },
    );
  }
  const outline = markOutlinePoints(mark, 16);
  if (outline?.length) {
    outline.forEach((p, i) => {
      if (i % 2 === 0)
        samples.push({ x: Number(p.x) || 0, y: Number(p.y) || 0 });
    });
  }
  return samples;
}

/**
 * Ids de marcas tocadas por el lasso: ancla, esquinas bbox u outline.
 * (Antes solo centro — fallaba con formas grandes.)
 */
export function markIdsInsidePolygon(marks, polygon) {
  const list = Array.isArray(marks) ? marks : [];
  const ids = [];
  list.forEach((m) => {
    const samples = markSamplesForLasso(m);
    if (samples.some((p) => pointInPolygon(p.x, p.y, polygon))) {
      ids.push(String(m.id));
    }
  });
  return ids;
}

function hexToRgb(hex) {
  const s = String(hex || "")
    .replace("#", "")
    .trim();
  const full =
    s.length === 3 ? `${s[0]}${s[0]}${s[1]}${s[1]}${s[2]}${s[2]}` : s;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

/** Distancia RGB 0–1 (0 = igual). */
export function hexColorDistance(a, b) {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  if (!ca || !cb) {
    return String(a || "")
      .trim()
      .toLowerCase() ===
      String(b || "")
        .trim()
        .toLowerCase()
      ? 0
      : 1;
  }
  return (
    Math.hypot(ca.r - cb.r, ca.g - cb.g, ca.b - cb.b) /
    Math.hypot(255, 255, 255)
  );
}

/**
 * Ids de marcas con color similar.
 * @param {number} [tolerance=0] 0 = exacto; 0.18 ≈ wand Illustrator laxo
 */
export function markIdsWithColor(marks, color, tolerance = 0) {
  const target = String(color || "").trim();
  if (!target) return [];
  const tol = Math.max(0, Number(tolerance) || 0);
  return (Array.isArray(marks) ? marks : [])
    .filter((m) => {
      const candidates = [m.color, m.fill, m.strokeColor].filter(
        (c) => c && String(c).toLowerCase() !== "none",
      );
      if (!candidates.length) return false;
      if (tol <= 0) {
        const t = target.toLowerCase();
        return candidates.some((c) => String(c).trim().toLowerCase() === t);
      }
      return candidates.some((c) => hexColorDistance(c, target) <= tol);
    })
    .map((m) => String(m.id));
}

/**
 * Ángulos de arco según puntero (mismo convenio que ellipticalArcPathD).
 * startAngle fijo (defecto −90 = arriba); sweep sigue al cursor.
 */
export function arcAnglesFromPointer({
  ox = 0,
  oy = 0,
  w = 0,
  h = 0,
  px = 0,
  py = 0,
  startAngle = -90,
  shiftKey = false,
} = {}) {
  const x = w < 0 ? ox + w : ox;
  const y = h < 0 ? oy + h : oy;
  const aw = Math.max(Math.abs(w), 0.001);
  const ah = Math.max(Math.abs(h), 0.001);
  const cx = x + aw / 2;
  const cy = y + ah / 2;
  const start = Number.isFinite(Number(startAngle)) ? Number(startAngle) : -90;
  let pointerDeg = (Math.atan2(py - cy, px - cx) * 180) / Math.PI;
  let sweep = pointerDeg - start;
  while (sweep > 180) sweep -= 360;
  while (sweep <= -180) sweep += 360;
  if (shiftKey) sweep = Math.round(sweep / 45) * 45;
  if (Math.abs(sweep) < 5) sweep = Math.sign(sweep || 1) * 5;
  sweep = Math.max(-359, Math.min(359, sweep));
  return { startAngle: start, sweep };
}

/**
 * Path eraser: corta el trazo cerca de (x,y) → 0–2 paths abiertos.
 * null = no tocó el stroke.
 */
export function erasePathNearPoint(points, x, y, radius = 0.036) {
  const pts = (Array.isArray(points) ? points : []).map((p) => ({
    x: Number(p?.x) || 0,
    y: Number(p?.y) || 0,
  }));
  if (pts.length < 2) return null;
  const r = Math.max(0.012, Number(radius) || 0.036);
  const hit = nearestOnPolyline(pts, x, y);
  if (!hit || hit.dist > r) return null;
  const parts = splitPathAtPoint(pts, hit.qx, hit.qy, r * 1.8);
  if (!parts) {
    const kept = pts.filter((p) => Math.hypot(p.x - x, p.y - y) > r);
    if (kept.length >= 2 && kept.length < pts.length) return [kept];
    if (kept.length < 2) return [];
    return null;
  }
  const trimEnd = (arr, fromStart) => {
    const out = [...arr];
    while (out.length >= 2) {
      const tip = fromStart ? out[0] : out[out.length - 1];
      if (Math.hypot(tip.x - hit.qx, tip.y - hit.qy) > r * 0.85) break;
      if (fromStart) out.shift();
      else out.pop();
    }
    return out;
  };
  return parts
    .map((seg, i) => trimEnd(trimEnd(seg, true), false))
    .filter((seg) => seg.length >= 2);
}

/** Vértices de polígono regular en bbox normalizado. */
export function regularPolygonPoints({
  x = 0,
  y = 0,
  w = 0,
  h = 0,
  sides = 6,
  rotation = -Math.PI / 2,
} = {}) {
  const cx = Number(x) + Number(w) / 2;
  const cy = Number(y) + Number(h) / 2;
  const rx = Math.max(Number(w) / 2, 0.0001);
  const ry = Math.max(Number(h) / 2, 0.0001);
  const n = Math.max(3, Math.min(24, Math.round(Number(sides) || 6)));
  const pts = [];
  for (let i = 0; i < n; i += 1) {
    const a = rotation + (i * 2 * Math.PI) / n;
    pts.push({ x: cx + rx * Math.cos(a), y: cy + ry * Math.sin(a) });
  }
  return pts;
}

/** Estrella (punta exterior / interior) en bbox normalizado. */
export function starPolygonPoints({
  x = 0,
  y = 0,
  w = 0,
  h = 0,
  tips = 5,
  innerRatio = 0.45,
  rotation = -Math.PI / 2,
} = {}) {
  const cx = Number(x) + Number(w) / 2;
  const cy = Number(y) + Number(h) / 2;
  const rx = Math.max(Number(w) / 2, 0.0001);
  const ry = Math.max(Number(h) / 2, 0.0001);
  const n = Math.max(3, Math.min(16, Math.round(Number(tips) || 5)));
  const ratio = Math.min(0.9, Math.max(0.15, Number(innerRatio) || 0.45));
  const pts = [];
  for (let i = 0; i < n * 2; i += 1) {
    const a = rotation + (i * Math.PI) / n;
    const r = i % 2 === 0 ? 1 : ratio;
    pts.push({ x: cx + rx * r * Math.cos(a), y: cy + ry * r * Math.sin(a) });
  }
  return pts;
}

/**
 * Samplea una cúbica Bézier (coords artboard 0–1).
 * p0/p1 anclas; c1/c2 controles absolutos.
 */
export function sampleCubicBezier(p0, c1, c2, p1, steps = 8) {
  const n = Math.max(2, Math.min(48, Math.floor(Number(steps)) || 8));
  const ax = Number(p0?.x) || 0;
  const ay = Number(p0?.y) || 0;
  const bx = Number(c1?.x) || 0;
  const by = Number(c1?.y) || 0;
  const cx = Number(c2?.x) || 0;
  const cy = Number(c2?.y) || 0;
  const dx = Number(p1?.x) || 0;
  const dy = Number(p1?.y) || 0;
  const out = [];
  for (let i = 0; i <= n; i += 1) {
    const t = i / n;
    const u = 1 - t;
    out.push({
      x:
        u * u * u * ax +
        3 * u * u * t * bx +
        3 * u * t * t * cx +
        t * t * t * dx,
      y:
        u * u * u * ay +
        3 * u * u * t * by +
        3 * u * t * t * cy +
        t * t * t * dy,
    });
  }
  return out;
}

/**
 * Anclas Pen VC: { x, y, ox?, oy?, ix?, iy? }
 * ox/oy = handleOut relativo; ix/iy = handleIn relativo.
 * Densifica a polilínea para knife/scissors/PDF.
 */
export function penAnchorsToPolyline(anchors, stepsPerSeg = 8) {
  const a = (Array.isArray(anchors) ? anchors : []).map((p) => ({
    x: Number(p?.x) || 0,
    y: Number(p?.y) || 0,
    ox: Number.isFinite(Number(p?.ox)) ? Number(p.ox) : null,
    oy: Number.isFinite(Number(p?.oy)) ? Number(p.oy) : null,
    ix: Number.isFinite(Number(p?.ix)) ? Number(p.ix) : null,
    iy: Number.isFinite(Number(p?.iy)) ? Number(p.iy) : null,
  }));
  if (!a.length) return [];
  if (a.length === 1) return [{ x: a[0].x, y: a[0].y }];
  const steps = Math.max(2, Math.min(24, Math.floor(Number(stepsPerSeg)) || 8));
  const out = [];
  for (let i = 0; i < a.length - 1; i += 1) {
    const A = a[i];
    const B = a[i + 1];
    const p0 = { x: A.x, y: A.y };
    const p1 = { x: B.x, y: B.y };
    const hasOut = A.ox != null && A.oy != null;
    const hasIn = B.ix != null && B.iy != null;
    if (!hasOut && !hasIn) {
      if (i === 0) out.push(p0);
      out.push(p1);
      continue;
    }
    const c1 = hasOut
      ? { x: p0.x + A.ox, y: p0.y + A.oy }
      : {
          x: p0.x + (p1.x - p0.x) / 3,
          y: p0.y + (p1.y - p0.y) / 3,
        };
    const c2 = hasIn
      ? { x: p1.x + B.ix, y: p1.y + B.iy }
      : {
          x: p1.x - (p1.x - p0.x) / 3,
          y: p1.y - (p1.y - p0.y) / 3,
        };
    const samples = sampleCubicBezier(p0, c1, c2, p1, steps);
    if (i === 0) out.push(...samples);
    else out.push(...samples.slice(1));
  }
  return out;
}

/** Umbral de arrastre Pen: por debajo = ancla esquina (sin asas). */
export const PEN_HANDLE_DRAG_MIN = 0.006;

/** Chaikin corner-cutting para suavizar un path abierto. */
export function smoothPathPoints(points, passes = 2) {
  let pts = (Array.isArray(points) ? points : []).map((p) => ({
    x: Number(p?.x) || 0,
    y: Number(p?.y) || 0,
  }));
  if (pts.length < 3) return pts;
  const nPass = Math.max(1, Math.min(4, Math.round(Number(passes) || 2)));
  for (let p = 0; p < nPass; p += 1) {
    const next = [pts[0]];
    for (let i = 0; i < pts.length - 1; i += 1) {
      const a = pts[i];
      const b = pts[i + 1];
      next.push({
        x: 0.75 * a.x + 0.25 * b.x,
        y: 0.75 * a.y + 0.25 * b.y,
      });
      next.push({
        x: 0.25 * a.x + 0.75 * b.x,
        y: 0.25 * a.y + 0.75 * b.y,
      });
    }
    next.push(pts[pts.length - 1]);
    pts = next;
  }
  return pts;
}

function clamp01Local(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

function distPointToSeg(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len2 = dx * dx + dy * dy || 1;
  let t = ((px - x1) * dx + (py - y1) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const qx = x1 + t * dx;
  const qy = y1 + t * dy;
  return { dist: Math.hypot(px - qx, py - qy), t, qx, qy };
}

/** Punto más cercano en una polilínea (segmento + t). */
export function nearestOnPolyline(points, x, y) {
  const pts = Array.isArray(points) ? points : [];
  let best = null;
  for (let i = 1; i < pts.length; i += 1) {
    const a = pts[i - 1];
    const b = pts[i];
    const hit = distPointToSeg(
      x,
      y,
      Number(a.x) || 0,
      Number(a.y) || 0,
      Number(b.x) || 0,
      Number(b.y) || 0,
    );
    if (!best || hit.dist < best.dist) {
      best = { ...hit, segIndex: i - 1 };
    }
  }
  return best;
}

/** Índice del ancla más cercana, o -1. */
export function nearestAnchorIndex(points, x, y, tol = 0.03) {
  const pts = Array.isArray(points) ? points : [];
  let bestI = -1;
  let bestD = tol;
  pts.forEach((p, i) => {
    const d = Math.hypot((Number(p.x) || 0) - x, (Number(p.y) || 0) - y);
    if (d <= bestD) {
      bestD = d;
      bestI = i;
    }
  });
  return bestI;
}

/**
 * Corta un path en (x,y): devuelve dos arrays de puntos, o null.
 */
export function splitPathAtPoint(points, x, y, tol = 0.028) {
  const pts = (Array.isArray(points) ? points : []).map((p) => ({
    x: Number(p?.x) || 0,
    y: Number(p?.y) || 0,
  }));
  if (pts.length < 2) return null;
  const hit = nearestOnPolyline(pts, x, y);
  if (!hit || hit.dist > tol) return null;
  const cut = {
    x: clamp01Local(hit.qx),
    y: clamp01Local(hit.qy),
  };
  const i = hit.segIndex;
  const left = [...pts.slice(0, i + 1), cut];
  const right = [cut, ...pts.slice(i + 1)];
  if (left.length < 2 || right.length < 2) return null;
  // Evitar cortes degenerados en extremos
  if (hit.t < 0.02 && i === 0) return null;
  if (hit.t > 0.98 && i === pts.length - 2) return null;
  return [left, right];
}

/** Inserta un ancla en el segmento más cercano. */
export function insertPathAnchor(points, x, y, tol = 0.028) {
  const pts = (Array.isArray(points) ? points : []).map((p) => ({
    x: Number(p?.x) || 0,
    y: Number(p?.y) || 0,
  }));
  if (pts.length < 2) return null;
  const near = nearestAnchorIndex(pts, x, y, tol * 0.7);
  if (near >= 0) return null; // ya hay ancla
  const hit = nearestOnPolyline(pts, x, y);
  if (!hit || hit.dist > tol) return null;
  const next = [...pts];
  next.splice(hit.segIndex + 1, 0, {
    x: clamp01Local(hit.qx),
    y: clamp01Local(hit.qy),
  });
  return next;
}

/** Elimina el ancla más cercana (mín. 2 puntos). */
export function deletePathAnchor(points, x, y, tol = 0.035) {
  const pts = (Array.isArray(points) ? points : []).map((p) => ({
    x: Number(p?.x) || 0,
    y: Number(p?.y) || 0,
  }));
  if (pts.length <= 2) return null;
  const i = nearestAnchorIndex(pts, x, y, tol);
  if (i < 0) return null;
  return pts.filter((_, idx) => idx !== i);
}

/** Mueve el ancla `index` a (x,y). */
export function movePathAnchor(
  points,
  index,
  x,
  y,
  { allowOutside = true } = {},
) {
  const pts = (Array.isArray(points) ? points : []).map((p) => ({
    x: Number(p?.x) || 0,
    y: Number(p?.y) || 0,
  }));
  const i = Math.floor(Number(index));
  if (i < 0 || i >= pts.length) return pts;
  const nx = Number(x);
  const ny = Number(y);
  pts[i] = allowOutside
    ? { x: Number.isFinite(nx) ? nx : 0, y: Number.isFinite(ny) ? ny : 0 }
    : { x: clamp01Local(x), y: clamp01Local(y) };
  return pts;
}

/** Límite de caracteres por marca de texto (UI + PDF). */
export const TEXT_MARK_MAX_LEN = 2000;

/** Divisiones de cuadrícula / polar / flare (herramientas VC). */
export const GRID_COLS_MIN = 2;
export const GRID_COLS_MAX = 24;
export const GRID_ROWS_MIN = 2;
export const GRID_ROWS_MAX = 24;
export const POLAR_RINGS_MIN = 2;
export const POLAR_RINGS_MAX = 24;
export const POLAR_RAYS_MIN = 3;
export const POLAR_RAYS_MAX = 36;

export function clampGridCols(raw, fallback = 4) {
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n)) return fallback;
  return Math.min(GRID_COLS_MAX, Math.max(GRID_COLS_MIN, n));
}

export function clampGridRows(raw, fallback = 4) {
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n)) return fallback;
  return Math.min(GRID_ROWS_MAX, Math.max(GRID_ROWS_MIN, n));
}

export function clampPolarRings(raw, fallback = 4) {
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n)) return fallback;
  return Math.min(POLAR_RINGS_MAX, Math.max(POLAR_RINGS_MIN, n));
}

export function clampPolarRays(raw, fallback = 8) {
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n)) return fallback;
  return Math.min(POLAR_RAYS_MAX, Math.max(POLAR_RAYS_MIN, n));
}

/** Parsea "4x6", "4×6", "4,6" → { cols, rows } o null. */
export function parseGridDivisions(raw, fallback = { cols: 4, rows: 4 }) {
  const s = String(raw || "").trim();
  const m = s.match(/(\d+)\s*[x×*,/;]\s*(\d+)/i);
  if (m) {
    return {
      cols: clampGridCols(m[1], fallback.cols),
      rows: clampGridRows(m[2], fallback.rows),
    };
  }
  const only = s.match(/^(\d+)$/);
  if (only) {
    const n = clampGridCols(only[1], fallback.cols);
    return { cols: n, rows: clampGridRows(n, fallback.rows) };
  }
  return {
    cols: clampGridCols(fallback.cols, 4),
    rows: clampGridRows(fallback.rows, 4),
  };
}

/** Parsea "4x8" / "4,8" → rings × rays. */
export function parsePolarDivisions(raw, fallback = { rings: 4, rays: 8 }) {
  const s = String(raw || "").trim();
  const m = s.match(/(\d+)\s*[x×*,/;]\s*(\d+)/i);
  if (m) {
    return {
      rings: clampPolarRings(m[1], fallback.rings),
      rays: clampPolarRays(m[2], fallback.rays),
    };
  }
  const only = s.match(/^(\d+)$/);
  if (only) {
    const n = clampPolarRings(only[1], fallback.rings);
    return { rings: n, rays: clampPolarRays(n * 2, fallback.rays) };
  }
  return {
    rings: clampPolarRings(fallback.rings, 4),
    rays: clampPolarRays(fallback.rays, 8),
  };
}

/** Tamaño tipográfico de marcas (pt lógicos del canvas). */
export const MARK_FONT_SIZE_MIN = 6;
export const MARK_FONT_SIZE_MAX = 200;
/** Presets del desplegable Size (+ el valor actual si no está en la lista). */
export const MARK_FONT_SIZE_PRESETS = [
  6, 7, 8, 9, 10, 11, 12, 13, 14, 16, 18, 20, 22, 24, 28, 32, 36, 40, 48, 56,
  64, 72, 80, 96, 120, 144, 160, 180, 200,
];

export function clampMarkFontSize(raw, fallback = 14) {
  const n = Number(raw);
  const base = Number.isFinite(n) ? n : fallback;
  return Math.min(
    MARK_FONT_SIZE_MAX,
    Math.max(MARK_FONT_SIZE_MIN, Math.round(base)),
  );
}

export function markFontSizeOptions(current) {
  const cur = clampMarkFontSize(current, 14);
  return [...new Set([...MARK_FONT_SIZE_PRESETS, cur])].sort((a, b) => a - b);
}

/** Orden de los grupos del desplegable de tipografía. */
export const MARK_FONT_GROUP_ORDER = [
  "system",
  "sans",
  "serif",
  "display",
  "script",
];

/**
 * Tipografías de marcas de texto. Sistema = PDF estándar.
 * El resto son Google Fonts (OFL) y el PDF incrusta el mismo archivo.
 */
export const MARK_FONT_FAMILIES = {
  sans: {
    id: "sans",
    group: "system",
    label: "Sans",
    css: "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
  },
  serif: {
    id: "serif",
    group: "system",
    label: "Serif",
    css: 'Georgia, "Times New Roman", Times, serif',
  },
  mono: {
    id: "mono",
    group: "system",
    label: "Mono",
    css: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  },
  montserrat: {
    id: "montserrat",
    group: "sans",
    label: "Montserrat",
    css: '"Moodboard Montserrat", Montserrat, "Segoe UI", sans-serif',
  },
  poppins: {
    id: "poppins",
    group: "sans",
    label: "Poppins",
    css: '"Moodboard Poppins", Poppins, "Segoe UI", sans-serif',
  },
  raleway: {
    id: "raleway",
    group: "sans",
    label: "Raleway",
    css: '"Moodboard Raleway", Raleway, "Segoe UI", sans-serif',
  },
  roboto: {
    id: "roboto",
    group: "sans",
    label: "Roboto",
    css: '"Moodboard Roboto", Roboto, "Segoe UI", sans-serif',
  },
  opensans: {
    id: "opensans",
    group: "sans",
    label: "Open Sans",
    css: '"Moodboard Open Sans", "Open Sans", "Segoe UI", sans-serif',
  },
  josefin: {
    id: "josefin",
    group: "sans",
    label: "Josefin Sans",
    css: '"Moodboard Josefin", "Josefin Sans", "Segoe UI", sans-serif',
  },
  barlow: {
    id: "barlow",
    group: "sans",
    label: "Barlow Condensed",
    css: '"Moodboard Barlow", "Arial Narrow", "Segoe UI", sans-serif',
  },
  comfortaa: {
    id: "comfortaa",
    group: "sans",
    label: "Comfortaa",
    css: '"Moodboard Comfortaa", Comfortaa, "Segoe UI", sans-serif',
  },
  oswald: {
    id: "oswald",
    group: "sans",
    label: "Oswald",
    css: '"Moodboard Oswald", "Arial Narrow", Impact, sans-serif',
  },
  inter: {
    id: "inter",
    group: "sans",
    label: "Inter",
    css: '"Moodboard Inter", Inter, "Segoe UI", sans-serif',
  },
  worksans: {
    id: "worksans",
    group: "sans",
    label: "Work Sans",
    css: '"Moodboard Work Sans", "Work Sans", "Segoe UI", sans-serif',
  },
  nunito: {
    id: "nunito",
    group: "sans",
    label: "Nunito Sans",
    css: '"Moodboard Nunito", "Nunito Sans", "Segoe UI", sans-serif',
  },
  dmsans: {
    id: "dmsans",
    group: "sans",
    label: "DM Sans",
    css: '"Moodboard DM Sans", "DM Sans", "Segoe UI", sans-serif',
  },
  manrope: {
    id: "manrope",
    group: "sans",
    label: "Manrope",
    css: '"Moodboard Manrope", Manrope, "Segoe UI", sans-serif',
  },
  spacegrotesk: {
    id: "spacegrotesk",
    group: "sans",
    label: "Space Grotesk",
    css: '"Moodboard Space Grotesk", "Space Grotesk", "Segoe UI", sans-serif',
  },
  outfit: {
    id: "outfit",
    group: "sans",
    label: "Outfit",
    css: '"Moodboard Outfit", Outfit, "Segoe UI", sans-serif',
  },
  urbanist: {
    id: "urbanist",
    group: "sans",
    label: "Urbanist",
    css: '"Moodboard Urbanist", Urbanist, "Segoe UI", sans-serif',
  },
  sora: {
    id: "sora",
    group: "sans",
    label: "Sora",
    css: '"Moodboard Sora", Sora, "Segoe UI", sans-serif',
  },
  lato: {
    id: "lato",
    group: "sans",
    label: "Lato",
    css: '"Moodboard Lato", Lato, "Segoe UI", sans-serif',
  },
  sourcesans: {
    id: "sourcesans",
    group: "sans",
    label: "Source Sans 3",
    css: '"Moodboard Source Sans", "Source Sans 3", "Segoe UI", sans-serif',
  },
  playfair: {
    id: "playfair",
    group: "serif",
    label: "Playfair",
    css: '"Moodboard Playfair", Georgia, "Times New Roman", serif',
  },
  lora: {
    id: "lora",
    group: "serif",
    label: "Lora",
    css: '"Moodboard Lora", Georgia, "Times New Roman", serif',
  },
  baskerville: {
    id: "baskerville",
    group: "serif",
    label: "Libre Baskerville",
    css: '"Moodboard Baskerville", Georgia, "Times New Roman", serif',
  },
  cormorant: {
    id: "cormorant",
    group: "serif",
    label: "Cormorant",
    css: '"Moodboard Cormorant", Georgia, "Times New Roman", serif',
  },
  cinzel: {
    id: "cinzel",
    group: "serif",
    label: "Cinzel",
    css: '"Moodboard Cinzel", Georgia, "Times New Roman", serif',
  },
  merriweather: {
    id: "merriweather",
    group: "serif",
    label: "Merriweather",
    css: '"Moodboard Merriweather", Georgia, "Times New Roman", serif',
  },
  ebgaramond: {
    id: "ebgaramond",
    group: "serif",
    label: "EB Garamond",
    css: '"Moodboard EB Garamond", "EB Garamond", Georgia, serif',
  },
  bodonimoda: {
    id: "bodonimoda",
    group: "serif",
    label: "Bodoni Moda",
    css: '"Moodboard Bodoni Moda", "Bodoni Moda", Georgia, serif',
  },
  fraunces: {
    id: "fraunces",
    group: "serif",
    label: "Fraunces",
    css: '"Moodboard Fraunces", Fraunces, Georgia, serif',
  },
  spectral: {
    id: "spectral",
    group: "serif",
    label: "Spectral",
    css: '"Moodboard Spectral", Spectral, Georgia, serif',
  },
  newsreader: {
    id: "newsreader",
    group: "serif",
    label: "Newsreader",
    css: '"Moodboard Newsreader", Newsreader, Georgia, serif',
  },
  sourceserif: {
    id: "sourceserif",
    group: "serif",
    label: "Source Serif 4",
    css: '"Moodboard Source Serif", "Source Serif 4", Georgia, serif',
  },
  bebas: {
    id: "bebas",
    group: "display",
    label: "Bebas",
    css: '"Moodboard Bebas", Impact, "Arial Narrow", sans-serif',
  },
  anton: {
    id: "anton",
    group: "display",
    label: "Anton",
    css: '"Moodboard Anton", Impact, "Arial Narrow", sans-serif',
  },
  abril: {
    id: "abril",
    group: "display",
    label: "Abril Fatface",
    css: '"Moodboard Abril", Georgia, "Times New Roman", serif',
  },
  archivoblack: {
    id: "archivoblack",
    group: "display",
    label: "Archivo Black",
    css: '"Moodboard Archivo Black", Impact, "Arial Narrow", sans-serif',
  },
  lobster: {
    id: "lobster",
    group: "display",
    label: "Lobster",
    css: '"Moodboard Lobster", "Segoe Script", cursive',
  },
  righteous: {
    id: "righteous",
    group: "display",
    label: "Righteous",
    css: '"Moodboard Righteous", Impact, "Arial Narrow", sans-serif',
  },
  staatliches: {
    id: "staatliches",
    group: "display",
    label: "Staatliches",
    css: '"Moodboard Staatliches", Impact, "Arial Narrow", sans-serif',
  },
  bungee: {
    id: "bungee",
    group: "display",
    label: "Bungee",
    css: '"Moodboard Bungee", Impact, "Arial Narrow", sans-serif',
  },
  blackops: {
    id: "blackops",
    group: "display",
    label: "Black Ops One",
    css: '"Moodboard Black Ops", Impact, "Arial Narrow", sans-serif',
  },
  passionone: {
    id: "passionone",
    group: "display",
    label: "Passion One",
    css: '"Moodboard Passion One", Impact, "Arial Narrow", sans-serif',
  },
  alfaslab: {
    id: "alfaslab",
    group: "display",
    label: "Alfa Slab One",
    css: '"Moodboard Alfa Slab", Impact, "Arial Narrow", sans-serif',
  },
  russoone: {
    id: "russoone",
    group: "display",
    label: "Russo One",
    css: '"Moodboard Russo One", Impact, "Arial Narrow", sans-serif',
  },
  caveat: {
    id: "caveat",
    group: "script",
    label: "Caveat",
    css: '"Moodboard Caveat", "Segoe Script", cursive',
  },
  amatic: {
    id: "amatic",
    group: "script",
    label: "Amatic",
    css: '"Moodboard Amatic", "Segoe Script", cursive',
  },
  pacifico: {
    id: "pacifico",
    group: "script",
    label: "Pacifico",
    css: '"Moodboard Pacifico", "Segoe Script", cursive',
  },
  dancing: {
    id: "dancing",
    group: "script",
    label: "Dancing Script",
    css: '"Moodboard Dancing", "Segoe Script", cursive',
  },
  greatvibes: {
    id: "greatvibes",
    group: "script",
    label: "Great Vibes",
    css: '"Moodboard Great Vibes", "Segoe Script", cursive',
  },
  marker: {
    id: "marker",
    group: "script",
    label: "Permanent Marker",
    css: '"Moodboard Marker", "Segoe Script", cursive',
  },
  satisfy: {
    id: "satisfy",
    group: "script",
    label: "Satisfy",
    css: '"Moodboard Satisfy", "Segoe Script", cursive',
  },
  allura: {
    id: "allura",
    group: "script",
    label: "Allura",
    css: '"Moodboard Allura", "Segoe Script", cursive',
  },
  parisienne: {
    id: "parisienne",
    group: "script",
    label: "Parisienne",
    css: '"Moodboard Parisienne", "Segoe Script", cursive',
  },
  sacramento: {
    id: "sacramento",
    group: "script",
    label: "Sacramento",
    css: '"Moodboard Sacramento", "Segoe Script", cursive',
  },
  yellowtail: {
    id: "yellowtail",
    group: "script",
    label: "Yellowtail",
    css: '"Moodboard Yellowtail", "Segoe Script", cursive',
  },
  cookie: {
    id: "cookie",
    group: "script",
    label: "Cookie",
    css: '"Moodboard Cookie", "Segoe Script", cursive',
  },
  kalam: {
    id: "kalam",
    group: "script",
    label: "Kalam",
    css: '"Moodboard Kalam", "Segoe Script", cursive',
  },
  indieflower: {
    id: "indieflower",
    group: "script",
    label: "Indie Flower",
    css: '"Moodboard Indie Flower", "Segoe Script", cursive',
  },
};

export function markFontGroups() {
  return MARK_FONT_GROUP_ORDER.map((id) => ({
    id,
    families: Object.values(MARK_FONT_FAMILIES).filter(
      (family) => family.group === id,
    ),
  })).filter((group) => group.families.length);
}

export function normalizeMarkFontFamily(raw) {
  const id = String(raw || "sans").toLowerCase();
  return MARK_FONT_FAMILIES[id] ? id : "sans";
}

export function markFontCss(family) {
  return (
    MARK_FONT_FAMILIES[normalizeMarkFontFamily(family)]?.css ||
    MARK_FONT_FAMILIES.sans.css
  );
}

export const DEFAULT_MARK_COLOR = "#e11d48";
export const DEFAULT_MARK_STROKE = 2;

const CLOSED_MARK_TYPES = new Set([
  "rect",
  "roundrect",
  "ellipse",
  "polygon",
  "star",
]);

/** ¿La marca admite relleno (contenido)? Incluye texto (color de letra). */
export function markSupportsFill(type) {
  const t = String(type || "").toLowerCase();
  return CLOSED_MARK_TYPES.has(t) || t === "text" || t === "textpath";
}

/**
 * Ejes SVG objectBoundingBox para degradado lineal (ángulo Illustrator/VC: 0° = →).
 */
export function linearGradientAxis(angleDeg = 0) {
  const rad = ((Number(angleDeg) || 0) * Math.PI) / 180;
  const dx = Math.cos(rad) * 0.5;
  const dy = Math.sin(rad) * 0.5;
  return {
    x1: 0.5 - dx,
    y1: 0.5 - dy,
    x2: 0.5 + dx,
    y2: 0.5 + dy,
  };
}

/** Normaliza objeto gradient de marca (from/to/kind/angle). */
export function normalizeMarkGradient(raw, fallbackFrom = DEFAULT_MARK_COLOR) {
  if (!raw || typeof raw !== "object" || !raw.from || !raw.to) return null;
  const kind =
    String(raw.kind || "linear").toLowerCase() === "radial"
      ? "radial"
      : "linear";
  let angle = Number(raw.angle);
  if (!Number.isFinite(angle)) angle = 0;
  angle = ((angle % 360) + 360) % 360;
  if (angle > 180) angle -= 360;
  return {
    from: normalizeHexColorLoose(raw.from, fallbackFrom),
    to: normalizeHexColorLoose(raw.to, fallbackFrom),
    kind,
    angle: Math.round(angle * 10) / 10,
  };
}

/** Mezcla dos hex (#rgb / #rrggbb) → #rrggbb. */
export function lerpHexColor(a, b, t) {
  const u = Math.min(1, Math.max(0, Number(t) || 0));
  const parse = (h, fb) => {
    const s = normalizeHexColorLoose(h, fb);
    return [
      parseInt(s.slice(1, 3), 16),
      parseInt(s.slice(3, 5), 16),
      parseInt(s.slice(5, 7), 16),
    ];
  };
  const ca = parse(a, DEFAULT_MARK_COLOR);
  const cb = parse(b, DEFAULT_MARK_COLOR);
  const hex = (n) => Math.round(n).toString(16).padStart(2, "0");
  return `#${hex(ca[0] + (cb[0] - ca[0]) * u)}${hex(
    ca[1] + (cb[1] - ca[1]) * u,
  )}${hex(ca[2] + (cb[2] - ca[2]) * u)}`;
}

/**
 * Ejes del annotator de degradado en coords de página (0–1), centro = bbox.
 * `t` 0 = from, 1 = to.
 */
export function gradientAnnotatorAxis(mark, gradient) {
  const b = markBounds(mark);
  if (!b) return null;
  const g = normalizeMarkGradient(gradient || mark?.gradient, mark?.fill);
  if (!g) return null;
  const rad = ((Number(g.angle) || 0) * Math.PI) / 180;
  const len = Math.max(0.04, Math.hypot(b.w, b.h) * 0.55);
  const dx = Math.cos(rad) * len * 0.5;
  const dy = Math.sin(rad) * len * 0.5;
  return {
    cx: b.cx,
    cy: b.cy,
    x1: b.cx - dx,
    y1: b.cy - dy,
    x2: b.cx + dx,
    y2: b.cy + dy,
    angle: g.angle,
    kind: g.kind,
    from: g.from,
    to: g.to,
  };
}

/**
 * Mesh de degradado simple (filas×cols) sobre bbox de la marca.
 * Colores: esquina TL=from, TR/BR mezclan, BL=to (o from→to en Y).
 */
export function createGradientMeshOnMark(
  mark,
  { rows = 3, cols = 3, from, to } = {},
) {
  if (!mark || typeof mark !== "object") return null;
  const b = markBounds(mark);
  if (!b || b.w < 0.01 || b.h < 0.01) return null;
  const r = Math.max(2, Math.min(8, Math.round(Number(rows) || 3)));
  const c = Math.max(2, Math.min(8, Math.round(Number(cols) || 3)));
  const paint = markPaintColors(mark);
  const c0 = normalizeHexColorLoose(
    from || paint.fill || paint.ink,
    DEFAULT_MARK_COLOR,
  );
  const c1 = normalizeHexColorLoose(to || "#ffffff", "#ffffff");
  const points = [];
  for (let i = 0; i < r; i += 1) {
    const row = [];
    const v = r <= 1 ? 0 : i / (r - 1);
    for (let j = 0; j < c; j += 1) {
      const u = c <= 1 ? 0 : j / (c - 1);
      row.push({
        x: b.x + b.w * u,
        y: b.y + b.h * v,
        color: lerpHexColor(c0, c1, (u + v) / 2),
      });
    }
    points.push(row);
  }
  return clampMarkAllowOutside({
    ...mark,
    mesh: { rows: r, cols: c, points },
    // Mesh sustituye fill sólido; gradient dock sigue opcional
    fill: c0,
    color: c0,
  });
}

/** Normaliza mesh de marca (o null). */
export function normalizeMarkMesh(raw) {
  if (!raw || typeof raw !== "object" || !Array.isArray(raw.points))
    return null;
  const points = raw.points
    .map((row) =>
      (Array.isArray(row) ? row : [])
        .map((p) => {
          if (!p || typeof p !== "object") return null;
          const x = Number(p.x);
          const y = Number(p.y);
          if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
          return {
            x,
            y,
            color: normalizeHexColorLoose(p.color, DEFAULT_MARK_COLOR),
          };
        })
        .filter(Boolean),
    )
    .filter((row) => row.length >= 2);
  if (points.length < 2) return null;
  const cols = Math.min(...points.map((row) => row.length));
  if (cols < 2) return null;
  return {
    rows: points.length,
    cols,
    points: points.map((row) => row.slice(0, cols)),
  };
}

/**
 * Resuelve fill / strokeColor de una marca.
 * Legacy: solo `color` → stroke = color; fill = color en cerradas/texto, none en abiertas.
 * Texto: fill = color de letra; strokeColor + stroke = contorno.
 */
export function markPaintColors(mark) {
  const type = String(mark?.type || "").toLowerCase();
  const isText = type === "text" || type === "textpath";
  const ink = normalizeHexColorLoose(mark?.color, DEFAULT_MARK_COLOR);
  const strokeColor = normalizeHexColorLoose(
    mark?.strokeColor ?? mark?.color,
    ink,
  );
  let fill = mark?.fill;
  if (fill == null || fill === "") {
    const closedPath = type === "path" && Boolean(mark?.closed);
    fill = markSupportsFill(type) || closedPath ? ink : "none";
  } else if (String(fill).toLowerCase() === "none") {
    // En texto "none" no tiene sentido → cae a color de letra
    fill = isText ? ink : "none";
  } else {
    fill = normalizeHexColorLoose(fill, ink);
  }
  return { fill, strokeColor, ink };
}

/**
 * Live Paint Bucket (VC K): aplica fill a una forma.
 * Paths abiertos se cierran al pintar (cara rellenable).
 * Barras cabecera/pie: también strokeColor (stroke 0 pero mismo tinte).
 */
export function applyLivePaintFillToMark(mark, fillRaw) {
  if (!mark || typeof mark !== "object") return mark;
  const fill =
    fillRaw == null || String(fillRaw).toLowerCase() === "none"
      ? "none"
      : normalizeHexColorLoose(fillRaw, DEFAULT_MARK_COLOR);
  const t = String(mark.type || "").toLowerCase();
  const ink = fill === "none" ? mark.color : fill;
  if (t === "path" && mark.closed === false) {
    return {
      ...mark,
      closed: true,
      fill,
      color: ink,
      strokeColor: fill === "none" ? mark.strokeColor : fill,
    };
  }
  if (t === "text" || t === "textpath") {
    return { ...mark, fill, color: ink };
  }
  // Cabecera/pie y formas cerradas: fill + color + stroke alineados
  return {
    ...mark,
    fill,
    color: ink,
    strokeColor: fill === "none" ? mark.strokeColor : fill,
  };
}

/** Color de relleno de letra (texto / textpath). */
export function textMarkFillColor(mark) {
  const paint = markPaintColors(mark);
  if (paint.fill && paint.fill !== "none") return paint.fill;
  return paint.ink;
}

/**
 * Estilos CSS de contorno tipográfico (Illustrator-like).
 * stroke 0 = sin contorno. Escala con el tamaño de fuente en px de pantalla.
 */
export function textMarkOutlineCss(mark, fontSizePx) {
  const stroke = Number(mark?.stroke);
  if (!Number.isFinite(stroke) || stroke <= 0) {
    return { WebkitTextStroke: "0", paintOrder: "normal" };
  }
  const paint = markPaintColors(mark);
  const fs = Math.max(8, Number(fontSizePx) || 14);
  // stroke 1–12 → ~2%–14% del alto de letra
  const px = Math.min(
    fs * 0.18,
    Math.max(0.35, (stroke / 12) * fs * 0.14 + stroke * 0.15),
  );
  return {
    WebkitTextStroke: `${px.toFixed(2)}px ${paint.strokeColor}`,
    // stroke detrás del fill (contorno limpio)
    paintOrder: "stroke fill",
  };
}

function clamp01(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

function normalizeHexColorLoose(raw, fallback = DEFAULT_MARK_COLOR) {
  const s = String(raw || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(s)) return s.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(s)) {
    return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`.toLowerCase();
  }
  return fallback;
}

function clampMarkStroke(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return DEFAULT_MARK_STROKE;
  // 0 permitido (textos de reunión sin contorno/sombra).
  return Math.min(12, Math.max(0, Math.round(n * 10) / 10));
}

/** Grosor SVG en coords 0–1 (viewBox 0 0 1 1). Misma fórmula en canvas y preview. */
export function markStrokeSvg(stroke) {
  return Math.max(0.0015, (Number(stroke) || 2) * 0.0022);
}

/**
 * Perfil Width tool (VC): puntos { t∈[0,1], scale } relativos al stroke base.
 * También acepta left/right (simétricos → media).
 */
export function normalizeWidthPoints(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((p) => {
      if (!p || typeof p !== "object") return null;
      const t = Number(p.t);
      let scale = Number(p.scale);
      if (!Number.isFinite(scale)) {
        const left = Number(p.left);
        const right = Number(p.right);
        if (Number.isFinite(left) && Number.isFinite(right)) {
          scale = (left + right) / 2;
        } else if (Number.isFinite(left)) scale = left;
        else if (Number.isFinite(right)) scale = right;
        else return null;
      }
      if (!Number.isFinite(t)) return null;
      return {
        t: Math.max(0, Math.min(1, t)),
        scale: Math.max(0.05, Math.min(8, scale)),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.t - b.t)
    .slice(0, 32);
}

/** Factor de anchura en fracción t (interp. lineal entre pines). */
export function widthScaleAtT(widthPoints, t) {
  const pts = normalizeWidthPoints(widthPoints);
  if (!pts.length) return 1;
  const tt = Math.max(0, Math.min(1, Number(t) || 0));
  if (tt <= pts[0].t) return pts[0].scale;
  if (tt >= pts[pts.length - 1].t) return pts[pts.length - 1].scale;
  for (let i = 1; i < pts.length; i += 1) {
    if (tt <= pts[i].t) {
      const a = pts[i - 1];
      const b = pts[i];
      const span = b.t - a.t || 1e-9;
      const u = (tt - a.t) / span;
      return a.scale + (b.scale - a.scale) * u;
    }
  }
  return 1;
}

function polylineCumSegments(points, closed = false) {
  const pts = (Array.isArray(points) ? points : [])
    .map((p) => ({ x: Number(p?.x) || 0, y: Number(p?.y) || 0 }))
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
  const segs = [];
  let total = 0;
  if (pts.length < 2) return { pts, segs, total };
  const n = pts.length;
  const count = closed && n >= 3 ? n : n - 1;
  for (let i = 0; i < count; i += 1) {
    const a = pts[i];
    const b = pts[(i + 1) % n];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    segs.push({ a, b, len, acc: total });
    total += len;
  }
  return { pts, segs, total };
}

/**
 * Punto + normal izquierda + fracción t en polilínea (Width / Puppet helpers).
 */
export function nearestPolylineFraction(points, x, y, closed = false) {
  const { segs, total } = polylineCumSegments(points, closed);
  if (!segs.length || total < 1e-12) return null;
  let best = null;
  segs.forEach((s) => {
    const hit = distPointToSeg(x, y, s.a.x, s.a.y, s.b.x, s.b.y);
    const t = (s.acc + hit.t * s.len) / total;
    if (!best || hit.dist < best.dist) {
      best = {
        dist: hit.dist,
        t: Math.max(0, Math.min(1, t)),
        qx: hit.qx,
        qy: hit.qy,
      };
    }
  });
  return best;
}

/** Posición + normal izquierda en fracción t. */
export function samplePolylineAtT(points, t, closed = false) {
  const { pts, segs, total } = polylineCumSegments(points, closed);
  if (!segs.length || total < 1e-12) return null;
  const tt = Math.max(0, Math.min(1, Number(t) || 0));
  let target = tt * total;
  if (tt >= 1) target = total;
  for (let i = 0; i < segs.length; i += 1) {
    const s = segs[i];
    if (target <= s.acc + s.len + 1e-12 || i === segs.length - 1) {
      const u = s.len < 1e-12 ? 0 : (target - s.acc) / s.len;
      const uu = Math.max(0, Math.min(1, u));
      const x = s.a.x + (s.b.x - s.a.x) * uu;
      const y = s.a.y + (s.b.y - s.a.y) * uu;
      let tx = s.b.x - s.a.x;
      let ty = s.b.y - s.a.y;
      const len = Math.hypot(tx, ty) || 1;
      tx /= len;
      ty /= len;
      return { x, y, nx: -ty, ny: tx, t: tt };
    }
  }
  const last = pts[pts.length - 1];
  return { x: last.x, y: last.y, nx: 0, ny: -1, t: tt };
}

/**
 * Contorno relleno de stroke variable (Width profile VC).
 * Devuelve puntos del polígono outline, o null.
 */
export function variableWidthOutline(
  points,
  strokePt = 2,
  widthPoints = null,
  { closed = false } = {},
) {
  const { pts, segs, total } = polylineCumSegments(points, closed);
  if (segs.length < 1 || total < 1e-12) return null;
  const profile = normalizeWidthPoints(widthPoints);
  const samples = Math.max(8, Math.min(96, Math.round(total / 0.01) + 1));
  const tSet = new Set([0, 1]);
  profile.forEach((p) => tSet.add(p.t));
  for (let i = 0; i < samples; i += 1) tSet.add(i / (samples - 1));
  const ts = [...tSet].sort((a, b) => a - b);
  const uniq = [];
  ts.forEach((t) => {
    if (!uniq.length || Math.abs(uniq[uniq.length - 1] - t) > 1e-4) {
      uniq.push(t);
    }
  });
  const left = [];
  const right = [];
  uniq.forEach((t) => {
    const s = samplePolylineAtT(pts, t, closed);
    if (!s) return;
    const scale = profile.length ? widthScaleAtT(profile, t) : 1;
    const half = Math.max(0.0008, markStrokeSvg(strokePt) * 0.5 * scale);
    left.push({ x: s.x + s.nx * half, y: s.y + s.ny * half });
    right.push({ x: s.x - s.nx * half, y: s.y - s.ny * half });
  });
  if (left.length < 2) return null;
  if (closed) {
    const outline = [...left, ...right.reverse()];
    if (outline.length < 4) return null;
    outline.push({ ...outline[0] });
    return outline;
  }
  // Casquetes abiertos (como blob)
  const cap = (center, outward, into, half) => {
    const samplesCap = [];
    for (let k = 1; k <= 4; k += 1) {
      const a = (k / 5) * Math.PI;
      const c = Math.cos(a);
      const sn = Math.sin(a);
      samplesCap.push({
        x: center.x + (outward.x * c + into.x * sn) * half,
        y: center.y + (outward.y * c + into.y * sn) * half,
      });
    }
    return samplesCap;
  };
  const s0 = samplePolylineAtT(pts, 0, false);
  const s1 = samplePolylineAtT(pts, 1, false);
  if (!s0 || !s1) return null;
  const half0 = Math.max(
    0.0008,
    markStrokeSvg(strokePt) *
      0.5 *
      (profile.length ? widthScaleAtT(profile, 0) : 1),
  );
  const half1 = Math.max(
    0.0008,
    markStrokeSvg(strokePt) *
      0.5 *
      (profile.length ? widthScaleAtT(profile, 1) : 1),
  );
  const t0x = pts[1].x - pts[0].x;
  const t0y = pts[1].y - pts[0].y;
  const l0 = Math.hypot(t0x, t0y) || 1;
  const startOut = { x: -t0x / l0, y: -t0y / l0 };
  const tn = pts[pts.length - 1].x - pts[pts.length - 2].x;
  const tm = pts[pts.length - 1].y - pts[pts.length - 2].y;
  const ln = Math.hypot(tn, tm) || 1;
  const endOut = { x: tn / ln, y: tm / ln };
  const outline = [
    ...left,
    ...cap(s1, endOut, { x: s1.nx, y: s1.ny }, half1),
    ...right.reverse(),
    ...cap(s0, startOut, { x: s0.nx, y: s0.ny }, half0),
  ];
  if (outline.length < 4) return null;
  outline.push({ ...outline[0] });
  return outline;
}

/**
 * Blob Brush VC: trazo grueso → contorno cerrado relleno (cápsula offset).
 * `strokePt` en unidades de marca (pt UI); radio en coords 0–1.
 */
export function blobStrokeToOutline(points, strokePt = 12) {
  const raw = (Array.isArray(points) ? points : [])
    .map((p) => ({ x: Number(p?.x) || 0, y: Number(p?.y) || 0 }))
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
  if (raw.length < 2) return null;
  // Decimar puntos muy juntos
  const pts = [raw[0]];
  for (let i = 1; i < raw.length; i += 1) {
    const prev = pts[pts.length - 1];
    if (Math.hypot(raw[i].x - prev.x, raw[i].y - prev.y) >= 0.004) {
      pts.push(raw[i]);
    }
  }
  if (pts.length < 2) pts.push(raw[raw.length - 1]);
  const half = Math.max(0.004, markStrokeSvg(strokePt) * 0.55);
  const left = [];
  const right = [];
  for (let i = 0; i < pts.length; i += 1) {
    const prev = pts[Math.max(0, i - 1)];
    const next = pts[Math.min(pts.length - 1, i + 1)];
    let tx = next.x - prev.x;
    let ty = next.y - prev.y;
    const len = Math.hypot(tx, ty) || 1;
    tx /= len;
    ty /= len;
    const nx = -ty;
    const ny = tx;
    left.push({
      x: clamp01Local(pts[i].x + nx * half),
      y: clamp01Local(pts[i].y + ny * half),
    });
    right.push({
      x: clamp01Local(pts[i].x - nx * half),
      y: clamp01Local(pts[i].y - ny * half),
    });
  }
  // Casquetes semicirculares en extremos (4 pasos)
  const cap = (center, outward, into) => {
    const samples = [];
    for (let k = 1; k <= 4; k += 1) {
      const a = (k / 5) * Math.PI;
      const c = Math.cos(a);
      const s = Math.sin(a);
      samples.push({
        x: clamp01Local(center.x + (outward.x * c + into.x * s) * half),
        y: clamp01Local(center.y + (outward.y * c + into.y * s) * half),
      });
    }
    return samples;
  };
  const t0x = pts[1].x - pts[0].x;
  const t0y = pts[1].y - pts[0].y;
  const l0 = Math.hypot(t0x, t0y) || 1;
  const startOut = { x: -t0x / l0, y: -t0y / l0 };
  const startInto = { x: -t0y / l0, y: t0x / l0 };
  const tn = pts[pts.length - 1].x - pts[pts.length - 2].x;
  const tm = pts[pts.length - 1].y - pts[pts.length - 2].y;
  const ln = Math.hypot(tn, tm) || 1;
  const endOut = { x: tn / ln, y: tm / ln };
  const endInto = { x: -tm / ln, y: tn / ln };
  const outline = [
    ...left,
    ...cap(pts[pts.length - 1], endOut, endInto),
    ...right.reverse(),
    ...cap(pts[0], startOut, startInto),
  ];
  if (outline.length < 4) return null;
  outline.push({ ...outline[0] });
  return outline;
}

/**
 * Marcas tocadas por un trazo libre (Shape Builder interactivo / lasso-stroke).
 */
export function markIdsHitByStroke(marks, strokePoints, tol = 0.028) {
  const list = Array.isArray(marks) ? marks : [];
  const stroke = (Array.isArray(strokePoints) ? strokePoints : []).map((p) => ({
    x: Number(p?.x) || 0,
    y: Number(p?.y) || 0,
  }));
  const t = Math.max(0.01, Number(tol) || 0.028);
  if (stroke.length < 1) return [];
  const ids = [];
  list.forEach((m) => {
    const b = markBounds(m);
    if (!b) return;
    const pad = t;
    const nearBox = stroke.some(
      (p) =>
        p.x >= b.x - pad &&
        p.x <= b.x + b.w + pad &&
        p.y >= b.y - pad &&
        p.y <= b.y + b.h + pad,
    );
    if (!nearBox) return;
    const outline = markOutlinePoints(m, 24);
    if (!outline || outline.length < 2) {
      ids.push(String(m.id));
      return;
    }
    const hit = stroke.some((p) => {
      const n = nearestOnPolyline(outline, p.x, p.y);
      if (n && n.dist <= t) return true;
      if (
        m.closed ||
        m.type === "rect" ||
        m.type === "roundrect" ||
        m.type === "ellipse" ||
        m.type === "polygon" ||
        m.type === "star"
      ) {
        return pointInPolygon(p.x, p.y, outline);
      }
      return false;
    });
    if (hit) ids.push(String(m.id));
  });
  return ids;
}

/**
 * Object › Path › Offset Path: desplaza la polilínea por la normal (delta en 0–1).
 */
export function offsetPathPoints(points, deltaNorm = 0.02, closed = false) {
  const raw = (Array.isArray(points) ? points : []).map((p) => ({
    x: Number(p?.x) || 0,
    y: Number(p?.y) || 0,
  }));
  if (raw.length < 2) return raw;
  const d = Number(deltaNorm);
  if (!Number.isFinite(d) || Math.abs(d) < 1e-8) {
    return raw.map((p) => ({ ...p }));
  }
  const isClosed =
    closed ||
    (raw.length >= 3 &&
      Math.hypot(
        raw[0].x - raw[raw.length - 1].x,
        raw[0].y - raw[raw.length - 1].y,
      ) < 0.001);
  const ring = isClosed && raw.length >= 3 ? raw.slice(0, -1) : raw;
  const n = ring.length;
  const out = [];
  for (let i = 0; i < n; i += 1) {
    const prev = ring[(i - 1 + n) % n];
    const cur = ring[i];
    const next = ring[(i + 1) % n];
    let tx;
    let ty;
    if (!isClosed && i === 0) {
      tx = next.x - cur.x;
      ty = next.y - cur.y;
    } else if (!isClosed && i === n - 1) {
      tx = cur.x - prev.x;
      ty = cur.y - prev.y;
    } else {
      tx = next.x - prev.x;
      ty = next.y - prev.y;
    }
    const len = Math.hypot(tx, ty) || 1;
    const nx = -ty / len;
    const ny = tx / len;
    out.push({
      x: clamp01Local(cur.x + nx * d),
      y: clamp01Local(cur.y + ny * d),
    });
  }
  if (isClosed && out.length >= 3) out.push({ ...out[0] });
  return out;
}

/** Distancia punto→segmento (para RDP). */
function distPointSegRdp(px, py, ax, ay, bx, by) {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy || 1;
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/**
 * Object › Path › Simplify (Ramer–Douglas–Peucker).
 * `tolerance` en coords artboard 0–1.
 */
export function simplifyPathPoints(points, tolerance = 0.012) {
  const pts = (Array.isArray(points) ? points : []).map((p) => ({
    x: Number(p?.x) || 0,
    y: Number(p?.y) || 0,
  }));
  if (pts.length < 3) return pts;
  const eps = Math.max(0.001, Math.min(0.08, Number(tolerance) || 0.012));
  const keep = new Array(pts.length).fill(false);
  keep[0] = true;
  keep[pts.length - 1] = true;
  const stack = [[0, pts.length - 1]];
  while (stack.length) {
    const [i0, i1] = stack.pop();
    let maxD = 0;
    let maxI = -1;
    const a = pts[i0];
    const b = pts[i1];
    for (let i = i0 + 1; i < i1; i += 1) {
      const d = distPointSegRdp(pts[i].x, pts[i].y, a.x, a.y, b.x, b.y);
      if (d > maxD) {
        maxD = d;
        maxI = i;
      }
    }
    if (maxD > eps && maxI >= 0) {
      keep[maxI] = true;
      stack.push([i0, maxI], [maxI, i1]);
    }
  }
  return pts.filter((_, i) => keep[i]);
}

/**
 * Object › Path › Outline Stroke: path/caja con stroke → path cerrado relleno.
 */
export function outlineStrokeMark(mark) {
  if (!mark) return null;
  const strokePt = Math.max(0.5, Number(mark.stroke) || 2);
  let pts = null;
  if (
    (mark.type === "path" || mark.type === "textpath") &&
    Array.isArray(mark.points) &&
    mark.points.length >= 2
  ) {
    pts = blobStrokeToOutline(mark.points, strokePt);
  } else {
    const outline = markOutlinePoints(mark, 48);
    if (outline && outline.length >= 2) {
      const closed =
        Boolean(mark.closed) ||
        SCISSORS_GEOM_TYPES.has(String(mark.type || "").toLowerCase()) ||
        polylineIsClosed(outline);
      if (closed) {
        const half = markStrokeSvg(strokePt) * 0.55;
        const outer = offsetPathPoints(outline, half, true);
        const inner = offsetPathPoints(outline, -half * 0.85, true);
        if (outer?.length >= 4 && inner?.length >= 4) {
          // Anillo: exterior + interior invertido (evenodd)
          pts = [...outer, ...[...inner].reverse()];
        } else {
          pts = blobStrokeToOutline(outline, strokePt);
        }
      } else {
        pts = blobStrokeToOutline(outline, strokePt);
      }
    }
  }
  if (!pts || pts.length < 4) return null;
  const ink = mark.strokeColor || mark.color || mark.fill || DEFAULT_MARK_COLOR;
  return clampMark({
    type: "path",
    id: mark.id,
    points: pts,
    closed: true,
    fill: ink,
    color: ink,
    strokeColor: ink,
    stroke: 0.5,
    fillRule: "evenodd",
    opacity: mark.opacity,
    rotation: mark.rotation,
    groupId: mark.groupId,
  });
}

/**
 * Offset Path sobre una marca (geom → path offset).
 */
export function offsetMark(mark, deltaNorm = 0.02) {
  if (!mark) return null;
  const outline = markOutlinePoints(mark, 48);
  if (!outline || outline.length < 2) return null;
  const closed =
    Boolean(mark.closed) ||
    mark.type === "text" ||
    SCISSORS_GEOM_TYPES.has(String(mark.type || "").toLowerCase()) ||
    polylineIsClosed(outline);
  const pts = offsetPathPoints(outline, deltaNorm, closed);
  if (!pts || pts.length < 2) return null;
  const paint = markPaintColors(mark);
  const wasText = mark.type === "text" || mark.type === "textpath";
  return clampMark({
    type: "path",
    id: mark.id,
    points: pts,
    closed,
    fill: closed ? paint.fill || paint.strokeColor || mark.color : "none",
    color: paint.strokeColor || mark.color,
    strokeColor: paint.strokeColor || mark.color,
    stroke: wasText ? 1 : mark.stroke != null ? mark.stroke : 2,
    opacity: mark.opacity,
    rotation: mark.rotation,
    groupId: mark.groupId,
  });
}

/**
 * Type › Create Outlines: texto / textpath → path (caja o trazo).
 * En Illustrator convierte glifos; aquí la caja tipográfica o el path del textpath.
 */
export function createOutlinesMark(mark) {
  if (!mark) return null;
  if (mark.type === "text") {
    const outline = markOutlinePoints(mark);
    if (!outline || outline.length < 4) return null;
    const ink =
      mark.fill && mark.fill !== "none"
        ? mark.fill
        : mark.color || DEFAULT_MARK_COLOR;
    return clampMark({
      type: "path",
      id: mark.id,
      points: outline,
      closed: true,
      fill: ink,
      color: ink,
      strokeColor: mark.strokeColor || ink,
      stroke: Number(mark.stroke) > 0 ? mark.stroke : 0.5,
      opacity: mark.opacity,
      rotation: mark.rotation,
      groupId: mark.groupId,
    });
  }
  if (mark.type === "textpath" && Array.isArray(mark.points)) {
    const ink =
      mark.fill && mark.fill !== "none"
        ? mark.fill
        : mark.color || DEFAULT_MARK_COLOR;
    return clampMark({
      type: "path",
      id: mark.id,
      points: mark.points.map((p) => ({
        x: Number(p.x) || 0,
        y: Number(p.y) || 0,
      })),
      closed: Boolean(mark.closed),
      fill: "none",
      color: ink,
      strokeColor: mark.strokeColor || ink,
      stroke: Math.max(1, Number(mark.stroke) || 2),
      opacity: mark.opacity,
      rotation: mark.rotation,
      groupId: mark.groupId,
    });
  }
  return null;
}

/**
 * Object › Path › Average: promedia anclas de paths seleccionados.
 * mode: 'both' | 'x' | 'y'
 */
export function averagePathAnchors(marks, selectedIds, mode = "both") {
  const list = Array.isArray(marks) ? marks : [];
  const ids = new Set((selectedIds || []).map(String));
  if (!ids.size) return null;
  const targets = list.filter(
    (m) =>
      ids.has(String(m.id)) &&
      (m.type === "path" || m.type === "textpath") &&
      Array.isArray(m.points) &&
      m.points.length >= 1,
  );
  if (!targets.length) return null;
  let sx = 0;
  let sy = 0;
  let n = 0;
  targets.forEach((m) => {
    (m.points || []).forEach((p) => {
      sx += Number(p.x) || 0;
      sy += Number(p.y) || 0;
      n += 1;
    });
  });
  if (n < 2) return null;
  const ax = sx / n;
  const ay = sy / n;
  const axis = String(mode || "both").toLowerCase();
  const idSet = new Set(targets.map((m) => String(m.id)));
  return list.map((m) => {
    if (!idSet.has(String(m.id))) return m;
    return clampMark({
      ...m,
      points: (m.points || []).map((p) => ({
        ...p,
        x: axis === "y" ? Number(p.x) || 0 : ax,
        y: axis === "x" ? Number(p.y) || 0 : ay,
      })),
    });
  });
}

/**
 * Shaper (VectorCraft / Illustrator Shift+N): trazo libre → forma limpia o scribble.
 * Coords normalizadas 0–1. Ref: vectorcraft-ref/crates/geom/src/recognize.rs
 *
 * @returns {{ kind: 'line'|'rect'|'ellipse'|'polygon'|'scribble', ... } | null}
 */
export function recognizeShaperStroke(rawPoints) {
  const pts = (Array.isArray(rawPoints) ? rawPoints : [])
    .map((p) => ({ x: Number(p?.x), y: Number(p?.y) }))
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
  if (pts.length < 2) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let len = 0;
  for (let i = 0; i < pts.length; i += 1) {
    minX = Math.min(minX, pts[i].x);
    minY = Math.min(minY, pts[i].y);
    maxX = Math.max(maxX, pts[i].x);
    maxY = Math.max(maxY, pts[i].y);
    if (i > 0) {
      len += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
    }
  }
  const w = Math.max(0.001, maxX - minX);
  const h = Math.max(0.001, maxY - minY);
  const size = Math.max(w, h);
  if (size < 0.012 || !Number.isFinite(len) || len <= 0) return null;

  const first = pts[0];
  const last = pts[pts.length - 1];
  const chord = Math.hypot(last.x - first.x, last.y - first.y);

  const distSeg = (p, a, b) => {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const l2 = dx * dx + dy * dy;
    if (l2 < 1e-16) return Math.hypot(p.x - a.x, p.y - a.y);
    let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
  };

  // Douglas–Peucker ligero
  const simplify = (list, tol) => {
    if (list.length < 3) return list.slice();
    const keep = new Array(list.length).fill(false);
    keep[0] = true;
    keep[list.length - 1] = true;
    const stack = [[0, list.length - 1]];
    while (stack.length) {
      const [s, e] = stack.pop();
      let bestI = -1;
      let bestD = 0;
      for (let i = s + 1; i < e; i += 1) {
        const d = distSeg(list[i], list[s], list[e]);
        if (d > bestD) {
          bestD = d;
          bestI = i;
        }
      }
      if (bestD > tol && bestI >= 0) {
        keep[bestI] = true;
        if (bestI > s + 1) stack.push([s, bestI]);
        if (e > bestI + 1) stack.push([bestI, e]);
      }
    }
    return list.filter((_, i) => keep[i]);
  };

  const corners = simplify(pts, size * 0.1);
  let reversals = 0;
  const coarse = simplify(pts, size * 0.08);
  for (let i = 1; i < coarse.length - 1; i += 1) {
    const u = {
      x: coarse[i].x - coarse[i - 1].x,
      y: coarse[i].y - coarse[i - 1].y,
    };
    const v = {
      x: coarse[i + 1].x - coarse[i].x,
      y: coarse[i + 1].y - coarse[i].y,
    };
    const lu = Math.hypot(u.x, u.y);
    const lv = Math.hypot(v.x, v.y);
    if (lu > size * 0.05 && lv > size * 0.05) {
      const dot = (u.x * v.x + u.y * v.y) / (lu * lv);
      if (dot < -0.5) reversals += 1;
    }
  }

  // Scribble: zig-zag denso → borrar arte debajo
  if (reversals >= 2 && len > 1.5 * size && chord > 0.2 * size) {
    return {
      kind: "scribble",
      x: minX,
      y: minY,
      w,
      h,
      points: pts,
    };
  }

  // Línea abierta
  let maxDev = 0;
  for (const p of pts) {
    maxDev = Math.max(maxDev, distSeg(p, first, last));
  }
  if (
    chord > 0.45 * size &&
    maxDev < 0.1 * Math.max(chord, 0.01) &&
    len < 1.35 * chord
  ) {
    return { kind: "line", x1: first.x, y1: first.y, x2: last.x, y2: last.y };
  }

  // Cerrado: extremos cerca
  const closed = chord <= 0.28 * size;
  if (!closed) {
    // Trazo abierto no lineal → elipse envolvente (gesto tipo óvalo incompleto)
    if (len > 1.8 * chord && w > 0.02 && h > 0.02) {
      return { kind: "ellipse", x: minX, y: minY, w, h };
    }
    return { kind: "line", x1: first.x, y1: first.y, x2: last.x, y2: last.y };
  }

  // Área aproximada (shoelace)
  let area2 = 0;
  for (let i = 0; i < pts.length; i += 1) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    area2 += a.x * b.y - b.x * a.y;
  }
  const area = Math.abs(area2) / 2;
  const fill = area / (w * h);

  const nCorners = corners.length;
  // Cuadrado / rectángulo
  if (nCorners === 4 || (nCorners === 5 && chord < 0.15 * size)) {
    const nearSquare = Math.abs(w - h) < 0.12 * size;
    const side = nearSquare ? (w + h) / 2 : null;
    return {
      kind: "rect",
      x: nearSquare ? minX + (w - side) / 2 : minX,
      y: nearSquare ? minY + (h - side) / 2 : minY,
      w: nearSquare ? side : w,
      h: nearSquare ? side : h,
    };
  }
  // Triángulo
  if (nCorners === 3 || (nCorners === 4 && fill < 0.55)) {
    return {
      kind: "polygon",
      sides: 3,
      x: minX,
      y: minY,
      w,
      h,
    };
  }
  // Hexágono / polígono
  if (nCorners >= 5 && nCorners <= 8 && fill > 0.55 && fill < 0.9) {
    return {
      kind: "polygon",
      sides: nCorners === 5 ? 5 : nCorners >= 7 ? 8 : 6,
      x: minX,
      y: minY,
      w,
      h,
    };
  }
  // Elipse / círculo
  if (fill > 0.55) {
    const nearCircle = Math.abs(w - h) < 0.12 * size;
    const side = nearCircle ? (w + h) / 2 : null;
    return {
      kind: "ellipse",
      x: nearCircle ? minX + (w - side) / 2 : minX,
      y: nearCircle ? minY + (h - side) / 2 : minY,
      w: nearCircle ? side : w,
      h: nearCircle ? side : h,
    };
  }
  return { kind: "rect", x: minX, y: minY, w, h };
}

/** Convierte el resultado del Shaper en un draft de marca (sin id). */
export function shaperResultToMarkDraft(result, paint = {}) {
  if (!result?.kind) return null;
  const ink = paint.color || paint.strokeColor || DEFAULT_MARK_COLOR;
  const stroke = paint.stroke ?? DEFAULT_MARK_STROKE;
  const fill = paint.fill && paint.fill !== "none" ? paint.fill : "none";
  const base = {
    color: ink,
    strokeColor: ink,
    stroke,
    fill,
  };
  if (result.kind === "line") {
    return {
      type: "line",
      ...base,
      fill: "none",
      x1: result.x1,
      y1: result.y1,
      x2: result.x2,
      y2: result.y2,
    };
  }
  if (result.kind === "rect") {
    return {
      type: "rect",
      ...base,
      x: result.x,
      y: result.y,
      w: result.w,
      h: result.h,
    };
  }
  if (result.kind === "ellipse") {
    return {
      type: "ellipse",
      ...base,
      x: result.x,
      y: result.y,
      w: result.w,
      h: result.h,
    };
  }
  if (result.kind === "polygon") {
    return {
      type: "polygon",
      ...base,
      x: result.x,
      y: result.y,
      w: result.w,
      h: result.h,
      sides: result.sides || 6,
    };
  }
  return null;
}

/** Marcas tocadas por un scribble del Shaper (borrar). */
export function marksHitByShaperScribble(marks, scribble) {
  const list = Array.isArray(marks) ? marks : [];
  const pts = scribble?.points || [];
  if (!pts.length) return [];
  const pad = 0.02;
  const sx = Number(scribble.x) - pad;
  const sy = Number(scribble.y) - pad;
  const sw = Number(scribble.w) + pad * 2;
  const sh = Number(scribble.h) + pad * 2;
  return list.filter((m) => {
    const b = markBounds(m);
    if (!b) return false;
    const overlap =
      b.x < sx + sw && b.x + b.w > sx && b.y < sy + sh && b.y + b.h > sy;
    if (!overlap) return false;
    // Algún punto del trazo cerca del AABB de la marca
    return pts.some(
      (p) =>
        p.x >= b.x - pad &&
        p.x <= b.x + b.w + pad &&
        p.y >= b.y - pad &&
        p.y <= b.y + b.h + pad,
    );
  });
}

/** Hit-test AABB (preview / activar mesa en 1 clic). De arriba a abajo. */
export function hitMarkAt(marks, nx, ny, tol = 0.025) {
  const list = Array.isArray(marks) ? marks : [];
  const x = Number(nx);
  const y = Number(ny);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const m = list[i];
    const b = markBounds(m);
    if (!b) continue;
    const pad = Math.max(tol, 0.012);
    const w = Math.max(b.w, pad * 2);
    const h = Math.max(b.h, pad * 2);
    const bx = b.w < pad * 2 ? b.cx - w / 2 : b.x - pad;
    const by = b.h < pad * 2 ? b.cy - h / 2 : b.y - pad;
    if (x >= bx && x <= bx + w + pad && y >= by && y <= by + h + pad) {
      return m;
    }
  }
  return null;
}

function clampMarkPoint(p) {
  if (!p || typeof p !== "object") return { x: 0, y: 0 };
  return { x: clamp01(p.x), y: clamp01(p.y) };
}

/**
 * Desplaza un path cerrado como cuerpo rígido para caber en [0,1]
 * sin aplastar vértices (clamp por punto encogía cabecera/pie al borde).
 */
export function fitClosedPathInPage(points) {
  const pts = (Array.isArray(points) ? points : []).map((p) => ({
    x: Number(p?.x) || 0,
    y: Number(p?.y) || 0,
  }));
  if (pts.length < 2) return pts;
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  let sx = 0;
  let sy = 0;
  const w = maxX - minX;
  const h = maxY - minY;
  if (w <= 1) {
    if (minX < 0) sx = -minX;
    else if (maxX > 1) sx = 1 - maxX;
  } else {
    sx = -minX; // cabe hacia la izquierda; puede asomar por la derecha
  }
  if (h <= 1) {
    if (minY < 0) sy = -minY;
    else if (maxY > 1) sy = 1 - maxY;
  } else {
    sy = -minY;
  }
  if (!sx && !sy) return pts;
  return pts.map((p) => ({ x: p.x + sx, y: p.y + sy }));
}

const MARK_TYPE_BY_LOWER = Object.fromEntries(
  MARK_TYPES.map((t) => [String(t).toLowerCase(), t]),
);

/**
 * Normalize a page annotation mark (coords 0–1 over the canvas).
 * `allowOutside`: no reclavar al filo (arrastre fuera de la mesa sin deformar).
 */
export function clampMark(raw = {}, { allowOutside = false } = {}) {
  const typeRaw = String(raw.type || "").toLowerCase();
  const type = MARK_TYPE_BY_LOWER[typeRaw] || "path";
  const color = normalizeHexColorLoose(raw.color, DEFAULT_MARK_COLOR);
  const stroke = clampMarkStroke(raw.stroke ?? raw.strokeWidth);
  const strokeColor = normalizeHexColorLoose(
    raw.strokeColor ?? raw.color,
    color,
  );
  const closedPath = type === "path" && Boolean(raw.closed);
  let fill;
  if (raw.fill == null || raw.fill === "") {
    fill = markSupportsFill(type) || closedPath ? color : "none";
  } else if (String(raw.fill).toLowerCase() === "none") {
    fill = "none";
  } else {
    fill = normalizeHexColorLoose(raw.fill, color);
  }
  const base = {
    id: String(raw.id || newMarkId()),
    type,
    color,
    stroke,
    strokeColor,
    fill,
  };
  const opacityRaw = Number(raw.opacity);
  if (Number.isFinite(opacityRaw) && opacityRaw < 0.999) {
    base.opacity = Math.max(0.05, Math.min(1, opacityRaw));
  }
  if (raw.role != null && String(raw.role).trim()) {
    base.role = String(raw.role).trim().slice(0, 48);
  }
  // Copia pegada (sin role de cabecera/pie): no colapsar en scrubChromeMarkDupes
  if (raw.free === 1 || raw.free === true) {
    base.free = 1;
  }
  if (raw.groupId != null && String(raw.groupId).trim()) {
    base.groupId = String(raw.groupId).trim().slice(0, 64);
  }
  const mapPoint = (p) => {
    if (!p || typeof p !== "object") return { x: 0, y: 0 };
    const x = Number(p.x);
    const y = Number(p.y);
    if (allowOutside) {
      return {
        x: Number.isFinite(x) ? x : 0,
        y: Number.isFinite(y) ? y : 0,
      };
    }
    return clampMarkPoint(p);
  };
  // Cerrado: primero desplazar rígido (sin clamp01 por vértice → evitaba aplastar).
  const mapClosed = (pts) => {
    const rawMapped = (Array.isArray(pts) ? pts : []).map((p) => {
      if (!p || typeof p !== "object") return { x: 0, y: 0 };
      const x = Number(p.x);
      const y = Number(p.y);
      return {
        x: Number.isFinite(x) ? x : 0,
        y: Number.isFinite(y) ? y : 0,
      };
    });
    return allowOutside ? rawMapped : fitClosedPathInPage(rawMapped);
  };
  const clampCoord = (v, fallback = 0) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return fallback;
    return allowOutside ? n : clamp01(n);
  };
  if (
    raw.gradient &&
    typeof raw.gradient === "object" &&
    raw.gradient.from &&
    raw.gradient.to
  ) {
    const kind =
      String(raw.gradient.kind || "linear").toLowerCase() === "radial"
        ? "radial"
        : "linear";
    let angle = Number(raw.gradient.angle);
    if (!Number.isFinite(angle)) angle = 0;
    angle = ((angle % 360) + 360) % 360;
    if (angle > 180) angle -= 360;
    base.gradient = {
      from: normalizeHexColorLoose(raw.gradient.from, color),
      to: normalizeHexColorLoose(raw.gradient.to, color),
      kind,
      angle: Math.round(angle * 10) / 10,
    };
  }
  const meshNorm = normalizeMarkMesh(raw.mesh);
  if (meshNorm) base.mesh = meshNorm;
  if (raw.puppet && typeof raw.puppet === "object") {
    const restPoints = Array.isArray(raw.puppet.restPoints)
      ? raw.puppet.restPoints
          .map((p) => {
            const x = Number(p?.x);
            const y = Number(p?.y);
            if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
            return { x, y };
          })
          .filter(Boolean)
      : [];
    const pins = Array.isArray(raw.puppet.pins)
      ? raw.puppet.pins
          .map((p) => {
            const rx = Number(p?.rest?.x);
            const ry = Number(p?.rest?.y);
            const ax = Number(p?.at?.x ?? p?.rest?.x);
            const ay = Number(p?.at?.y ?? p?.rest?.y);
            if (![rx, ry, ax, ay].every(Number.isFinite)) return null;
            return { rest: { x: rx, y: ry }, at: { x: ax, y: ay } };
          })
          .filter(Boolean)
          .slice(0, 24)
      : [];
    if (restPoints.length >= 2 && pins.length) {
      base.puppet = { restPoints, pins };
    }
  }
  const widthPts = normalizeWidthPoints(raw.widthPoints);
  if (widthPts.length) base.widthPoints = widthPts;
  if (raw.envelope && typeof raw.envelope === "object") {
    const envType =
      String(raw.envelope.type || "").toLowerCase() === "mesh"
        ? "mesh"
        : "warp";
    if (envType === "mesh") {
      const rows = Math.max(
        1,
        Math.min(12, Math.round(Number(raw.envelope.rows) || 3)),
      );
      const cols = Math.max(
        1,
        Math.min(12, Math.round(Number(raw.envelope.cols) || 4)),
      );
      const points = Array.isArray(raw.envelope.points)
        ? raw.envelope.points
            .map((p) => {
              const x = Number(p?.x);
              const y = Number(p?.y);
              const u = Number(p?.u);
              const v = Number(p?.v);
              if (![x, y].every(Number.isFinite)) return null;
              return {
                x,
                y,
                u: Number.isFinite(u) ? u : 0,
                v: Number.isFinite(v) ? v : 0,
                rest: {
                  x: Number(p?.rest?.x) || x,
                  y: Number(p?.rest?.y) || y,
                },
              };
            })
            .filter(Boolean)
            .slice(0, 200)
        : [];
      base.envelope = {
        type: "mesh",
        rows,
        cols,
        ...(points.length ? { points } : null),
      };
    } else {
      const style = String(raw.envelope.style || "arc").slice(0, 24);
      const bend = Number(raw.envelope.bend);
      const h = Number(raw.envelope.h);
      const v = Number(raw.envelope.v);
      base.envelope = {
        type: "warp",
        style,
        bend: Number.isFinite(bend) ? Math.max(-100, Math.min(100, bend)) : 50,
        horizontal: raw.envelope.horizontal !== false,
        h: Number.isFinite(h) ? Math.max(-100, Math.min(100, h)) : 0,
        v: Number.isFinite(v) ? Math.max(-100, Math.min(100, v)) : 0,
      };
    }
  }
  if (raw.objectsOnPath && typeof raw.objectsOnPath === "object") {
    const members = Array.isArray(raw.objectsOnPath.members)
      ? raw.objectsOnPath.members
          .map((mem) => {
            if (!mem || mem.id == null) return null;
            const t = Number(mem.t);
            return {
              id: String(mem.id),
              t: Number.isFinite(t) ? Math.max(0, Math.min(1, t)) : 0,
            };
          })
          .filter(Boolean)
          .slice(0, 64)
      : [];
    if (members.length) {
      base.objectsOnPath = {
        members,
        rotate: Number(raw.objectsOnPath.rotate) || 0,
        followPath: raw.objectsOnPath.followPath !== false,
      };
    }
  }
  if (raw.onPath && typeof raw.onPath === "object" && raw.onPath.pathId) {
    const t = Number(raw.onPath.t);
    base.onPath = {
      pathId: String(raw.onPath.pathId),
      t: Number.isFinite(t) ? Math.max(0, Math.min(1, t)) : 0,
    };
  }
  if (raw.symbolId != null && String(raw.symbolId).trim()) {
    base.symbolId = String(raw.symbolId).trim().slice(0, 64);
  }

  if (type === "path") {
    const closed = Boolean(raw.closed);
    const rawPts = Array.isArray(raw.points) ? raw.points : [];
    // Cerrado: fit rígido (no aplastar); allowOutside = sin reclavar a la mesa.
    const pts = closed ? mapClosed(rawPts) : rawPts.map(mapPoint);
    const holesRaw = Array.isArray(raw.holes) ? raw.holes : [];
    const holes = holesRaw
      .map((ring) => {
        const hr = Array.isArray(ring) ? ring : [];
        const mapped = closed ? mapClosed(hr) : hr.map(mapPoint);
        return mapped.length >= 3 ? mapped : null;
      })
      .filter(Boolean);
    return {
      ...base,
      points: pts.length ? pts : [{ x: 0, y: 0 }],
      rotation: normalizeRotation(raw.rotation),
      ...(closed ? { closed: true } : null),
      ...(holes.length
        ? {
            holes,
            fillRule: raw.fillRule === "nonzero" ? "nonzero" : "evenodd",
          }
        : null),
    };
  }
  if (type === "line" || type === "arrow" || type === "dimension") {
    const out = {
      ...base,
      x1: clampCoord(raw.x1, 0),
      y1: clampCoord(raw.y1, 0),
      x2: clampCoord(raw.x2, 0),
      y2: clampCoord(raw.y2, 0),
      rotation: normalizeRotation(raw.rotation),
    };
    if (type === "dimension") {
      if (String(raw.unit || "").toLowerCase() === "mm") out.unit = "mm";
      else if (raw.unit != null) out.unit = "pct";
      if (raw.label != null && String(raw.label).trim()) {
        out.label = String(raw.label).trim().slice(0, 48);
      }
    }
    return out;
  }
  if (
    type === "ellipse" ||
    type === "arc" ||
    type === "rect" ||
    type === "roundrect" ||
    type === "polygon" ||
    type === "star" ||
    type === "grid" ||
    type === "polarGrid" ||
    type === "flare"
  ) {
    let x = Number(raw.x);
    let y = Number(raw.y);
    let w = Number(raw.w);
    let h = Number(raw.h);
    if (!Number.isFinite(x)) x = 0;
    if (!Number.isFinite(y)) y = 0;
    if (!Number.isFinite(w)) w = 0;
    if (!Number.isFinite(h)) h = 0;
    if (w < 0) {
      x += w;
      w = -w;
    }
    if (h < 0) {
      y += h;
      h = -h;
    }
    w = Math.max(0.008, w);
    h = Math.max(0.008, h);
    if (allowOutside) {
      // Cuerpo rígido: no achicar w/h al borde (eso deformaba al arrastrar)
      x = Number.isFinite(x) ? x : 0;
      y = Number.isFinite(y) ? y : 0;
    } else {
      x = clamp01(x);
      y = clamp01(y);
      w = Math.min(w, Math.max(0.008, 1 - x));
      h = Math.min(h, Math.max(0.008, 1 - y));
    }
    const out = {
      ...base,
      x,
      y,
      w,
      h,
      rotation: normalizeRotation(raw.rotation),
    };
    if (type === "arc") {
      let startAngle = Number(raw.startAngle);
      let sweep = Number(raw.sweep);
      if (!Number.isFinite(startAngle)) startAngle = -90;
      if (!Number.isFinite(sweep) || Math.abs(sweep) < 1) sweep = 90;
      out.startAngle = Math.max(-360, Math.min(360, startAngle));
      out.sweep = Math.max(-359, Math.min(359, sweep));
    }
    if (type === "roundrect") {
      const maxR = Math.min(w, h) / 2;
      let rx = Number(raw.rx);
      if (!Number.isFinite(rx)) rx = Math.min(0.04, maxR || 0.04);
      out.rx = Math.min(Math.max(0, rx), maxR || 0);
    }
    if (type === "polygon") {
      out.sides = Math.max(3, Math.min(24, Math.round(Number(raw.sides) || 6)));
    }
    if (type === "star") {
      out.tips = Math.max(
        3,
        Math.min(16, Math.round(Number(raw.tips ?? raw.points) || 5)),
      );
      out.innerRatio = Math.min(
        0.9,
        Math.max(0.15, Number(raw.innerRatio) || 0.45),
      );
    }
    if (type === "grid") {
      out.cols = clampGridCols(raw.cols, 4);
      out.rows = clampGridRows(raw.rows, 4);
    }
    if (type === "polarGrid") {
      out.rings = clampPolarRings(raw.rings, 4);
      out.rays = clampPolarRays(raw.rays, 8);
    }
    if (type === "flare") {
      out.rings = Math.max(1, Math.min(12, clampPolarRings(raw.rings, 3)));
      out.rays = Math.max(4, Math.min(32, clampPolarRays(raw.rays, 8)));
    }
    return out;
  }
  if (type === "textpath") {
    const fontSize = clampMarkFontSize(raw.fontSize, 14);
    const pts = Array.isArray(raw.points) ? raw.points.map(mapPoint) : [];
    const vertical =
      raw.orientation === "vertical" ||
      raw.vertical === true ||
      raw.vertical === 1;
    return {
      ...base,
      type: "textpath",
      points:
        pts.length >= 2
          ? pts
          : [
              { x: 0.1, y: 0.5 },
              { x: 0.9, y: 0.5 },
            ],
      text: String(raw.text ?? "").slice(0, TEXT_MARK_MAX_LEN),
      fontSize,
      fontFamily: normalizeMarkFontFamily(raw.fontFamily),
      fontWeight: normalizeMarkFontWeight(raw.fontWeight),
      rotation: normalizeRotation(raw.rotation),
      stroke: 0,
      ...(vertical ? { orientation: "vertical" } : null),
    };
  }
  // text
  const fontSize = clampMarkFontSize(raw.fontSize, 14);
  let x = Number(raw.x);
  let y = Number(raw.y);
  if (!Number.isFinite(x)) x = 0;
  if (!Number.isFinite(y)) y = 0;
  let w = Number(raw.w);
  if (!Number.isFinite(w) || w <= 0) {
    // Legacy sin ancho: estimar por caracteres / tamaño
    const lines = String(raw.text ?? "").split(/\r?\n/);
    const longest = lines.reduce((max, line) => Math.max(max, line.length), 1);
    w = Math.min(0.55, Math.max(0.1, longest * 0.011 * (fontSize / 14)));
  }
  w = Math.max(0.05, w);
  if (!allowOutside) {
    w = Math.min(1, w);
    if (x + w > 1) x = 1 - w;
    if (x < 0) x = 0;
    if (y < 0) y = 0;
    if (y > 0.98) y = 0.98;
  }
  let h = Number(raw.h);
  if (Number.isFinite(h) && h > 0.015) {
    h = Math.max(0.02, h);
    if (!allowOutside) {
      h = Math.min(0.95, h);
      if (y + h > 1) y = Math.max(0, 1 - h);
    }
  } else {
    h = undefined;
  }
  const charOffsets = normalizeTextCharOffsets(raw.charOffsets);
  return {
    ...base,
    x,
    y,
    w,
    ...(h != null ? { h } : null),
    text: String(raw.text ?? "").slice(0, TEXT_MARK_MAX_LEN),
    fontSize,
    fontFamily: normalizeMarkFontFamily(raw.fontFamily),
    fontWeight: normalizeMarkFontWeight(raw.fontWeight),
    textAlign: normalizeMarkTextAlign(raw.textAlign),
    writingMode: normalizeMarkWritingMode(raw.writingMode),
    rotation: normalizeRotation(raw.rotation),
    ...(charOffsets.length ? { charOffsets } : null),
  };
}

/** Offsets por glifo (Touch Type). */
export function normalizeTextCharOffsets(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((o) => {
      if (!o || typeof o !== "object") return null;
      const i = Math.round(Number(o.i));
      const dx = Number(o.dx);
      const dy = Number(o.dy);
      if (!Number.isFinite(i) || i < 0) return null;
      if (!Number.isFinite(dx) || !Number.isFinite(dy)) return null;
      if (Math.abs(dx) < 1e-6 && Math.abs(dy) < 1e-6) return null;
      return {
        i,
        dx: Math.max(-0.2, Math.min(0.2, dx)),
        dy: Math.max(-0.2, Math.min(0.2, dy)),
      };
    })
    .filter(Boolean)
    .slice(0, 200);
}

/** horizontal-tb | vertical-rl (Texto vertical VC). */
export function normalizeMarkWritingMode(raw) {
  const s = String(raw || "").toLowerCase();
  if (
    s === "vertical-rl" ||
    s === "vertical" ||
    s === "tb" ||
    s === "vertical-lr"
  ) {
    return "vertical-rl";
  }
  return "horizontal-tb";
}

/** 400 | 700 (Character · Weight). */
export function normalizeMarkFontWeight(raw) {
  const n = Number(raw);
  if (n >= 600) return 700;
  const s = String(raw || "").toLowerCase();
  if (s === "bold" || s === "700" || s === "800" || s === "900") return 700;
  return 400;
}

/** left | center | right (Paragraph). */
export function normalizeMarkTextAlign(raw) {
  const s = String(raw || "left").toLowerCase();
  if (s === "center" || s === "middle") return "center";
  if (s === "right" || s === "end") return "right";
  return "left";
}

/** Grados CSS (positivo = horario), −180…180. */
export function normalizeRotation(raw) {
  let n = Number(raw);
  if (!Number.isFinite(n)) return 0;
  n = ((((n + 180) % 360) + 360) % 360) - 180;
  if (Object.is(n, -0)) n = 0;
  return Math.round(n * 10) / 10;
}

/** Ángulo pantalla (grados) desde centro → puntero. */
export function pointerAngleDeg(cx, cy, px, py) {
  return (Math.atan2(py - cy, px - cx) * 180) / Math.PI;
}

/**
 * Nueva rotación al arrastrar el handle.
 * Shift → snap 15°.
 */
export function rotationFromPointerDrag({
  centerX,
  centerY,
  clientX,
  clientY,
  startAngle,
  originRotation,
  shiftKey = false,
} = {}) {
  const cur = pointerAngleDeg(centerX, centerY, clientX, clientY);
  let next = (Number(originRotation) || 0) + (cur - (Number(startAngle) || 0));
  if (shiftKey) next = Math.round(next / 15) * 15;
  return normalizeRotation(next);
}

/** Delta de pantalla → ejes locales de un elemento rotado (CSS). */
export function worldDeltaToLocal(dx, dy, rotationDeg) {
  const rad = ((Number(rotationDeg) || 0) * Math.PI) / 180;
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  return {
    dx: dx * c + dy * s,
    dy: -dx * s + dy * c,
  };
}

/** Tipos con bbox x/y/w/h redimensionable. */
export const BOX_MARK_TYPES = [
  "rect",
  "roundrect",
  "ellipse",
  "arc",
  "polygon",
  "star",
  "grid",
  "polarGrid",
  "flare",
];

/**
 * Vértices editables con A (selección directa) para formas con bbox.
 * Ellipse/arc: muestreo; rect: 4 esquinas; polygon/star: vértices reales.
 */
export function boxMarkAnchorPoints(mark) {
  if (!mark) return [];
  const t = String(mark.type || "").toLowerCase();
  const x = Number(mark.x) || 0;
  const y = Number(mark.y) || 0;
  const w = Math.max(0.001, Number(mark.w) || 0);
  const h = Math.max(0.001, Number(mark.h) || 0);
  if (t === "polygon") {
    return regularPolygonPoints({
      x,
      y,
      w,
      h,
      sides: mark.sides,
    });
  }
  if (t === "star") {
    return starPolygonPoints({
      x,
      y,
      w,
      h,
      tips: mark.tips,
      innerRatio: mark.innerRatio,
    });
  }
  if (t === "ellipse" || t === "arc") {
    const cx = x + w / 2;
    const cy = y + h / 2;
    const rx = w / 2;
    const ry = h / 2;
    const n = 8;
    let a0 = -Math.PI / 2;
    let sweep = Math.PI * 2;
    if (t === "arc") {
      const startDeg = Number(mark.startAngle);
      const sweepDeg = Number(mark.sweep);
      a0 = ((Number.isFinite(startDeg) ? startDeg : -90) * Math.PI) / 180;
      sweep = ((Number.isFinite(sweepDeg) ? sweepDeg : 90) * Math.PI) / 180;
    }
    const pts = [];
    const steps = t === "arc" ? Math.max(3, n) : n;
    for (let i = 0; i < steps; i += 1) {
      const a = a0 + (sweep * i) / (t === "arc" ? steps - 1 : steps);
      pts.push({ x: cx + rx * Math.cos(a), y: cy + ry * Math.sin(a) });
    }
    return pts;
  }
  if (
    t === "rect" ||
    t === "roundrect" ||
    t === "grid" ||
    t === "polarGrid" ||
    t === "flare"
  ) {
    return [
      { x, y },
      { x: x + w, y },
      { x: x + w, y: y + h },
      { x, y: y + h },
    ];
  }
  return [];
}

/**
 * Convierte forma bbox (rect/elipse/…) en path cerrado para poder deformar con A.
 * Si ya es path, la devuelve tal cual.
 */
export function expandBoxMarkToPath(mark) {
  if (!mark) return null;
  const t = String(mark.type || "").toLowerCase();
  if (t === "path" || t === "textpath") return mark;
  if (!BOX_MARK_TYPES.some((x) => String(x).toLowerCase() === t)) return null;
  let points = boxMarkAnchorPoints(mark);
  // Elipse: más puntos al expandir para que el contorno no quede octogonal
  if (t === "ellipse") {
    const x = Number(mark.x) || 0;
    const y = Number(mark.y) || 0;
    const w = Math.max(0.001, Number(mark.w) || 0);
    const h = Math.max(0.001, Number(mark.h) || 0);
    const cx = x + w / 2;
    const cy = y + h / 2;
    const rx = w / 2;
    const ry = h / 2;
    const n = 24;
    points = [];
    for (let i = 0; i < n; i += 1) {
      const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
      points.push({ x: cx + rx * Math.cos(a), y: cy + ry * Math.sin(a) });
    }
  }
  if (!points.length) return null;
  const {
    x: _x,
    y: _y,
    w: _w,
    h: _h,
    rx: _rx,
    sides: _sides,
    tips: _tips,
    innerRatio: _ir,
    cols: _cols,
    rows: _rows,
    rings: _rings,
    rays: _rays,
    startAngle: _sa,
    sweep: _sw,
    ...rest
  } = mark;
  return clampMarkAllowOutside({
    ...rest,
    type: "path",
    points,
    closed: true,
  });
}

export function markHasBox(mark) {
  if (!mark) return false;
  const t = String(mark.type || "").toLowerCase();
  if (BOX_MARK_TYPES.some((x) => String(x).toLowerCase() === t)) return true;
  // Path cerrado (cabecera/pie con esquinas cortadas, etc.): bbox como un rect
  if (
    (t === "path" || t === "textpath") &&
    mark.closed &&
    Array.isArray(mark.points) &&
    mark.points.length >= 3
  ) {
    return Boolean(markBounds(mark));
  }
  return false;
}

/** Escala los puntos de un path para encajar en un bbox nuevo. */
function scalePathPointsToBox(points, fromBox, toBox) {
  const pts = Array.isArray(points) ? points : [];
  const fw = Math.max(1e-6, Number(fromBox?.w) || 1e-6);
  const fh = Math.max(1e-6, Number(fromBox?.h) || 1e-6);
  const ox = Number(fromBox?.x) || 0;
  const oy = Number(fromBox?.y) || 0;
  const nx = Number(toBox?.x) || 0;
  const ny = Number(toBox?.y) || 0;
  const nw = Math.max(0.008, Number(toBox?.w) || 0.008);
  const nh = Math.max(0.008, Number(toBox?.h) || 0.008);
  const sx = nw / fw;
  const sy = nh / fh;
  return pts.map((p) => ({
    ...p,
    x: nx + ((Number(p.x) || 0) - ox) * sx,
    y: ny + ((Number(p.y) || 0) - oy) * sy,
  }));
}

/**
 * Redimensiona marca con bbox según handle.
 * edge: n|s|e|w|ne|nw|se|sw
 * Paths cerrados (cabecera/pie): escala vértices al nuevo AABB.
 */
export function resizeMarkBox(
  mark,
  edge,
  currX,
  currY,
  orig = null,
  { allowOutside = false } = {},
) {
  if (!markHasBox(mark)) return mark;
  const o = orig || mark;
  const fromBounds =
    o.type === "path" || o.type === "textpath"
      ? markBounds(o)
      : {
          x: Number(o.x) || 0,
          y: Number(o.y) || 0,
          w: Math.max(0.001, Number(o.w) || 0),
          h: Math.max(0.001, Number(o.h) || 0),
        };
  if (!fromBounds) return mark;
  let x = fromBounds.x;
  let y = fromBounds.y;
  let w = fromBounds.w;
  let h = fromBounds.h;
  const right = x + w;
  const bottom = y + h;
  let cx = Number(currX);
  let cy = Number(currY);
  if (!Number.isFinite(cx)) cx = right;
  if (!Number.isFinite(cy)) cy = bottom;
  if (!allowOutside) {
    cx = Math.min(1, Math.max(0, cx));
    cy = Math.min(1, Math.max(0, cy));
  }
  const e = String(edge || "se");

  if (e.includes("w")) {
    x = Math.min(cx, right - 0.008);
    w = right - x;
  } else if (e.includes("e")) {
    w = Math.max(0.008, cx - x);
  }
  if (e.includes("n")) {
    y = Math.min(cy, bottom - 0.008);
    h = bottom - y;
  } else if (e.includes("s")) {
    h = Math.max(0.008, cy - y);
  }

  const opts = { allowOutside };
  if (mark.type === "path" || mark.type === "textpath") {
    const pts = scalePathPointsToBox(o.points || mark.points, fromBounds, {
      x,
      y,
      w,
      h,
    });
    return clampMark(
      {
        ...mark,
        // Con allowOutside no reclavar el path al [0,1]
        points: mark.closed && !allowOutside ? fitClosedPathInPage(pts) : pts,
      },
      opts,
    );
  }

  return clampMark({ ...mark, x, y, w, h }, opts);
}

/**
 * Desplaza una marca (path/line/arrow/rect/ellipse/text) en coords normalizadas.
 * `allowOutside`: no reclavar al [0,1] (arrastre fuera de la mesa).
 */
export function translateMark(mark, dx, dy, { allowOutside = false } = {}) {
  if (!mark || (!dx && !dy)) return mark;
  const ox = Number(dx) || 0;
  const oy = Number(dy) || 0;
  const opts = { allowOutside };
  if (mark.type === "path" || mark.type === "textpath") {
    const moved = (mark.points || []).map((p) => ({
      x: (Number(p.x) || 0) + ox,
      y: (Number(p.y) || 0) + oy,
    }));
    return clampMark(
      {
        ...mark,
        points: moved,
      },
      opts,
    );
  }
  if (
    mark.type === "line" ||
    mark.type === "arrow" ||
    mark.type === "dimension"
  ) {
    return clampMark(
      {
        ...mark,
        x1: (Number(mark.x1) || 0) + ox,
        y1: (Number(mark.y1) || 0) + oy,
        x2: (Number(mark.x2) || 0) + ox,
        y2: (Number(mark.y2) || 0) + oy,
      },
      opts,
    );
  }
  if (
    mark.type === "ellipse" ||
    mark.type === "arc" ||
    mark.type === "rect" ||
    mark.type === "roundrect" ||
    mark.type === "polygon" ||
    mark.type === "star" ||
    mark.type === "grid" ||
    mark.type === "polarGrid" ||
    mark.type === "flare"
  ) {
    return clampMark(
      {
        ...mark,
        x: (Number(mark.x) || 0) + ox,
        y: (Number(mark.y) || 0) + oy,
        w: mark.w,
        h: mark.h,
      },
      opts,
    );
  }
  if (mark.type === "text") {
    return clampMark(
      {
        ...mark,
        x: (Number(mark.x) || 0) + ox,
        y: (Number(mark.y) || 0) + oy,
        w: mark.w,
        h: mark.h,
      },
      opts,
    );
  }
  return mark;
}

/** Como clampMark pero permite coords fuera de la mesa. */
export function clampMarkAllowOutside(raw = {}) {
  return clampMark(raw, { allowOutside: true });
}

/**
 * ¿La marca está (casi) fuera de la mesa? Misma heurística que casillas al eject.
 */
export function markIsOffPage(mark) {
  const b = markBounds(mark);
  if (!b) return false;
  const cx = b.cx;
  const cy = b.cy;
  const centerOutside = cx < 0 || cx > 1 || cy < 0 || cy > 1;
  const mostlyOutside =
    b.x + b.w < 0.08 || b.x > 0.92 || b.y + b.h < 0.08 || b.y > 0.92;
  return centerOutside || mostlyOutside;
}

/**
 * Convierte una marca de mesa (0–1) a float de pasteboard (board units),
 * centrado donde estaba en el artboard.
 */
export function markToPasteboardItem(mark, pageBoard, { id, boardPoint } = {}) {
  if (!mark) return null;
  const b = markBounds(mark);
  if (!b) return null;
  const board = pageBoard || {
    x: 0,
    y: 0,
    w: 1,
    h: 1,
  };
  const bw = Math.max(0.01, Number(board.w) || 1);
  const bh = Math.max(0.01, Number(board.h) || 1);
  const sizeW = Math.max(0.02, b.w * bw);
  const sizeH = Math.max(0.02, b.h * bh);
  let cx = Number(board.x) + b.cx * bw;
  let cy = Number(board.y) + b.cy * bh;
  if (
    boardPoint &&
    Number.isFinite(Number(boardPoint.x)) &&
    Number.isFinite(Number(boardPoint.y))
  ) {
    cx = Number(boardPoint.x);
    cy = Number(boardPoint.y);
  }
  // Re-origen: coords de la marca relativas a su bbox → se reponen al volver a mesa
  const localMark = translateMark(mark, -b.x, -b.y, { allowOutside: true });
  return {
    id: id || `pbm_${String(mark.id || "m")}_${Date.now().toString(36)}`,
    kind: "mark",
    mark: clampMarkAllowOutside({ ...localMark, id: mark.id }),
    x: cx - sizeW / 2,
    y: cy - sizeH / 2,
    w: sizeW,
    h: sizeH,
    pageW: b.w,
    pageH: b.h,
  };
}

/** Ancho de caja de texto (0–1). Siempre > 0 tras clampMark. */
export function textMarkBoxWidth(mark) {
  const w = Number(mark?.w);
  if (Number.isFinite(w) && w > 0) return Math.min(0.95, Math.max(0.05, w));
  return 0.22;
}

/** Alto de caja de texto (artboard 0–1). Usa h guardado o estima por líneas × fontSize. */
export function textMarkBoxHeight(mark) {
  const h = Number(mark?.h);
  if (Number.isFinite(h) && h > 0.015) return Math.min(0.95, Math.max(0.02, h));
  const fs = Math.max(8, Number(mark?.fontSize) || 14);
  const lines = Math.max(
    1,
    String(mark?.text ?? mark?.value ?? "")
      .split(/\r?\n/)
      .filter((l, i, a) => l.length || i < a.length - 1).length || 1,
  );
  // Misma escala aprox. que el render (font ~0.0028 * pt por línea)
  return Math.min(0.9, Math.max(0.028, (fs / 14) * 0.032 * lines * 1.15));
}

/**
 * Estrecha / ensancha la caja de texto (coords normalizadas).
 * edge "e" = borde derecho; "w" = borde izquierdo (mueve x y ajusta w).
 */
export function resizeTextMarkWidth(
  mark,
  { edge = "e", startX, currX, origX, origW } = {},
) {
  if (!mark || mark.type !== "text") return mark;
  const ox = Number.isFinite(Number(origX))
    ? Number(origX)
    : Number(mark.x) || 0;
  const ow = Number.isFinite(Number(origW))
    ? Number(origW)
    : textMarkBoxWidth(mark);
  const dx = (Number(currX) || 0) - (Number(startX) || 0);
  if (edge === "w") {
    let x = ox + dx;
    let w = ow - dx;
    if (w < 0.05) {
      x = ox + ow - 0.05;
      w = 0.05;
    }
    x = Math.min(Math.max(0, x), 0.95);
    w = Math.min(Math.max(0.05, w), 1 - x);
    return clampMark({ ...mark, x, w });
  }
  let w = ow + dx;
  w = Math.min(Math.max(0.05, w), 1 - ox);
  return clampMark({ ...mark, x: ox, w });
}

/**
 * Escala el fontSize de un texto arrastrando (px de pantalla → pt).
 * axis: "both" (esquina), "y" (arriba/abajo), "x" (lado).
 * edge "n": subir = más grande; "s": bajar = más grande.
 */
export function resizeTextMarkFontSize(
  mark,
  {
    startClientX,
    startClientY,
    clientX,
    clientY,
    originFontSize,
    axis = "both",
    edge = "se",
  } = {},
) {
  if (!mark || mark.type !== "text") return mark;
  const base = Number(originFontSize);
  const fs0 = Number.isFinite(base) ? base : Number(mark.fontSize) || 14;
  const dx = (Number(clientX) || 0) - (Number(startClientX) || 0);
  const dy = (Number(clientY) || 0) - (Number(startClientY) || 0);
  let delta;
  if (axis === "y") {
    // n: arrastrar hacia arriba agranda; s: hacia abajo agranda
    delta = (edge === "n" ? -dy : dy) / 2.5;
  } else if (axis === "x") {
    delta = dx / 2.5;
  } else {
    // ~3 px de diagonal ≈ 1 pt
    delta = (dx + dy) / 3;
  }
  const next = Math.round(fs0 + delta);
  return clampMark({ ...mark, fontSize: next });
}

const MARKS_SOFT_CAP = 200;

/** Roles de cabecera/pie — no tirar al truncar marks. */
function isChromeRoleMark(m) {
  const role = m?.role != null ? String(m.role) : "";
  return (
    role === "pageTitleBar" ||
    role === "pageTitleText" ||
    role === "pageTitleTextSub" ||
    role === "pageStudioBar" ||
    role === "pageStudioText" ||
    role === "pageStudioTextSub"
  );
}

/** Por defecto allowOutside: el autosave no debe reclavar marcas al [0,1]. */
export function normalizeMarks(raw, { allowOutside = true } = {}) {
  if (!Array.isArray(raw)) return [];
  const list = raw
    .map((m) => {
      try {
        return clampMark(m, { allowOutside });
      } catch {
        return null;
      }
    })
    .filter(Boolean);
  if (list.length <= MARKS_SOFT_CAP) return list;
  // Priorizar cabecera/pie: si van al final, slice(0,200) las borraba.
  const chrome = [];
  const rest = [];
  for (const m of list) {
    if (isChromeRoleMark(m)) chrome.push(m);
    else rest.push(m);
  }
  return [...chrome, ...rest].slice(0, MARKS_SOFT_CAP);
}

/** Tipos de marca que pueden ser máscara de recorte (VC clipping path). */
export const CLIP_MASK_MARK_TYPES = new Set([
  "rect",
  "roundrect",
  "ellipse",
  "polygon",
  "star",
  "path",
]);

/**
 * Normaliza clip de casilla.
 * Preferible space:"local" (0–1 del marco): no se dispara al mover/sacar la foto.
 * Legacy sin space = coords de página (se migra en ensureLocalClip).
 * null = sin máscara de recorte.
 */
/** Snapshot de pintura de la marca → Release restaura aspecto, no solo geometría. */
export function clipPaintFromMark(mark) {
  if (!mark || typeof mark !== "object") return null;
  const out = {};
  if (mark.id != null) out.id = String(mark.id);
  if (mark.fill != null) out.fill = mark.fill;
  if (mark.color != null) out.color = mark.color;
  if (mark.strokeColor != null) out.strokeColor = mark.strokeColor;
  if (mark.stroke != null) out.stroke = mark.stroke;
  if (mark.closed) out.closed = true;
  if (mark.opacity != null) out.opacity = mark.opacity;
  if (mark.gradient) out.gradient = mark.gradient;
  if (mark.role) out.role = mark.role;
  return Object.keys(out).length ? out : null;
}

function attachClipPaint(out, raw) {
  if (!out) return out;
  const from =
    raw?.from && typeof raw.from === "object"
      ? clipPaintFromMark(raw.from)
      : null;
  return from ? { ...out, from } : out;
}

export function normalizeSlotClip(raw) {
  if (raw == null || typeof raw !== "object") return null;
  const typeRaw = String(raw.type || "").toLowerCase();
  const type = MARK_TYPE_BY_LOWER[typeRaw] || typeRaw;
  if (!CLIP_MASK_MARK_TYPES.has(type)) return null;
  const rotation = normalizeRotation(raw.rotation);
  const local = String(raw.space || "").toLowerCase() === "local";
  const finite = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  if (type === "path") {
    const pts = Array.isArray(raw.points)
      ? raw.points
          .map((p) => ({
            x: finite(p?.x),
            y: finite(p?.y),
          }))
          .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
      : [];
    if (!pts.length) return null;
    return attachClipPaint(
      {
        type: "path",
        points: pts,
        ...(local ? { space: "local" } : null),
        ...(rotation ? { rotation } : null),
      },
      raw,
    );
  }
  let x = finite(raw.x);
  let y = finite(raw.y);
  let w = finite(raw.w);
  let h = finite(raw.h);
  if (w < 0) {
    x += w;
    w = -w;
  }
  if (h < 0) {
    y += h;
    h = -h;
  }
  w = Math.max(w, 1e-6);
  h = Math.max(h, 1e-6);
  const out = {
    type,
    x,
    y,
    w,
    h,
    ...(local ? { space: "local" } : null),
    ...(rotation ? { rotation } : null),
  };
  if (type === "roundrect") {
    const maxR = Math.min(w, h) / 2;
    let rx = Number(raw.rx);
    if (!Number.isFinite(rx)) rx = Math.min(0.04, maxR || 0.04);
    out.rx = Math.min(Math.max(0, rx), maxR || 0);
  }
  if (type === "polygon") {
    out.sides = Math.max(3, Math.min(24, Math.round(Number(raw.sides) || 6)));
  }
  if (type === "star") {
    out.tips = Math.max(
      3,
      Math.min(16, Math.round(Number(raw.tips ?? raw.points) || 5)),
    );
    out.innerRatio = Math.min(
      0.9,
      Math.max(0.15, Number(raw.innerRatio) || 0.45),
    );
  }
  return attachClipPaint(out, raw);
}

/** Clave de geometría (ignora `from`) para detectar edición de máscara. */
export function slotClipGeomKey(clip) {
  const n = normalizeSlotClip(clip);
  if (!n) return "";
  if (n.type === "path") {
    return `path:${(n.points || [])
      .map((p) => `${Number(p.x).toFixed(4)},${Number(p.y).toFixed(4)}`)
      .join(";")}`;
  }
  return [
    n.type,
    Number(n.x).toFixed(4),
    Number(n.y).toFixed(4),
    Number(n.w).toFixed(4),
    Number(n.h).toFixed(4),
    Number(n.rx || 0).toFixed(4),
    n.sides || "",
    n.tips || "",
  ].join(":");
}

/** Página → local (objectBoundingBox del slot). */
export function pageClipToLocal(clip, frame) {
  const norm = normalizeSlotClip(clip);
  if (!norm) return null;
  if (norm.space === "local") return norm;
  const sx = Number(frame?.x) || 0;
  const sy = Number(frame?.y) || 0;
  const sw = Math.max(Number(frame?.w) || 0.001, 0.001);
  const sh = Math.max(Number(frame?.h) || 0.001, 0.001);
  const map = (x, y) => ({
    x: ((Number(x) || 0) - sx) / sw,
    y: ((Number(y) || 0) - sy) / sh,
  });
  if (norm.type === "path" && Array.isArray(norm.points)) {
    return normalizeSlotClip({
      ...norm,
      space: "local",
      points: norm.points.map((p) => map(p.x, p.y)),
    });
  }
  const o = map(norm.x, norm.y);
  return normalizeSlotClip({
    ...norm,
    space: "local",
    x: o.x,
    y: o.y,
    w: (Number(norm.w) || 0) / sw,
    h: (Number(norm.h) || 0) / sh,
    ...(norm.rx != null ? { rx: Number(norm.rx) / sw } : null),
  });
}

/** Local → página (para Release clipping mask). */
export function localClipToPage(clip, frame) {
  const norm = normalizeSlotClip(clip);
  if (!norm) return null;
  if (norm.space !== "local") return norm;
  const sx = Number(frame?.x) || 0;
  const sy = Number(frame?.y) || 0;
  const sw = Math.max(Number(frame?.w) || 0.001, 0.001);
  const sh = Math.max(Number(frame?.h) || 0.001, 0.001);
  const map = (x, y) => ({
    x: sx + (Number(x) || 0) * sw,
    y: sy + (Number(y) || 0) * sh,
  });
  if (norm.type === "path" && Array.isArray(norm.points)) {
    const { space: _s, ...rest } = norm;
    return normalizeSlotClip({
      ...rest,
      points: norm.points.map((p) => map(p.x, p.y)),
    });
  }
  const o = map(norm.x, norm.y);
  const { space: _s, ...rest } = norm;
  return normalizeSlotClip({
    ...rest,
    x: o.x,
    y: o.y,
    w: (Number(norm.w) || 0) * sw,
    h: (Number(norm.h) || 0) * sh,
    ...(norm.rx != null ? { rx: Number(norm.rx) * sw } : null),
  });
}

/** Garantiza clip en space local respecto al marco dado. */
export function ensureLocalClip(rawClip, frame) {
  const clip = normalizeSlotClip(rawClip);
  if (!clip) return null;
  if (clip.space === "local") return clip;
  return pageClipToLocal(clip, frame);
}

export function clampSlot(raw = {}) {
  let w = Math.max(MIN_SLOT, clamp01(raw.w ?? 0.2));
  let h = Math.max(MIN_SLOT, clamp01(raw.h ?? 0.2));
  let x = clamp01(raw.x ?? 0);
  let y = clamp01(raw.y ?? 0);
  if (x + w > 1) x = Math.max(0, 1 - w);
  if (y + h > 1) y = Math.max(0, 1 - h);
  w = Math.min(w, 1 - x);
  h = Math.min(h, 1 - y);
  // Máscara de recorte = slot.clip (explícita). shape:circle legacy → clip elipse.
  const legacyCircle = String(raw.shape || "rect").toLowerCase() === "circle";
  const assetRaw = raw.assetId;
  const assetId =
    assetRaw == null || assetRaw === ""
      ? null
      : Number.isFinite(Number(assetRaw))
        ? Number(assetRaw)
        : null;
  let zoom = Number(raw.zoom);
  if (!Number.isFinite(zoom)) zoom = 1;
  zoom = Math.min(8, Math.max(0.25, zoom));
  let z = Number(raw.z);
  if (!Number.isFinite(z)) z = 0;
  z = Math.max(0, Math.min(9999, Math.round(z)));
  const frame = { x, y, w: Math.max(MIN_SLOT, w), h: Math.max(MIN_SLOT, h) };
  let clip = ensureLocalClip(raw.clip, frame);
  if (!clip && legacyCircle) {
    clip = normalizeSlotClip({
      type: "ellipse",
      x: 0,
      y: 0,
      w: 1,
      h: 1,
      space: "local",
    });
  }
  return {
    id: String(raw.id || newSlotId()),
    x,
    y,
    w: Math.max(MIN_SLOT, w),
    h: Math.max(MIN_SLOT, h),
    assetId,
    zoom: Math.round(zoom * 100) / 100,
    panX: clamp01(raw.panX ?? 0.5),
    panY: clamp01(raw.panY ?? 0.5),
    shape: "rect",
    z,
    rotation: normalizeRotation(raw.rotation),
    ...(raw.flipX ? { flipX: true } : null),
    ...(raw.flipY ? { flipY: true } : null),
    ...(clip ? { clip } : null),
  };
}

/**
 * Como clampSlot pero permite x/y fuera de [0,1] (arrastrar foto fuera de la mesa).
 * w/h y demás campos siguen normalizados.
 */
export function clampSlotAllowOutside(raw = {}) {
  const x = Number(raw.x);
  const y = Number(raw.y);
  const frameX = Number.isFinite(x) ? x : 0;
  const frameY = Number.isFinite(y) ? y : 0;
  // Localizar el clip con el marco real ANTES de clampSlot(x=0,y=0)
  const localClip = ensureLocalClip(raw.clip, {
    x: frameX,
    y: frameY,
    w: Number(raw.w) || 0.2,
    h: Number(raw.h) || 0.2,
  });
  const base = clampSlot({
    ...raw,
    x: 0,
    y: 0,
    clip: localClip,
  });
  return {
    ...base,
    x: Number.isFinite(x) ? x : 0,
    y: Number.isFinite(y) ? y : 0,
  };
}

/**
 * Reclava la casilla dentro de la mesa sin cambiar w/h (solo traslación).
 * Evita el aplastón de clampSlot al borde según el punto de agarre.
 */
export function fitSlotInPage(raw = {}) {
  let w = Number(raw.w);
  let h = Number(raw.h);
  if (!Number.isFinite(w) || w < MIN_SLOT) w = 0.2;
  if (!Number.isFinite(h) || h < MIN_SLOT) h = 0.2;
  w = Math.min(1, Math.max(MIN_SLOT, w));
  h = Math.min(1, Math.max(MIN_SLOT, h));
  let x = Number(raw.x);
  let y = Number(raw.y);
  if (!Number.isFinite(x)) x = 0;
  if (!Number.isFinite(y)) y = 0;
  if (x + w > 1) x = 1 - w;
  if (y + h > 1) y = 1 - h;
  if (x < 0) x = 0;
  if (y < 0) y = 0;
  return clampSlot({ ...raw, x, y, w, h });
}

/** Centro o mayor parte fuera de [0,1] (misma idea que eject a pasteboard). */
export function slotIsOffPage(slot) {
  const x = Number(slot?.x) || 0;
  const y = Number(slot?.y) || 0;
  const w = Number(slot?.w) || 0;
  const h = Number(slot?.h) || 0;
  const cx = x + w / 2;
  const cy = y + h / 2;
  const centerOutside = cx < 0 || cx > 1 || cy < 0 || cy > 1;
  const mostlyOutside = x + w < 0.08 || x > 0.92 || y + h < 0.08 || y > 0.92;
  return centerOutside || mostlyOutside;
}

/** Stable paint order: lower z behind; selected can still float in UI. */
export function sortSlotsByLayer(slots = []) {
  return [...(Array.isArray(slots) ? slots : [])].sort((a, b) => {
    const dz = (Number(a.z) || 0) - (Number(b.z) || 0);
    if (dz !== 0) return dz;
    return String(a.id || "").localeCompare(String(b.id || ""));
  });
}

/** Aviator preset in normalized top-left coords (matches BACK). */
export function aviatorPresetSlots(assetIds = []) {
  const leftW = LEFT_COL_RATIO;
  const restW = 1 - leftW;
  const colW = restW / 3;
  const rowH = 0.5;
  const x1 = leftW;
  const x2 = leftW + colW;
  const x3 = leftW + colW * 2;
  const rects = [
    { key: "hero", x: 0, y: 0, w: leftW, h: 1 },
    { key: "midLeft", x: x1, y: 0, w: colW, h: rowH },
    { key: "midRight", x: x1, y: rowH, w: colW, h: rowH },
    { key: "botTL", x: x2, y: 0, w: colW, h: rowH },
    { key: "botTR", x: x2, y: rowH, w: colW, h: rowH },
    { key: "botBL", x: x3, y: 0, w: colW, h: rowH },
    { key: "botBR", x: x3, y: rowH, w: colW, h: rowH },
  ];
  const ids = Array.isArray(assetIds) ? assetIds : [];
  return rects.map((r, idx) => {
    const rawId = ids[idx];
    const assetId =
      rawId == null || rawId === ""
        ? null
        : Number.isFinite(Number(rawId))
          ? Number(rawId)
          : null;
    return clampSlot({
      id: `aviator_${r.key}`,
      x: r.x,
      y: r.y,
      w: r.w,
      h: r.h,
      assetId,
    });
  });
}

/** Página en blanco: sin casillas foto (el preset Aviator es opcional). */
export function emptyFreePage() {
  return { slots: [], marks: [] };
}

/** Clona páginas regenerando ids (aplicar carátula → board nuevo). */
export function clonePagesWithFreshIds(pages, pasteboard) {
  const list = Array.isArray(pages) ? pages : [];
  const nextPages = list.map((page) => ({
    ...page,
    slots: (Array.isArray(page?.slots) ? page.slots : []).map((s) => ({
      ...s,
      id: newSlotId("tpl"),
    })),
    marks: (Array.isArray(page?.marks) ? page.marks : []).map((m) => ({
      ...m,
      id: newMarkId("tpl"),
    })),
    ...(page?.board && typeof page.board === "object"
      ? { board: { ...page.board } }
      : null),
  }));
  if (pasteboard == null) return { pages: nextPages };
  return {
    pages: nextPages,
    pasteboard: (Array.isArray(pasteboard) ? pasteboard : []).map((p) => ({
      ...p,
      id: newSlotId("tplpb"),
    })),
  };
}

/**
 * Página al pulsar "+": si el board ya trae plantilla (carátula / chrome en
 * página 0), clona esa estructura con ids frescos y sin fotos. Si no, siembra
 * el chrome Stage clásico.
 */
export function blankPageFromBoardTemplate(templatePage, seed = null) {
  const src =
    templatePage && typeof templatePage === "object" ? templatePage : null;
  const marks = Array.isArray(src?.marks) ? src.marks : [];
  const slots = Array.isArray(src?.slots) ? src.slots : [];
  const hasTemplate = marks.length > 0 || slots.length > 0 || Boolean(src?.hf);
  if (!hasTemplate) {
    return seedPageHeaderFooterMarks(emptyFreePage(), seed);
  }
  const cleared = {
    ...src,
    slots: slots.map((s) => ({
      ...s,
      assetId: null,
    })),
    marks: marks.map((m) => ({ ...m })),
    hf: 1,
  };
  const { pages } = clonePagesWithFreshIds([cleared]);
  const page = pages[0] || emptyFreePage();
  return { ...page, hf: 1 };
}

export { assignDefaultBoards, normalizePageBoard };

function isLegacySlotsObject(slots) {
  if (!slots || typeof slots !== "object" || Array.isArray(slots)) return false;
  return SLOT_KEYS.some((k) => Object.prototype.hasOwnProperty.call(slots, k));
}

export function migrateLegacyPage(page) {
  const src = page?.slots && typeof page.slots === "object" ? page.slots : {};
  const assetIds = SLOT_KEYS.map((k) => {
    const v = src[k];
    if (v == null || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  });
  const out = { slots: aviatorPresetSlots(assetIds), marks: [] };
  if (page?.board != null) out.board = normalizePageBoard(page.board);
  return out;
}

function withPreservedBoard(page, next) {
  if (page?.board == null && next?.board == null) return next;
  const board = normalizePageBoard(page?.board ?? next?.board);
  return { ...next, board };
}

export function normalizeFreePage(page, { allowOutside = false } = {}) {
  if (isLegacySlotsObject(page?.slots)) return migrateLegacyPage(page);
  const list = Array.isArray(page?.slots) ? page.slots : [];
  // Marcas: siempre pueden vivir fuera de la mesa (si no, el autosave las reclava).
  // Casillas: allowOutside solo durante arrastre.
  const marks = normalizeMarks(page?.marks, { allowOutside: true });
  const clampFn = allowOutside ? clampSlotAllowOutside : clampSlot;
  // Permitir página solo con marcas / sin casillas (antes regeneraba Aviator vacío)
  const next = {
    slots: list.map((s) => clampFn(s)),
    marks,
  };
  const symbols = normalizeSymbols(page?.symbols);
  if (symbols.length) next.symbols = symbols;
  if (page?.perspectiveGrid && typeof page.perspectiveGrid === "object") {
    // Lazy import avoided: shape validated lightly here
    const pg = page.perspectiveGrid;
    next.perspectiveGrid = {
      mode:
        String(pg.mode || "").toLowerCase() === "1point" ? "1point" : "2point",
      visible: pg.visible !== false,
      origin: {
        x: Number(pg.origin?.x) || 0.5,
        y: Number(pg.origin?.y) || 0.78,
      },
      vpLeft: {
        x: Number(pg.vpLeft?.x) || 0.06,
        y: Number(pg.vpLeft?.y) || 0.36,
      },
      vpRight: {
        x: Number(pg.vpRight?.x) || 0.94,
        y: Number(pg.vpRight?.y) || 0.36,
      },
      cells: Math.max(2, Math.min(16, Math.round(Number(pg.cells) || 6))),
      plane: ["left", "right", "ground", "none"].includes(
        String(pg.plane || "").toLowerCase(),
      )
        ? String(pg.plane).toLowerCase()
        : "ground",
    };
  }
  if (page?.board != null) next.board = normalizePageBoard(page.board);
  // Cabecera/pie migrados → nunca conservar chrome (si no, el autosave lo
  // reinyecta y migrate vuelve a crear barras/textos).
  if (page?.hf || pageHasChromeMarks(marks)) {
    next.hf = 1;
  } else if (page?.chrome != null) {
    next.chrome = normalizePageChrome(page.chrome);
  }
  return next;
}

export function normalizePages(raw) {
  if (!Array.isArray(raw) || !raw.length) {
    return assignDefaultBoards([emptyFreePage()]);
  }
  return assignDefaultBoards(raw.map((p) => normalizeFreePage(p)));
}

export function applyAviatorPresetToPage(page) {
  const current = normalizeFreePage(page);
  const assetIds = current.slots.map((s) => s.assetId);
  return withPreservedBoard(page, {
    slots: aviatorPresetSlots(assetIds),
    marks: current.marks || [],
    ...(current.chrome ? { chrome: current.chrome } : null),
  });
}

export function listPageAssetIds(page) {
  return normalizeFreePage(page)
    .slots.map((s) => s.assetId)
    .filter((id) => id != null)
    .map(Number);
}

export function snap(v, step = 0.02) {
  return Math.round(v / step) * step;
}

export function snapSlot(slot, step = 0.02) {
  return clampSlot({
    ...slot,
    x: snap(slot.x, step),
    y: snap(slot.y, step),
    w: snap(slot.w, step),
    h: snap(slot.h, step),
  });
}

/** Same crop math as BACK PDF — box in any unit (e.g. 0–100%). */
export function coverFit(imgW, imgH, box, crop = {}) {
  const iw = Math.max(1, Number(imgW) || 1);
  const ih = Math.max(1, Number(imgH) || 1);
  let zoom = Number(crop.zoom);
  if (!Number.isFinite(zoom)) zoom = 1;
  zoom = Math.min(8, Math.max(0.25, zoom));
  const panX = clamp01(crop.panX ?? 0.5);
  const panY = clamp01(crop.panY ?? 0.5);
  const scale = Math.max(box.w / iw, box.h / ih) * zoom;
  const w = iw * scale;
  const h = ih * scale;
  return {
    x: box.x + (box.w - w) * panX,
    y: box.y + (box.h - h) * panY,
    w,
    h,
  };
}

/**
 * Tras partir una casilla con Knife: mismo zoom, pan para que la foto
 * quede anclada en la misma posición absoluta (sin deformar / zoom raro).
 * `axis` = "x" (corte vertical → izq/der) | "y" (corte horizontal).
 */
export function cropKeepImageFixed(
  origBox,
  newBox,
  crop = {},
  axis = "x",
  imgW = 1000,
  imgH = 1000,
) {
  const zoom = Math.min(8, Math.max(0.25, Number(crop.zoom) || 1));
  const panX0 = clamp01(crop.panX ?? 0.5);
  const panY0 = clamp01(crop.panY ?? 0.5);
  const iw = Math.max(1, Number(imgW) || 1000);
  const ih = Math.max(1, Number(imgH) || 1000);
  const ox = Number(origBox?.x) || 0;
  const oy = Number(origBox?.y) || 0;
  const ow = Math.max(0.001, Number(origBox?.w) || 0.001);
  const oh = Math.max(0.001, Number(origBox?.h) || 0.001);
  const nx = Number(newBox?.x) || 0;
  const ny = Number(newBox?.y) || 0;
  const nw = Math.max(0.001, Number(newBox?.w) || 0.001);
  const nh = Math.max(0.001, Number(newBox?.h) || 0.001);
  const origFit = coverFit(
    iw,
    ih,
    { x: ox, y: oy, w: ow, h: oh },
    { zoom, panX: panX0, panY: panY0 },
  );
  const scaleN = Math.max(nw / iw, nh / ih) * zoom;
  const Ww = iw * scaleN;
  const Hh = ih * scaleN;
  let panX = panX0;
  let panY = panY0;
  const ax = String(axis || "x").toLowerCase();
  if (ax === "x") {
    const den = nw - Ww;
    if (Math.abs(den) > 1e-8) {
      panX = clamp01((origFit.x - nx) / den);
    }
  } else {
    const den = nh - Hh;
    if (Math.abs(den) > 1e-8) {
      panY = clamp01((origFit.y - ny) / den);
    }
  }
  return { zoom, panX, panY };
}

/** Tamaño del recuadro que ocupa la foto tras rotarla (CSS, sentido horario). */
export function rotatedCoverExtent(imgW, imgH, rotationDeg = 0) {
  const iw = Math.max(1, Number(imgW) || 1);
  const ih = Math.max(1, Number(imgH) || 1);
  const rot = Math.abs(normalizeRotation(rotationDeg));
  const rad = (rot * Math.PI) / 180;
  const c = Math.abs(Math.cos(rad));
  const s = Math.abs(Math.sin(rad));
  return {
    iw,
    ih,
    extentW: iw * c + ih * s,
    extentH: iw * s + ih * c,
  };
}

/**
 * Coloca la foto (sin girar) dentro del hueco.
 * El CSS rotate va en el centro de este rectángulo; el recorte del hueco sigue recto.
 */
export function rotatedImageLayout(
  imgW,
  imgH,
  box,
  crop = {},
  rotationDeg = 0,
) {
  const { iw, ih, extentW, extentH } = rotatedCoverExtent(
    imgW,
    imgH,
    rotationDeg,
  );
  const aabb = coverFit(extentW, extentH, box, crop);
  const scale = aabb.w / extentW;
  const dw = iw * scale;
  const dh = ih * scale;
  const cx = aabb.x + aabb.w / 2;
  const cy = aabb.y + aabb.h / 2;
  return {
    x: cx - dw / 2,
    y: cy - dh / 2,
    w: dw,
    h: dh,
  };
}

/** Arrastre en píxeles del hueco → panX/panY. El hueco no está girado. */
export function panAfterDrag(imgW, imgH, box, crop, rotationDeg, dx, dy) {
  const { extentW, extentH } = rotatedCoverExtent(imgW, imgH, rotationDeg);
  const fitted0 = coverFit(extentW, extentH, box, crop);
  const denomW = box.w - fitted0.w;
  const denomH = box.h - fitted0.h;
  const panX =
    Math.abs(denomW) < 0.0001
      ? 0.5
      : clamp01((fitted0.x + (Number(dx) || 0) - box.x) / denomW);
  const panY =
    Math.abs(denomH) < 0.0001
      ? 0.5
      : clamp01((fitted0.y + (Number(dy) || 0) - box.y) / denomH);
  return { panX, panY };
}

export function splitCategoryLabel(raw) {
  const cat = String(raw || "Moodboard").trim();
  const dash = cat.search(/\s[-–—]\s/);
  if (dash < 0) return { main: cat, sub: "" };
  return {
    main: cat.slice(0, dash).trim(),
    sub: cat
      .slice(dash)
      .replace(/^\s*[-–—]\s*/, "")
      .trim(),
  };
}

export function splitStudioLine(raw) {
  const full = String(
    raw ||
      "Stage Design Studio · Irene Guijt | irene@stagedesign.es | +34 667 765 007",
  ).trim();
  const sep = full.search(/\s*[·|]\s*/);
  if (sep < 0) {
    return { studioName: full || "Stage Design Studio", contactLine: "" };
  }
  return {
    studioName: full.slice(0, sep).trim() || "Stage Design Studio",
    contactLine: full
      .slice(sep)
      .replace(/^\s*[·|]\s*/, "")
      .trim(),
  };
}

export const DEFAULT_CHROME_STYLE = {
  title: {
    barScale: 1,
    fontScale: 1,
    bg: "#383838",
    text: "#ffffff",
    subText: "#c7c7c7",
    /** top-left normalizado 0–1 sobre el lienzo; null = esquina por defecto */
    x: null,
    y: null,
    /** diagonal | rect | ribbon | tag */
    shape: "diagonal",
  },
  studio: {
    barScale: 1,
    fontScale: 1,
    bg: "#383838",
    name: "#8cd4e0",
    contact: "#ffffff",
    x: null,
    y: null,
    shape: "diagonal",
  },
};

export const CHROME_SHAPES = [
  { id: "diagonal", label: "Diagonal" },
  { id: "rect", label: "Rectángulo" },
  { id: "ribbon", label: "Cinta" },
  { id: "tag", label: "Etiqueta" },
];

export function normalizeChromeShape(raw) {
  const s = String(raw || "")
    .trim()
    .toLowerCase();
  if (s === "rect" || s === "rectangle" || s === "square") return "rect";
  if (s === "ribbon" || s === "banner" || s === "tape") return "ribbon";
  if (s === "tag" || s === "arrow" || s === "flag") return "tag";
  return "diagonal";
}

/**
 * clip-path CSS para barra título (which=title) o pie (studio).
 * slant: longitud CSS del chaflán (p.ej. "2.1cqw").
 */
export function chromeBarClipPath(shape, which, slant) {
  const s = normalizeChromeShape(shape);
  const side = which === "studio" ? "studio" : "title";
  if (s === "rect") return "none";
  if (s === "ribbon") {
    return `polygon(${slant} 0, 100% 0, calc(100% - ${slant}) 100%, 0 100%)`;
  }
  if (s === "tag") {
    if (side === "studio") {
      return `polygon(0 0, calc(100% - ${slant}) 0, 100% 50%, calc(100% - ${slant}) 100%, 0 100%)`;
    }
    return `polygon(${slant} 0, 100% 0, 100% 100%, ${slant} 100%, 0 50%)`;
  }
  // diagonal Stage
  if (side === "studio") {
    return `polygon(0 0, calc(100% - ${slant}) 0, 100% 100%, 0 100%)`;
  }
  return `polygon(0 0, 100% 0, 100% 100%, ${slant} 100%)`;
}

function clampChromeScale(v, fallback = 1) {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(1.6, Math.max(0.7, n));
}

/** Letra de cabecera/pie: rango amplio (25%–500%). */
export const CHROME_FONT_SCALE_MIN = 0.25;
export const CHROME_FONT_SCALE_MAX = 5;

function clampChromeFontScale(v, fallback = 1) {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(CHROME_FONT_SCALE_MAX, Math.max(CHROME_FONT_SCALE_MIN, n));
}

function clampChromePos(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.min(0.95, Math.max(0, n));
}

function normalizeHexColor(raw, fallback) {
  const s = String(raw || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(s)) return s.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(s)) {
    return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`.toLowerCase();
  }
  return fallback;
}

export function normalizeChromeStyle(raw) {
  let obj = raw;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t) obj = null;
    else {
      try {
        obj = JSON.parse(t);
      } catch {
        obj = null;
      }
    }
  }
  const titleIn = obj?.title && typeof obj.title === "object" ? obj.title : {};
  const studioIn =
    obj?.studio && typeof obj.studio === "object" ? obj.studio : {};
  return {
    title: {
      barScale: clampChromeScale(
        titleIn.barScale ?? titleIn.scale,
        DEFAULT_CHROME_STYLE.title.barScale,
      ),
      fontScale: clampChromeFontScale(
        titleIn.fontScale,
        DEFAULT_CHROME_STYLE.title.fontScale,
      ),
      bg: normalizeHexColor(titleIn.bg, DEFAULT_CHROME_STYLE.title.bg),
      text: normalizeHexColor(titleIn.text, DEFAULT_CHROME_STYLE.title.text),
      subText: normalizeHexColor(
        titleIn.subText,
        DEFAULT_CHROME_STYLE.title.subText,
      ),
      x: clampChromePos(titleIn.x),
      y: clampChromePos(titleIn.y),
      shape: normalizeChromeShape(
        titleIn.shape ?? DEFAULT_CHROME_STYLE.title.shape,
      ),
    },
    studio: {
      barScale: clampChromeScale(
        studioIn.barScale ?? studioIn.scale,
        DEFAULT_CHROME_STYLE.studio.barScale,
      ),
      fontScale: clampChromeFontScale(
        studioIn.fontScale,
        DEFAULT_CHROME_STYLE.studio.fontScale,
      ),
      bg: normalizeHexColor(studioIn.bg, DEFAULT_CHROME_STYLE.studio.bg),
      name: normalizeHexColor(studioIn.name, DEFAULT_CHROME_STYLE.studio.name),
      contact: normalizeHexColor(
        studioIn.contact,
        DEFAULT_CHROME_STYLE.studio.contact,
      ),
      x: clampChromePos(studioIn.x),
      y: clampChromePos(studioIn.y),
      shape: normalizeChromeShape(
        studioIn.shape ?? DEFAULT_CHROME_STYLE.studio.shape,
      ),
    },
  };
}

/** Ancho/alto mínimo de caja chrome (coords 0–1). Muy bajo: la barra puede ser casi una línea. */
export const CHROME_BOX_MIN_W = 0.04;
export const CHROME_BOX_MIN_H = 0.004;

function clampRange(n, lo, hi) {
  const v = Number(n);
  if (!Number.isFinite(v)) return lo;
  return Math.min(hi, Math.max(lo, v));
}

function chromeWidthFromBarScale(barScale, which) {
  const bs = clampChromeScale(barScale, 1);
  const factor = Math.min(1.35, Math.max(0.7, 0.85 + 0.15 * bs));
  if (which === "studio") return Math.min(0.55, 0.4 * factor);
  return Math.min(0.52, 0.36 * factor);
}

function chromeHeightFromBarScale(barScale, which) {
  const bs = clampChromeScale(barScale, 1);
  return which === "studio" ? 0.065 * bs + 0.035 : 0.04 * bs + 0.025;
}

/**
 * Caja chrome por página (x/y/w/h 0–1). Independiente entre mesas.
 * Legacy board ChromeStyleJson (x,y,barScale) se migra aquí.
 */
export function defaultChromeBox(which = "title", opts = {}) {
  const style = normalizeChromeStyle(opts.chromeStyle || null);
  const side = which === "studio" ? "studio" : "title";
  const s = style[side];
  const w = chromeWidthFromBarScale(s.barScale, side);
  const h = chromeHeightFromBarScale(s.barScale, side);
  let x;
  let y;
  if (s.x != null && s.y != null) {
    x = clampRange(s.x, 0, 1 - CHROME_BOX_MIN_W);
    y = clampRange(s.y, 0, 1 - CHROME_BOX_MIN_H);
  } else if (side === "studio") {
    x = 0;
    y = 1 - h;
  } else {
    x = 1 - w;
    y = 0;
  }
  // Texto suelto: coords absolutas en página (no van dentro del clip de la barra).
  const textX = clampRange(
    x + (side === "title" ? w * 0.08 : w * 0.06),
    0,
    0.98,
  );
  const textY = clampRange(y + h * 0.18, 0, 0.98);
  if (side === "title") {
    return {
      x,
      y,
      w,
      h,
      text: String(opts.text ?? opts.categoryLabel ?? ""),
      textX,
      textY,
      shape: s.shape,
      bg: s.bg,
      textColor: s.text,
      subTextColor: s.subText,
      fontScale: s.fontScale,
    };
  }
  return {
    x,
    y,
    w,
    h,
    text: String(opts.text ?? opts.studioLine ?? ""),
    textX,
    textY,
    shape: s.shape,
    bg: s.bg,
    nameColor: s.name,
    contactColor: s.contact,
    fontScale: s.fontScale,
  };
}

export function clampChromeBox(box, which = "title") {
  const side = which === "studio" ? "studio" : "title";
  const base = defaultChromeBox(side);
  const raw = box && typeof box === "object" ? box : {};
  let w = clampRange(raw.w ?? base.w, CHROME_BOX_MIN_W, 1);
  let h = clampRange(raw.h ?? base.h, CHROME_BOX_MIN_H, 0.5);
  let x = clampRange(raw.x ?? base.x, 0, 1 - w);
  let y = clampRange(raw.y ?? base.y, 0, 1 - h);
  const fontScale = clampChromeFontScale(raw.fontScale, base.fontScale);
  const shape = normalizeChromeShape(raw.shape ?? base.shape);
  const bg = normalizeHexColor(raw.bg, base.bg);
  const text = String(raw.text ?? base.text ?? "");
  const defaultTx = x + (side === "title" ? w * 0.08 : w * 0.06);
  const defaultTy = y + h * 0.18;
  const textX = clampRange(raw.textX != null ? raw.textX : defaultTx, 0, 0.98);
  const textY = clampRange(raw.textY != null ? raw.textY : defaultTy, 0, 0.98);
  if (side === "title") {
    return {
      x,
      y,
      w,
      h,
      text,
      textX,
      textY,
      shape,
      bg,
      textColor: normalizeHexColor(
        raw.textColor ?? raw.text_color,
        base.textColor,
      ),
      subTextColor: normalizeHexColor(
        raw.subTextColor ?? raw.subText,
        base.subTextColor,
      ),
      fontScale,
    };
  }
  return {
    x,
    y,
    w,
    h,
    text,
    textX,
    textY,
    shape,
    bg,
    nameColor: normalizeHexColor(raw.nameColor ?? raw.name, base.nameColor),
    contactColor: normalizeHexColor(
      raw.contactColor ?? raw.contact,
      base.contactColor,
    ),
    fontScale,
  };
}

export function normalizePageChrome(raw, seed = null) {
  const seedStyle = normalizeChromeStyle(seed?.chromeStyle ?? null);
  const titleSeed = defaultChromeBox("title", {
    chromeStyle: seedStyle,
    text: seed?.categoryLabel ?? seed?.titleText ?? "",
  });
  const studioSeed = defaultChromeBox("studio", {
    chromeStyle: seedStyle,
    text: seed?.studioLine ?? seed?.studioText ?? "",
  });
  const src = raw && typeof raw === "object" ? raw : {};
  return {
    title: clampChromeBox(src.title || titleSeed, "title"),
    studio: clampChromeBox(src.studio || studioSeed, "studio"),
  };
}

/** Asegura page.chrome (migra desde seed de board si falta). */
export function ensurePageChrome(page, seed = null) {
  const base = page && typeof page === "object" ? page : {};
  if (base.chrome?.title?.w != null && base.chrome?.studio?.w != null) {
    return {
      ...base,
      chrome: normalizePageChrome(base.chrome, seed),
    };
  }
  return {
    ...base,
    chrome: normalizePageChrome(base.chrome, seed),
  };
}

/** Estilo legacy (board) a partir de cajas de página — para diálogo / PDF fallback. */
export function chromeStyleFromPageChrome(pageChrome) {
  const c = normalizePageChrome(pageChrome);
  return normalizeChromeStyle({
    title: {
      barScale: Math.min(1.6, Math.max(0.7, c.title.h / 0.055)),
      fontScale: c.title.fontScale,
      bg: c.title.bg,
      text: c.title.textColor,
      subText: c.title.subTextColor,
      x: c.title.x,
      y: c.title.y,
      shape: c.title.shape,
    },
    studio: {
      barScale: Math.min(1.6, Math.max(0.7, c.studio.h / 0.08)),
      fontScale: c.studio.fontScale,
      bg: c.studio.bg,
      name: c.studio.nameColor,
      contact: c.studio.contactColor,
      x: c.studio.x,
      y: c.studio.y,
      shape: c.studio.shape,
    },
  });
}

/** Roles de marcas migradas desde chrome (idempotencia). */
export const CHROME_MARK_ROLES = {
  titleBar: "pageTitleBar",
  titleText: "pageTitleText",
  titleTextSub: "pageTitleTextSub",
  studioBar: "pageStudioBar",
  studioText: "pageStudioText",
  studioTextSub: "pageStudioTextSub",
};

export function pageHasChromeMarks(marks) {
  const list = Array.isArray(marks) ? marks : [];
  return list.some(
    (m) =>
      m?.role === CHROME_MARK_ROLES.titleBar ||
      m?.role === CHROME_MARK_ROLES.studioBar,
  );
}

/**
 * Vértices 0–1 (origen arriba-izq) del corte de cabecera/pie.
 * Equivalente a chromeBarClipPath / PDF chromeBarPolygon.
 */
export function chromeShapeToPathPoints(box, which = "title") {
  const x = Number(box?.x) || 0;
  const y = Number(box?.y) || 0;
  const w = Math.max(0.01, Number(box?.w) || 0.3);
  const h = Math.max(0.004, Number(box?.h) || 0.05);
  const shape = normalizeChromeShape(box?.shape);
  const side = which === "studio" ? "studio" : "title";
  const slant = Math.min(w * 0.45, Math.max(h * 0.85, h * 1.55));

  if (shape === "rect") {
    return [
      { x, y },
      { x: x + w, y },
      { x: x + w, y: y + h },
      { x, y: y + h },
    ];
  }
  if (shape === "ribbon") {
    return [
      { x: x + slant, y },
      { x: x + w, y },
      { x: x + w - slant, y: y + h },
      { x, y: y + h },
    ];
  }
  if (shape === "tag") {
    if (side === "studio") {
      return [
        { x, y },
        { x: x + w - slant, y },
        { x: x + w, y: y + h / 2 },
        { x: x + w - slant, y: y + h },
        { x, y: y + h },
      ];
    }
    return [
      { x: x + slant, y },
      { x: x + w, y },
      { x: x + w, y: y + h },
      { x: x + slant, y: y + h },
      { x, y: y + h / 2 },
    ];
  }
  // diagonal Stage (foto2)
  if (side === "studio") {
    return [
      { x, y },
      { x: x + w - slant, y },
      { x: x + w, y: y + h },
      { x, y: y + h },
    ];
  }
  return [
    { x, y },
    { x: x + w, y },
    { x: x + w, y: y + h },
    { x: x + slant, y: y + h },
  ];
}

function chromeFontPx(fontScale, kind) {
  const fs = clampChromeFontScale(fontScale, 1);
  const base =
    kind === "titleMain"
      ? 15
      : kind === "titleSub"
        ? 12
        : kind === "studioMain"
          ? 13
          : 10;
  return clampMarkFontSize(base * fs, 14);
}

/** Caja chrome → marcas path (barra) + text editables. */
export function chromeBoxToMarks(box, which = "title") {
  const side = which === "studio" ? "studio" : "title";
  const b = clampChromeBox(box, side);
  const pts = chromeShapeToPathPoints(b, side);
  const barRole =
    side === "title" ? CHROME_MARK_ROLES.titleBar : CHROME_MARK_ROLES.studioBar;
  const out = [
    clampMark({
      type: "path",
      id: newMarkId(side === "title" ? "hbar" : "sbar"),
      role: barRole,
      points: pts,
      closed: true,
      fill: b.bg,
      color: b.bg,
      strokeColor: b.bg,
      stroke: 0,
    }),
  ];

  const tx = Number(b.textX);
  const ty = Number(b.textY);
  const rawText = String(b.text || "").trim();
  if (!rawText) return out;

  // Un solo texto editable (Illustrator): no partir en main/sub (evitaba
  // que "-JKT" quedara como marca huérfana que se re-sembraba).
  if (side === "title") {
    out.push(
      clampMark({
        type: "text",
        id: newMarkId("htxt"),
        role: CHROME_MARK_ROLES.titleText,
        x: tx,
        y: ty,
        w: Math.max(0.14, b.w * 0.9),
        text: rawText,
        fontSize: chromeFontPx(b.fontScale, "titleMain"),
        fontFamily: "sans",
        color: b.textColor,
        stroke: 0,
      }),
    );
    return out;
  }

  const { studioName, contactLine } = splitStudioLine(rawText);
  const studioBlock = [studioName, contactLine].filter(Boolean).join("\n");
  out.push(
    clampMark({
      type: "text",
      id: newMarkId("stxt"),
      role: CHROME_MARK_ROLES.studioText,
      x: tx,
      y: ty,
      w: Math.max(0.14, b.w * 0.9),
      text: studioBlock || rawText,
      fontSize: chromeFontPx(b.fontScale, "studioMain"),
      fontFamily: "sans",
      color: b.nameColor,
      stroke: 0,
    }),
  );
  return out;
}

/**
 * Convierte page.chrome legacy → marcas y elimina chrome.
 * NUNCA re-siembra si ya no hay chrome (evita copias infinitas tras autosave
 * o si el BACK pierde role). Flag page.hf = ya migrado/sembrado.
 */
export function migratePageChromeToMarks(page, seed = null) {
  const base = normalizeFreePage(page);
  const done = Boolean(base.hf) || pageHasChromeMarks(base.marks);
  if (done) {
    if (base.chrome == null && base.hf) return base;
    const { chrome: _drop, ...rest } = base;
    return { ...rest, hf: 1 };
  }
  // Solo migrar cuando aún existe el objeto chrome legacy.
  if (base.chrome?.title == null && base.chrome?.studio == null) {
    return base;
  }
  const chrome = normalizePageChrome(base.chrome, seed);
  const injected = [
    ...chromeBoxToMarks(chrome.title, "title"),
    ...chromeBoxToMarks(chrome.studio, "studio"),
  ];
  // Inyectar delante: si hay ~200 marks, el cap no se come la cabecera/pie.
  const marks = normalizeMarks([...injected, ...(base.marks || [])]);
  const { chrome: _drop, ...rest } = base;
  return { ...rest, hf: 1, marks };
}

/**
 * Página nueva: siembra cabecera/pie una sola vez (marcas + hf).
 * No usar en cada render / autosave.
 */
export function seedPageHeaderFooterMarks(page, seed = null) {
  const migrated = migratePageChromeToMarks(page, seed);
  if (migrated.hf || pageHasChromeMarks(migrated.marks)) {
    return { ...migrated, hf: 1 };
  }
  const chrome = normalizePageChrome(null, seed);
  const injected = [
    ...chromeBoxToMarks(chrome.title, "title"),
    ...chromeBoxToMarks(chrome.studio, "studio"),
  ];
  const marks = normalizeMarks([...injected, ...(migrated.marks || [])]);
  const { chrome: _drop, ...rest } = migrated;
  return { ...rest, hf: 1, marks };
}

/**
 * Carga de board: migrar chrome legacy si existe; no inventar duplicados.
 * Páginas nuevas → seedPageHeaderFooterMarks.
 */
export function ensurePageHeaderFooterMarks(page, seed = null) {
  return migratePageChromeToMarks(page, seed);
}

const CHROME_ROLE_SET = new Set(Object.values(CHROME_MARK_ROLES));

/** Barra tipo cabecera/pie: path cerrado bajo y ancho (con o sin role). */
function isChromeLikeBar(mark) {
  if (!mark || mark.type !== "path" || !mark.closed) return false;
  // Pegado desde otra página: no es “chrome oficial”, no colapsar
  if (mark.free === 1) return false;
  if (
    mark.role === CHROME_MARK_ROLES.titleBar ||
    mark.role === CHROME_MARK_ROLES.studioBar
  ) {
    return true;
  }
  const fill = String(mark.fill || "").toLowerCase();
  if (!fill || fill === "none") return false;
  const b = markBounds(mark);
  if (!b) return false;
  return b.h > 0.008 && b.h < 0.22 && b.w > 0.1;
}

function isLikelyOrphanChromeText(mark) {
  if (!mark || mark.type !== "text") return false;
  if (
    mark.role === CHROME_MARK_ROLES.titleText ||
    mark.role === CHROME_MARK_ROLES.studioText ||
    mark.role === CHROME_MARK_ROLES.titleTextSub ||
    mark.role === CHROME_MARK_ROLES.studioTextSub
  ) {
    return false; // se gestionan por role / merge
  }
  if (mark.role) return false;
  const t = String(mark.text || "").trim();
  if (!t || t.length > 40) return false;
  // Solo restos claros del split "MAIN - SUB" (p.ej. "-JKT").
  // NO borrar textos de usuario cerca del borde (rompe copy/paste entre páginas).
  if (/^[-–—]\s*[A-Z0-9]{1,12}$/i.test(t)) return true;
  return false;
}

/**
 * Limpia barras basura sin role cuando ya hay cabecera/pie oficial.
 * Las copias pegadas llevan free=1 y no pasan por isChromeLikeBar.
 * Flood (>12): colapsa a 1 arriba + 1 abajo (bug histórico).
 */
function collapseChromeLikeBars(marks) {
  const list = Array.isArray(marks) ? marks : [];
  const bars = [];
  const rest = [];
  for (const m of list) {
    if (isChromeLikeBar(m)) bars.push(m);
    else rest.push(m);
  }
  if (bars.length <= 12) {
    const hasTitle = bars.some((m) => m?.role === CHROME_MARK_ROLES.titleBar);
    const hasStudio = bars.some((m) => m?.role === CHROME_MARK_ROLES.studioBar);
    if (!hasTitle && !hasStudio) return list;
    return list.filter((m) => {
      if (!isChromeLikeBar(m) || m.role) return true;
      const b = markBounds(m);
      const top = b ? b.cy < 0.45 : true;
      if (top && hasTitle) return false;
      if (!top && hasStudio) return false;
      return true;
    });
  }
  const top = [];
  const bot = [];
  for (const m of bars) {
    const b = markBounds(m);
    const cy = b ? b.cy : 0.5;
    if (cy < 0.45) top.push(m);
    else bot.push(m);
  }
  const pick = (arr, role) => {
    if (!arr.length) return null;
    const withRole = arr.find((m) => m?.role === role);
    const chosen = withRole || arr[0];
    return clampMark({ ...chosen, role });
  };
  const keepTop = pick(top, CHROME_MARK_ROLES.titleBar);
  const keepBot = pick(bot, CHROME_MARK_ROLES.studioBar);
  return [
    ...rest,
    ...(keepTop ? [keepTop] : []),
    ...(keepBot ? [keepBot] : []),
  ];
}

/** Como máximo 1 texto cabecera y 1 pie sin role (además de los con role). */
function collapseChromeLikeTexts(marks) {
  const list = Array.isArray(marks) ? marks : [];
  const titleRole = list.find((m) => m?.role === CHROME_MARK_ROLES.titleText);
  const studioRole = list.find((m) => m?.role === CHROME_MARK_ROLES.studioText);
  const orphans = list.filter((m) => isLikelyOrphanChromeText(m));
  if (!orphans.length) return list;
  const withoutOrphans = list.filter((m) => !isLikelyOrphanChromeText(m));
  // Si ya hay texto con role, tirar huérfanos; si no, promover uno arriba/abajo
  const topOrphans = orphans.filter((m) => Number(m.y) < 0.45);
  const botOrphans = orphans.filter((m) => Number(m.y) >= 0.45);
  const extra = [];
  if (!titleRole && topOrphans[0]) {
    extra.push(
      clampMark({
        ...topOrphans[0],
        role: CHROME_MARK_ROLES.titleText,
      }),
    );
  }
  if (!studioRole && botOrphans[0]) {
    extra.push(
      clampMark({
        ...botOrphans[0],
        role: CHROME_MARK_ROLES.studioText,
      }),
    );
  }
  return [...withoutOrphans, ...extra];
}

/** Quita duplicados por role (p.ej. tras autosave que re-sembró). Conserva el primero. */
export function dedupeChromeRoleMarks(marks) {
  const list = Array.isArray(marks) ? marks : [];
  const seen = new Set();
  const out = [];
  for (const m of list) {
    const role = m?.role != null ? String(m.role) : "";
    if (role && CHROME_ROLE_SET.has(role)) {
      if (seen.has(role)) continue;
      seen.add(role);
    }
    out.push(m);
  }
  return out;
}

/**
 * Limpia miles de copias de cabecera/pie (con o sin role) y fusiona subtextos.
 */
export function scrubChromeMarkDupes(marks) {
  let list = dedupeChromeRoleMarks(marks);
  list = collapseChromeLikeBars(list);
  list = collapseChromeLikeTexts(list);
  list = dedupeChromeRoleMarks(list);
  // Subtexto legacy → fusionar en el título principal
  const sub = list.find((m) => m?.role === CHROME_MARK_ROLES.titleTextSub);
  const main = list.find((m) => m?.role === CHROME_MARK_ROLES.titleText);
  if (sub) {
    list = list.filter((m) => m?.role !== CHROME_MARK_ROLES.titleTextSub);
    if (main && String(sub.text || "").trim()) {
      const subT = String(sub.text || "").trim();
      const mainT = String(main.text || "").trim();
      if (mainT && !mainT.includes(subT)) {
        list = list.map((m) =>
          m?.role === CHROME_MARK_ROLES.titleText
            ? clampMark({
                ...m,
                text: `${mainT} - ${subT.replace(/^[-–—]\s*/, "")}`,
              })
            : m,
        );
      }
    }
  }
  // studioTextSub legacy → fusionar
  const sSub = list.find((m) => m?.role === CHROME_MARK_ROLES.studioTextSub);
  const sMain = list.find((m) => m?.role === CHROME_MARK_ROLES.studioText);
  if (sSub) {
    list = list.filter((m) => m?.role !== CHROME_MARK_ROLES.studioTextSub);
    if (sMain && String(sSub.text || "").trim()) {
      const a = String(sMain.text || "").trim();
      const b = String(sSub.text || "").trim();
      if (a && !a.includes(b)) {
        list = list.map((m) =>
          m?.role === CHROME_MARK_ROLES.studioText
            ? clampMark({ ...m, text: `${a}\n${b}` })
            : m,
        );
      }
    }
  }
  return normalizeMarks(list);
}

/**
 * Carga segura: migra chrome legacy, limpia duplicados, marca hf.
 * Boards viejos sin page.chrome ni marcas (solo CategoryLabel/StudioLine):
 * siembra una vez — antes se pintaba con MoodboardChromeBar y ahora solo marks.
 * Si hf=1 y el usuario borró la cabecera/pie, no re-siembra.
 */
export function loadPageHeaderFooterMarks(page, seed = null) {
  let migrated = migratePageChromeToMarks(page, seed);
  let marks = scrubChromeMarkDupes(migrated.marks);
  if (!pageHasChromeMarks(marks) && !migrated.hf) {
    migrated = seedPageHeaderFooterMarks(
      { ...migrated, marks, hf: undefined },
      seed,
    );
    marks = scrubChromeMarkDupes(migrated.marks);
  }
  const { chrome: _drop, ...rest } = migrated;
  return {
    ...rest,
    marks,
    ...(pageHasChromeMarks(marks) || migrated.hf ? { hf: 1 } : null),
  };
}

/** Actualiza texto de marcas con role de cabecera/pie (diálogo meta). */
export function patchChromeTextMarks(marks, which, text) {
  const side = which === "studio" ? "studio" : "title";
  const list = Array.isArray(marks) ? marks.map((m) => ({ ...m })) : [];
  const bar =
    list.find(
      (m) =>
        m?.role ===
        (side === "title"
          ? CHROME_MARK_ROLES.titleBar
          : CHROME_MARK_ROLES.studioBar),
    ) || null;
  const bx = Number(bar?.points?.[0]?.x) || 0;
  const by = Number(bar?.points?.[0]?.y) || 0;
  // Quitar textos previos de ese lado y regenerar desde caja sintética
  const drop = new Set(
    side === "title"
      ? [CHROME_MARK_ROLES.titleText, CHROME_MARK_ROLES.titleTextSub]
      : [CHROME_MARK_ROLES.studioText, CHROME_MARK_ROLES.studioTextSub],
  );
  const kept = list.filter((m) => !drop.has(m?.role));
  const bounds = bar ? markBounds(bar) : null;
  const box = clampChromeBox(
    {
      x: bounds?.x ?? (side === "title" ? 0.64 : 0),
      y: bounds?.y ?? (side === "title" ? 0 : 0.9),
      w: bounds?.w ?? 0.36,
      h: bounds?.h ?? 0.06,
      text: String(text ?? ""),
      textX: bounds ? bounds.x + bounds.w * 0.08 : bx,
      textY: bounds ? bounds.y + bounds.h * 0.18 : by,
      shape: "diagonal",
      bg: bar?.fill || "#383838",
    },
    side,
  );
  const fresh = chromeBoxToMarks(box, side).filter((m) => m.type === "text");
  // Conservar barra existente
  return normalizeMarks([...kept, ...fresh]);
}

/** Nota STAGE asociada a un asset de reunión (API TeamNote). */
export function assetTeamNote(asset) {
  return String(asset?.TeamNote || asset?.teamNote || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, TEXT_MARK_MAX_LEN);
}

export function notesByAssetIdFromAssets(assets = []) {
  const map = new Map();
  for (const a of Array.isArray(assets) ? assets : []) {
    const id = Number(a?.id);
    const note = assetTeamNote(a);
    if (Number.isFinite(id) && id > 0 && note) map.set(id, note);
  }
  return map;
}

/**
 * Marcas de texto (Nota STAGE) ancladas bajo cada slot con nota.
 * Igual que el BACK al crear moodboard desde reunión.
 */
export function textMarksForMeetingNotes(slots = [], notesByAssetId = null) {
  if (!notesByAssetId) return [];
  const getNote = (aid) => {
    if (notesByAssetId instanceof Map) {
      return notesByAssetId.get(aid) ?? notesByAssetId.get(String(aid));
    }
    return notesByAssetId[aid] ?? notesByAssetId[String(aid)];
  };
  const marks = [];
  for (const slot of Array.isArray(slots) ? slots : []) {
    const aid = Number(slot?.assetId);
    if (!Number.isFinite(aid) || aid <= 0) continue;
    const text = String(getNote(aid) || "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, TEXT_MARK_MAX_LEN);
    if (!text) continue;
    const sx = Number(slot.x) || 0;
    const sy = Number(slot.y) || 0;
    const sw = Math.max(0.08, Number(slot.w) || 0.2);
    const sh = Math.max(0.04, Number(slot.h) || 0.2);
    // Dentro de la foto, franja inferior (overlay), no debajo del slot.
    const x = clamp01(sx + 0.02);
    const y = clamp01(sy + sh * 0.72);
    marks.push(
      clampMark({
        type: "text",
        id: newMarkId("note"),
        x,
        y,
        w: Math.max(0.08, sw - 0.04),
        text,
        fontSize: 11,
        fontFamily: "sans",
        color: "#000000",
        stroke: 0,
      }),
    );
  }
  return marks;
}

/** Añade notas de los slots recién rellenados sin duplicar el mismo texto en ese slot. */
export function mergeNoteMarksForSlots(page, slotsWithNotes, notesByAssetId) {
  const base = normalizeFreePage(page);
  const fresh = textMarksForMeetingNotes(slotsWithNotes, notesByAssetId);
  if (!fresh.length) return base.marks || [];
  const existing = Array.isArray(base.marks) ? [...base.marks] : [];
  const seen = new Set(
    existing
      .filter((m) => m?.type === "text")
      .map(
        (m) =>
          `${String(m.text || "").trim()}@${Number(m.x).toFixed(3)},${Number(m.y).toFixed(3)}`,
      ),
  );
  for (const m of fresh) {
    const key = `${String(m.text || "").trim()}@${Number(m.x).toFixed(3)},${Number(m.y).toFixed(3)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    existing.push(m);
  }
  return existing;
}

function textMarkInsideSlot(mark, slot) {
  if (mark?.type !== "text") return false;
  const x = Number(mark.x);
  const y = Number(mark.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  const sx = Number(slot?.x) || 0;
  const sy = Number(slot?.y) || 0;
  const sw = Math.max(0, Number(slot?.w) || 0);
  const sh = Math.max(0, Number(slot?.h) || 0);
  const pad = 0.02;
  return (
    x >= sx - pad && x <= sx + sw + pad && y >= sy - pad && y <= sy + sh + pad
  );
}

/**
 * Pone la Nota STAGE en las fotos ya colocadas.
 * Si la casilla ya tiene un texto, no se toca.
 * @returns {{ pages: Array, added: number }}
 */
export function stampMeetingNotesOnPages(pages = [], notesByAssetId = null) {
  let added = 0;
  const next = (Array.isArray(pages) ? pages : []).map((page) => {
    const base = normalizeFreePage(page);
    const slots = (base.slots || []).filter((slot) => {
      const aid = Number(slot?.assetId);
      if (!Number.isFinite(aid) || aid <= 0) return false;
      const note =
        notesByAssetId instanceof Map
          ? (notesByAssetId.get(aid) ?? notesByAssetId.get(String(aid)))
          : (notesByAssetId?.[aid] ?? notesByAssetId?.[String(aid)]);
      if (!String(note || "").trim()) return false;
      const hasText = (base.marks || []).some((mark) =>
        textMarkInsideSlot(mark, slot),
      );
      return !hasText;
    });
    if (!slots.length) return base;
    const before = (base.marks || []).length;
    const marks = mergeNoteMarksForSlots(base, slots, notesByAssetId);
    added += Math.max(0, marks.length - before);
    return { ...base, marks };
  });
  return { pages: next, added };
}

function textMark(partial) {
  return clampMark({
    type: "text",
    id: newMarkId("ft"),
    fontSize: 14,
    fontFamily: "sans",
    color: "#111111",
    stroke: 0,
    ...partial,
  });
}

function emptySlot(partial) {
  return clampSlot({
    id: newSlotId("ft"),
    zoom: 1,
    panX: 0.5,
    panY: 0.5,
    shape: "rect",
    assetId: null,
    ...partial,
  });
}

/**
 * Cabecera + pie al estilo STAGE tech pack (PDF FichaTecnica.pdf, A4 landscape).
 */
function fichaChromeMarks(pageKey, titulo) {
  return [
    textMark({
      id: `ft_${pageKey}_title`,
      x: 0.02,
      y: 0.015,
      w: 0.7,
      text: titulo,
      fontSize: 16,
      color: "#222222",
    }),
    textMark({
      id: `ft_${pageKey}_footer`,
      x: 0.35,
      y: 0.955,
      w: 0.3,
      text: "STAGE DESIGN STUDIO",
      fontSize: 10,
      color: "#666666",
    }),
  ];
}

/**
 * Plantilla Ficha Técnica (3 págs.) según PDF STAGE (7 págs. → MVP):
 * 1) Dibujos — foto + detalles + flats front/back (PDF p1)
 * 2) Siluetas — flats front/back construcción (PDF p2)
 * 3) Trimmings / Qties / Size / Color + fotos hardware (PDF p5)
 * @param {{ titulo?: string }} [opts]
 */
export function buildFichaTecnicaPages(opts = {}) {
  const titulo =
    String(opts.titulo || "Ficha técnica").trim() || "Ficha técnica";

  const pageDibujos = {
    slots: [
      // Columna izquierda: foto principal + 2 detalles (collar / zipper)
      emptySlot({ id: "ft_dib_main", x: 0.02, y: 0.09, w: 0.22, h: 0.42 }),
      emptySlot({ id: "ft_dib_det1", x: 0.02, y: 0.54, w: 0.22, h: 0.18 }),
      emptySlot({ id: "ft_dib_det2", x: 0.02, y: 0.74, w: 0.22, h: 0.18 }),
      // Flats front / back
      emptySlot({ id: "ft_dib_front", x: 0.28, y: 0.09, w: 0.32, h: 0.82 }),
      emptySlot({ id: "ft_dib_back", x: 0.64, y: 0.09, w: 0.32, h: 0.82 }),
    ],
    marks: [
      ...fichaChromeMarks("dib", titulo),
      textMark({
        id: "ft_dib_label",
        x: 0.72,
        y: 0.015,
        w: 0.25,
        text: "DIBUJOS",
        fontSize: 11,
        color: "#555555",
      }),
    ],
  };

  const pageSiluetas = {
    slots: [
      emptySlot({ id: "ft_sil_front", x: 0.04, y: 0.1, w: 0.44, h: 0.8 }),
      emptySlot({ id: "ft_sil_back", x: 0.52, y: 0.1, w: 0.44, h: 0.8 }),
    ],
    marks: [
      ...fichaChromeMarks("sil", titulo),
      textMark({
        id: "ft_sil_label",
        x: 0.72,
        y: 0.015,
        w: 0.25,
        text: "SILUETAS",
        fontSize: 11,
        color: "#555555",
      }),
      textMark({
        id: "ft_sil_front_lbl",
        x: 0.04,
        y: 0.915,
        w: 0.2,
        text: "FRONT",
        fontSize: 11,
        color: "#666666",
      }),
      textMark({
        id: "ft_sil_back_lbl",
        x: 0.52,
        y: 0.915,
        w: 0.2,
        text: "BACK",
        fontSize: 11,
        color: "#666666",
      }),
    ],
  };

  // Columnas como en PDF p5: TRIMMINGS | QTIES | SIZE | COLOR
  const colX = [0.02, 0.32, 0.42, 0.58];
  const colW = [0.28, 0.08, 0.14, 0.14];
  const headers = ["TRIMMINGS", "QTIES", "SIZE", "COLOR"];
  const headerMarks = headers.map((label, i) =>
    textMark({
      id: `ft_trim_h_${i}`,
      x: colX[i],
      y: 0.1,
      w: colW[i],
      text: label,
      fontSize: 12,
    }),
  );
  const rowMarks = [];
  for (let row = 0; row < 5; row += 1) {
    const y = 0.16 + row * 0.12;
    headers.forEach((_, i) => {
      rowMarks.push(
        textMark({
          id: `ft_trim_r${row}_c${i}`,
          x: colX[i],
          y,
          w: colW[i],
          text: "—",
          fontSize: 11,
          color: "#999999",
        }),
      );
    });
  }

  const pageTrimmings = {
    slots: [
      emptySlot({ id: "ft_trim_img1", x: 0.74, y: 0.1, w: 0.24, h: 0.26 }),
      emptySlot({ id: "ft_trim_img2", x: 0.74, y: 0.4, w: 0.24, h: 0.26 }),
      emptySlot({ id: "ft_trim_img3", x: 0.5, y: 0.68, w: 0.22, h: 0.24 }),
    ],
    marks: [
      ...fichaChromeMarks("trim", titulo),
      textMark({
        id: "ft_trim_label",
        x: 0.55,
        y: 0.015,
        w: 0.42,
        text: "TRIMMINGS · QTIES · SIZE · COLOR",
        fontSize: 11,
        color: "#555555",
      }),
      ...headerMarks,
      ...rowMarks,
    ],
  };

  return [pageDibujos, pageSiluetas, pageTrimmings].map((p) =>
    normalizeFreePage(p),
  );
}
